<template>
  <div class="platform-billing-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">PLATFORM BILLING</p>
        <h1>請求管理</h1>
        <p>
          契約企業ごとの請求額と、入金・請求書の発行状況を確認できます。
          各月の請求額は、<strong>その月分の料金設定とその月に在籍した人数</strong>をもとに計算されます。
          あとからプラン料金を変更しても、過去に確定済みの請求額は変わりません。
        </p>
        <!--
          2026-09-30：前は「確定時点の単価」と書いていた。実際に使うのはその月分の料金
          （「プランの料金」の「適用請求月」がその月以前で最新のもの）で、確定した時点の料金ではない。
          「適用請求月」で先の月の料金を保存できるようにしたので、食い違いが目に付く。
          2文目は Gemini と決めた AM-16（太字は前の文と同じく、何をもとに計算するかの所）。
        -->
      </div>
      <div class="header-actions">
        <!--
          **確定を送っているあいだは、月を変えさせない**（2026-10-10、監査 BF-1）。
          前は確定中でも月を変えられ、前の月の確定の返事が、変えたあとの月の画面に出た。
          返事の側でも、対象の月と通し番号を照合している（fixTicket）。
        -->
        <CInput
          v-model="yearMonthInput"
          type="month"
          class="month-input"
          :disabled="loading || issuing || fixing"
        />
        <CButton
          color="secondary"
          variant="outline"
          :disabled="loading || issuing"
          @click="reload"
        >
          再読込
        </CButton>
      </div>
    </header>

    <!--
      **祝日マスタの残りが少ないことを、毎月開く画面にも出す**（2026-09-30、束7）。
      祝日マスタは年に1回しか開かないので、そこにだけ警告を出しても気づけない。
      請求管理は運営管理者が毎月開く。出すのは取り込める人（祝日マスタを開ける人）だけ。
    -->
    <CAlert v-if="holidayStockWarning" color="warning" class="billing-alert holiday-stock-alert">
      <!--
        3つの場合とも、取り込む場所（祝日マスタ）へのリンクを文の中に置く。
        **「内閣府の公表（毎年2月頃）のあとに」とは書かない**（2026-10-08）。この帯が出るころには
        翌年分は公表済みで、切れている・1件も無いなら、いますぐ取り込める。待つように読ませない。
      -->
      <template v-if="!holidayStock.lastDate">
        <strong>祝日マスタに1件も入っていません。</strong>現在、全社で祝日が平日として扱われているため、<router-link to="/platform/national-holidays">祝日マスタ</router-link>でCSVを取り込んでください。
      </template>
      <!--
        **もう切れているときは、残りの日数を出さない**（2026-10-08）。最後の日を過ぎると
        残りが負になり、「残り -10 日」「切れた翌日から…扱われます」と、まだ先の話のように
        出ていた。祝日マスタの登録状況と同じ言い方。
      -->
      <template v-else-if="holidayStockExpired">
        <strong>{{ holidayStock.lastDate }}で登録が切れている</strong>ため、現在、全社で祝日が平日として扱われています。<router-link to="/platform/national-holidays">祝日マスタ</router-link>の画面でCSVを取り込むと解消されます。
      </template>
      <template v-else>
        祝日の登録が <strong>{{ holidayStock.lastDate }} まで</strong>（残り {{ holidayStock.daysRemaining }} 日）となっています。登録が切れると全社で祝日が平日として扱われるため、<router-link to="/platform/national-holidays">祝日マスタ</router-link>でCSVを取り込んで登録を更新してください。
      </template>
    </CAlert>

    <!--
      **発行すると番号が付き、中身が確定する。**押す前に、何が起きるかを読める場所へ置く。

      前は「番号が確定するため、取り消せません。」と書いていた。**発行済みの請求も、
      行の［取り消す］で取り消せる**（契約が解約済みでなく、入金の記録が無いとき）ので、
      同じ画面の中で説明とボタンが食い違っていた（2026-10-06、監査 BILL-1）。
      直し方（取り消す → 確定し直し → 発行）まで書く。文は Gemini と決めた（BL-A）。
      2026-10-08：ボタンの字は「取り消す」「この月を確定する」。前の文は「［取消］」と
      書いていて、同じ画面のボタンとも、ほかの文とも字が違っていた。字を合わせて
      決め直した（BL-A2）。
    -->
    <CAlert v-if="canIssue" color="warning" class="billing-alert">
      請求書を発行すると、運営会社全体の通し番号（例：INV-{{ compactMonth }}-0001…）が付与され、記載内容が確定します。
      内容を修正する場合は、対象行の［取り消す］を行ったあと、［この月を確定する］で対象月を確定し直してから発行してください。
      なお、発行済みの請求書が重複して発行されることはありません。
    </CAlert>

    <!--
      **ここに出すのは、読み込みの失敗だけ**（2026-09-26）。押した操作の結果は
      押したボタン・行・節のそばに出す。前は確定・発行の結果もここに出していて、
      並びのボタンや表の行から見えず、失敗は直後の読み直しで消えていた。
    -->
    <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>
    <!--
      **確定を見送った会社は、名前と理由をそのまま並べる。**

      席数の記録が対象月より後からしか無い会社は確定しない。
      件数だけ出すと「今月は0件だった」と読めてしまい、請求が
      抜けたことに気づけない。
    -->
    <CAlert v-if="heldRows.length" ref="heldBand" color="warning">
      <!--
        **理由を1つに言い切らない**（Gemini S12、2026-09-30）。
        前は「その月の在籍の記録がないためです。」と固定で書いていた。見送る理由は
        会社ごとに違う（在籍・数えた日・プラン・料金・周期）ので、下の行と食い違い、
        運営は人数を数え直しに行っていた（テスターの S5 No.12）。
      -->
      <div>
        <strong>{{ heldRows.length }} 件の確定が完了しませんでした。</strong>
        各会社の理由は下記をご確認ください。
      </div>
      <ul class="mb-0 mt-2">
        <li v-for="row in heldRows" :key="row.accountId">
          {{ row.accountName || ("会社ID " + row.accountId) }}
          … {{ row.reason }}
          <!--
            **登録したあと、その会社だけ確定し直せるようにする。**
            全社を流し直すと、ほかの会社の請求まで動かすことになる。
          -->
          <!--
            **0円で見送った会社は、直す場所（「プランの料金」のその会社のプランの行）へ
            1回で移れるようにする**（2026-10-10、利用者「人がたどる自然な動線を考えて
            設計しているか」）。前は文で場所を案内するだけで、利用者は画面のいちばん下まで
            送り、プランの行を探し、開くたびに翌月へ戻る「適用請求月」を選び直していた
            （選び直しを忘れると、別の月に保存される）。

            ボタンは、狭い幅でも1つずつ折り返せる箱に入れる（2つ並んで文に食い込まない）。
            字（planPriceJumpLabel）がまだ決まっていないあいだは、ボタンを出さない。
          -->
          <span class="held-actions">
            <CButton
              v-if="canJumpToPlanPrice(row)"
              size="sm"
              color="warning"
              variant="outline"
              :disabled="loading || issuing || fixing || savingPricing"
              data-testid="jump-to-plan-price"
              @click="goToPlanPrice(row)"
            >
              {{ planPriceJumpLabel }}
            </CButton>
            <CButton
              size="sm"
              color="warning"
              variant="outline"
              :disabled="loading || issuing || fixing"
              @click="fixOneAccount(row)"
            >
              この会社だけ確定する
            </CButton>
          </span>
        </li>
      </ul>
    </CAlert>
    <!--
      **落ちた会社は、見送りと分けて出す。**

      見送りは当時の人数を登録すれば直る。落ちたほうは原因を直さないと
      請求が立たない。前は例外をログへ1行書いて次へ進むだけだったので、
      **請求を作れなかった会社が誰にも見えないまま消えていた**
      （監査 2026-09-06、16:05）。
    -->
    <!--
      **見直しで落ちた会社も出す。**確定の失敗と分けて出さないと、
      「請求はできたが制限が戻っていない」が伝わらない。
    -->
    <CAlert v-if="dunningFailedRows.length" color="danger">
      <div>
        <strong>{{ dunningFailedRows.length }} 社の制限を見直せませんでした。</strong>
        該当する会社の状態は変わっていません。
      </div>
      <ul class="mb-0 mt-2">
        <li v-for="row in dunningFailedRows" :key="row.accountId">
          {{ nameOf(row.accountId) }} … {{ row.reason }}
          <div v-if="row.detail" class="small text-muted">
            {{ row.detail }}
          </div>
        </li>
      </ul>
    </CAlert>

    <CAlert v-if="failedRows.length" color="danger">
      <div>
        <strong>{{ failedRows.length }} 件の請求を作成できませんでした。</strong>
        この会社の請求処理は完了していません。
      </div>
      <ul class="mb-0 mt-2">
        <li v-for="row in failedRows" :key="row.accountId">
          {{ row.accountName || ("会社ID " + row.accountId) }}
          … {{ row.reason }}
          <div v-if="row.detail" class="small text-muted">
            {{ row.detail }}
          </div>
          <CButton
            size="sm"
            color="danger"
            variant="outline"
            class="mt-1"
            :disabled="loading || issuing || fixing"
            @click="fixOneAccount(row)"
          >
            もう一度この会社を確定する
          </CButton>
        </li>
      </ul>
    </CAlert>
    <!--
      ［この会社だけ確定する］の結果。押した行は確定できると一覧から消えるので、
      一覧の中ではなく、すぐ下に出す。
    -->
    <ActionResultMessage v-bind="accountFixResult"/>

    <div v-if="issueResults.length" class="issue-result">
      <h2>発行の結果</h2>
      <!--
        **件数だけだと使えない。**一括で出したとき、どれが出て
        どれが飛んだのかが分からないと、次に何をすればよいか決まらない。
      -->
      <ul>
        <li v-for="row in issueResults" :key="row.billingPeriodId">
          <strong>{{ nameOf(row.accountId) }}</strong>
          … {{ issueSaysItInOneLine(row) ? row.message : issueStatusLabel(row.status) }}
          <!--
            **理由の本文も出す。**「発行しませんでした」だけでは、
            何が足りないのか分からない。

            **すでに発行済みだったときは、本文の1行だけにする**（2026-10-10、実機で発見）。
            前は「すでに発行済みでした（二重には出していません）」の行と、同じことを言う
            本文（「すでに発行済みのため、今回は発行していません。…」）が続けて出ていた。
            本文が、発行済みの事実と次の手の両方を言っている。本文が無いときは今までどおり
            札の字を出す。
          -->
          <div v-if="row.message && !issueSaysItInOneLine(row)" class="small text-muted">
            {{ row.message }}
          </div>
          <span v-if="row.invoiceNo" class="invoice-no">{{ row.invoiceNo }}</span>
        </li>
      </ul>
    </div>

    <div class="toolbar">
      <label class="select-all">
        <input
          type="checkbox"
          :checked="allSelected"
          :disabled="!issuableRows.length || loading || issuing"
          @change="toggleAll"
        >
        発行できるものを全部選ぶ（{{ issuableRows.length }} 件）
      </label>

      <!--
        ボタンの並びと、［確定分を一括発行］が押せない理由を、1つの箱に入れる（2026-10-10）。
        前は理由を並びの外に置いて右へ寄せていたので、狭い幅（ボタンが左へ回る幅）では
        ボタンが左・理由が右に離れた。箱ごと動けば、どの幅でも理由はボタンのすぐ下に出る。
      -->
      <div v-if="canIssue" class="toolbar-actions-block">
      <div class="toolbar-actions">
        <!--
          **選んだ月を確定する。**月初の自動確定は既定で止めてあり
          （back の preika.billing.scheduler.enabled）、止まっている間は
          毎月これで前月分を確定しないと請求書を1枚も出せない。
          動いていても、それが落ちた月の手当てに要る。
          何度押しても増えない。
        -->
        <CButton
          color="secondary"
          variant="outline"
          :disabled="loading || issuing || fixing"
          @click="fixPeriods"
        >
          この月を確定する
        </CButton>
        <!--
          **入金を記録した直後に「これで戻りますか」と聞かれる。**
          翌朝まで待たないと分からないのでは、窓口が答えられない。
        -->
        <CButton
          color="secondary"
          variant="outline"
          :disabled="loading || issuing || fixing"
          @click="runDunning"
        >
          支払状況を見直す
        </CButton>
        <!--
          **押した日（今日）の在籍だけを記録する**（back の countOn に今日を渡す）。
          毎日 2:50 の自動処理が落ちた日に、その日のうちに押す手当て。
          過ぎた日の分は記録できない。
        -->
        <CButton
          color="secondary"
          variant="outline"
          :disabled="loading || issuing || fixing || counting"
          @click="countHeadcount"
        >
          今月の人数を数える
        </CButton>
        <CButton
          color="primary"
          variant="outline"
          :disabled="!selectedIds.length || loading || issuing"
          @click="issueSelected"
        >
          選択した請求書を発行（{{ selectedIds.length }} 件）
        </CButton>
        <CButton
          color="primary"
          :disabled="!issuableRows.length || loading || issuing"
          @click="issueAll"
        >
          確定分を一括発行
        </CButton>
      </div>
      <!--
        **押せない理由を、ボタンのすぐ下に出す**（2026-10-09）。発行できる確定済みの
        請求が0件だと［確定分を一括発行］が押せないが、理由がその場で読めなかった。
        押せるとき・読み込み中・一覧を読めなかったときは出さない。
        ボタンの並びの中に入れると、広い画面では折り返さない並びなのでボタンが
        押しつぶされる。並びのすぐ下に置く。寄せはボタンと同じ（広い幅では右端、
        ボタンが左から折り返す狭い幅では左）。
      -->
      <p
        v-if="nothingToIssue"
        class="toolbar-reason"
        data-issue-all="nothing"
      >{{ nothingToIssueNote }}</p>
      </div>

      <!--
        **押す前に、何が起きるかを読ませる。**行を選ばずに押せる
        （月全体に効く）ボタンなので、テスターに「何も選んでいないのに
        押せる。何が起きるのか分からない」と言われた。

        **説明は実際の動きに合わせる**（2026-09-30。文は Gemini で決めた）。
        前の説明は「当月分」「自動確定が未完了の場合のみ」「押して数え直せます」と、
        どれも動きと違っていた。確定も発行も、画面で選んだ月に効く。
        ［今月の人数を数える］は押した日の分しか記録しない。
        ボタン名も「当月分を一括発行」から「確定分を一括発行」へ変えた。

        **確定の説明は、月初の自動確定が動いているかで出し分ける。**
        値は back が「発行元の設定」の応答（autoFixEnabled）で渡す。
        読み込み前や読めなかったときは、止まっている側を出す
        （動いていない自動確定を当てにさせない）。
      -->
      <dl v-if="canIssue" class="toolbar-what-happens">
        <div>
          <dt>この月を確定する</dt>
          <dd v-if="autoFixRunning" data-auto-fix="running">対象月（前月以前）の請求をまとめて確定します。毎月1日の自動確定が失敗した場合や、手動で確定したい場合にご使用ください。</dd>
          <dd v-else data-auto-fix="stopped">対象月（前月以前）の請求をまとめて確定します。現在自動確定が停止しているため、毎月このボタンで確定を行ってください。</dd>
        </div>
        <div>
          <dt>支払状況を見直す</dt>
          <dd>全契約企業の入金状況を再判定し、利用制限の解除などを即時に反映します。</dd>
        </div>
        <div>
          <dt>今月の人数を数える</dt>
          <dd>本日時点の在籍人数を記録します。毎朝の自動処理が失敗した際などの手動実行用です。過ぎた過去日の人数を後から遡って記録することはできません。</dd>
        </div>
        <div>
          <dt>確定分を一括発行</dt>
          <dd>対象月のうち、確定済みかつ未発行の請求書を一括発行して請求書番号を付与します。発行済みの請求書は重複して発行されません。</dd>
        </div>
      </dl>
    </div>
    <!-- 上の並びのボタン（確定・見直し・人数・発行）の結果。並びのすぐ下に出す。 -->
    <ActionResultMessage :text="fixMessage" type="info"/>
    <ActionResultMessage :text="fixError" type="danger"/>

    <!-- lang="ja"：欄の中の注（見込み額・未集計など）を、文節で折る（billing-table の word-break）。 -->
    <CDataTable
      :items="rows"
      :fields="fields"
      :loading="loading"
      hover
      striped
      class="billing-table"
      lang="ja"
    >
      <template #select="{ item }">
        <td>
          <input
            type="checkbox"
            :disabled="!isIssuable(item) || issuing"
            :checked="selectedIds.indexOf(item.id) >= 0"
            @change="toggleOne(item)"
          >
        </td>
      </template>

      <template #accountName="{ item }">
        <td>
          <div class="account-name">{{ item.accountName }}</div>
          <div class="account-id">ID {{ item.accountId }}</div>
          <!--
            **運営の会社（請求書の発行元）の印**（2026-10-08、9/30 の決め1）。

            運営の会社には請求を立てない。行は消さない（消すと「なぜ無いのか」が
            分からない）ので、どの行が運営の会社かを会社名のそばで分かるようにする。
            ふつうは状態が「請求しません」で、理由は状態の欄に出る。前に立った
            運営の会社宛ての請求が残っている月は確定済みの行として並ぶので、
            そちらにも同じ印を出す（ほかの契約企業の未入金と見分ける）。
            どの会社が運営の会社かは back が決めて返す（ここで判定しない）。
          -->
          <div
            v-if="item.issuerAccount"
            class="issuer-account-mark"
            data-testid="issuer-account-mark"
          >
            <CBadge color="secondary">{{ issuerAccountMark }}</CBadge>
          </div>
        </td>
      </template>

      <!--
        **金額を出せない行に 0 を書かない。**

        「0人 ¥0」と並ぶと、0円で請求されると読める。請求が
        立っていないことと、0円であることは別のもの。
      -->
      <!--
        **月額ではない精算は、そうと書く。**

        月額の対象ではないが、残っている日割りだけを精算する行が
        ある。人数は 0 人なので、そのまま出すと「0人ぶんの月額」に
        見える。**請求額だけが立っている理由が読めない**
        （2026-09-19、監査 B6）。
      -->
      <template #billedUserCount="{ item }">
        <td class="numeric">
          <span v-if="item.adjustmentOnly" class="muted">調整のみ</span>
          <span v-else-if="hasAmount(item)">{{ item.billedUserCount }} 人</span>
          <span v-else class="muted">—</span>
        </td>
      </template>

      <template #totalAmount="{ item }">
        <td class="numeric">
          <template v-if="hasAmount(item)">
            {{ yen(item.totalAmount) }}
            <!--
              **ボリュームディスカウント（0108）を当てた月は、引いた額を添える。**
              割引のあとに最低月額が効くので、最低月額調整より上に置く（計算の順）。
              見込みの行も確定済みの行も、back が割引額を返す（当てない月は0。0円なら出さない）。
            -->
            <div v-if="hasVolumeDiscount(item)" class="planned-note volume-discount-note">
              人数割適用 -{{ yen(item.volumeDiscountAmount) }}
            </div>
            <!--
              **最低月額との差額（0107）が入っている月は、額を添える。**
              請求額が「人数×単価」より大きい理由が、運営の一覧では読めなかった。
              見込みの行も確定済みの行も、back が差額を返す（無い・0円なら出さない）。
            -->
            <div v-if="hasMinimumAdjustment(item)" class="planned-note minimum-adjustment-note">
              最低月額調整 {{ yen(item.minimumAdjustmentAmount) }}
            </div>
            <!--
              **「確定するとこの額になります」とは言えない。**

              金額は確定と同じ計算で出しているが、
                ・月の途中なら、月末までに人が増えれば上がる
                ・記録が足りない月は、そのままでは確定できない
              のどちらもある。前は言い切っていたので、下の
              「◯日分の集計が必要ですが…」と同じ行で矛盾していた
              （監査 2026-09-19）。
            -->
            <div v-if="item.billingStatus === 'PLANNED'" class="planned-note" lang="ja">
              現在の利用人数から算出した見込み額です。確定額と異なる場合があります。
            </div>
            <!--
              **0円は「欠け」として弾かれない。**
              料金が0円で登録されていると、確定の見送り判定を
              すり抜けて0円の請求が静かに立つ。ここで気づかせる。
            -->
            <div v-if="item.zeroPriceWarning" class="short">
              {{ item.zeroPriceWarning }}
            </div>
            <!--
              0円の注意は「料金を保存し直してください」と言う。帯の行と同じく、直す場所
              （「プランの料金」のそのプランの行）へ1回で移れるようにする（2026-10-10）。
              料金を直せない人（保存のボタンが出ない人）には出さない。
            -->
            <div v-if="item.zeroPriceWarning && canJumpToPlanPrice(item)" class="zero-price-jump">
              <CButton
                size="sm"
                color="secondary"
                variant="outline"
                :disabled="loading || savingPricing"
                data-testid="jump-to-plan-price-from-row"
                @click="goToPlanPrice(item)"
              >
                {{ planPriceJumpLabel }}
              </CButton>
            </div>
            <!--
              **記録が足りない月は、暫定額だと分かるように出す。**

              「その月を漏れなく数えたか」は確定してよいかの判断で、
              いまいくらになりそうかの判断ではない。止めてしまうと
              月の途中に金額を見たい人が誰も見られないので、金額は
              出す。ただし**黙って出すと、運営はそれで入金を読む**
              （2026-09-19、監査 R3）。
            -->
            <template v-if="item.coverageWarning">
              <!--
                **金額が出ていても、この行は確定できない。**
                理由の中身はすぐ下の注記が書くので、ここでは
                「どうなるか」だけを短く言う。
              -->
              <div class="short">
                未集計の日があるため、このままでは確定できません。
              </div>
              <div class="short">
                {{ item.coverageWarning }}
              </div>
            </template>
          </template>
          <span v-else class="muted">—</span>
        </td>
      </template>

      <template #paidAmount="{ item }">
        <!--
          **請求が立っていない行に「¥0」と書かない。**
          入金が無いのではなく、入金する相手の請求がまだ無い。
        -->
        <td class="numeric">
          <template v-if="item.id">
            {{ yen(item.paidAmount) }}
            <div v-if="isShort(item)" class="short">
              不足 {{ yen(shortage(item)) }}
            </div>
          </template>
          <span v-else class="muted">—</span>
        </td>
      </template>

      <template #billingStatus="{ item }">
        <td>
          <CBadge :color="statusColor(item.billingStatus)">
            {{ statusLabel(item.billingStatus) }}
          </CBadge>
          <!--
            **確定できない理由は、その場に出す。**
            上の帯にもまとめて出しているが、行を見ている人は
            そこまで目を戻さない。
          -->
          <div
            v-if="item.heldReason"
            class="held-note"
            :class="{ calm: item.billingStatus === 'OUT_OF_SCOPE' }"
          >
            {{ item.heldReason }}
          </div>
          <!--
            **発行に選べない理由も、その場に出す**（2026-10-08、監査 B764-2）。

            決済の準備の済んでいない支払方法（口座振替・カード）で確定した、未発行の
            請求。左の選ぶ欄が押せないので、理由と次の手（取り消して確定し直す）が
            同じ行に要る。確定できない理由と同じ場所・同じ見た目で出す。
            どの行に出すかと文は back が決めて返す（発行の入口が断るのと同じ判定）。
          -->
          <div
            v-if="item.issueBlockedReason"
            class="held-note"
            data-testid="issue-blocked-reason"
          >
            {{ item.issueBlockedReason }}
          </div>
        </td>
      </template>

      <template #invoiceNo="{ item }">
        <td>
          <span v-if="item.invoiceNo">{{ item.invoiceNo }}</span>
          <span v-else class="muted">未発行</span>
        </td>
      </template>

      <template #paymentDueAt="{ item }">
        <!-- 日付は折らない（字を 12px にしたら「2026-11-／30」と割れた）。 -->
        <td class="text-nowrap">
          <span v-if="item.paymentDueAt">{{ day(item.paymentDueAt) }}</span>
          <span v-else class="muted">—</span>
        </td>
      </template>

      <!--
        **銀行振込は、入金があったことをシステムが知る手段が無い。**
        通帳を見た人が記録するしかない。
      -->
      <template #payment="{ item }">
        <td>
          <!--
            ボタンの並びは内側の箱に入れる。取り消した結果をボタンの下に
            出すため（欄そのものを横並びにすると、結果もボタンの横へ並ぶ）。
          -->
          <div class="row-actions">
            <!--
              **発行済みのものだけ。**番号の無い紙を出すと、正式な
              請求書なのか見積なのか区別できない。
            -->
            <a
              v-if="item.invoiceNo"
              :href="pdfUrl(item)"
              target="_blank"
              rel="noopener"
              class="btn btn-sm btn-outline-primary"
            >
              PDF
            </a>
            <!--
              **確定前の行には出さない。**まだ請求が立っていないので
              入金を結び付ける先が無い。押せてしまうと、空の欄が開いて
              「記録できない」と受け取られる。
            -->
            <CButton
              v-if="canIssue && item.id"
              color="secondary"
              variant="outline"
              size="sm"
              :disabled="savingPayment"
              @click="openPayments(item)"
            >
              入金
            </CButton>
            <!--
              **間違えて出した請求は、取り消して出し直す。**

              採った番号は取り消せないので、取り消しの記録を残して
              新しい番号で出し直す。払い終わった請求は、先に入金を
              取り消してもらう。
            -->
            <CButton
              v-if="canIssue && canVoid(item)"
              color="danger"
              variant="outline"
              size="sm"
              @click="voidBilling(item)"
            >
              取り消す
            </CButton>
          </div>
          <ActionResultMessage v-bind="rowResultOf(item)"/>
        </td>
      </template>

      <!--
        **［取り消す］を出さない理由を、その行のすぐ下に幅いっぱいで出す**（2026-10-09、
        置き場は 2026-10-10 に直した）。解約済みの会社の行は、状態だけ見れば取り消せるのに
        ボタンが無く、理由が読めなかった。取り消し済み・入金済みの行には出さない
        （その行は、解約済みでなくてもボタンが出ない）。

        はじめは操作の列の中に出していた。列が狭いので文が7行ほどに折り返し、行の高さが
        3倍になって、請求書番号や支払期限の列まで折れた。表の部品の「行の下の差し込み」
        （お客様の「これまでのご請求」の帯の行と同じ出し方）で、列の幅を変えずに出す。
        **差し込みの行は、文の無い会社の行の下にも1本ずつ入る**（部品の作り）。
        2026-10-10 の実機で、その空の行に高さ 45px の帯が見えた（全体の決まりが、表の
        セルに高さ 45px を付けている）。下の見た目の決まりで、差し込みのセルの高さを
        中身どおりにして、文の無い行の下には何も見えないようにする。縞は部品の指定の
        ままにして、差し込みの行のぶんだけずれる所を下の決まりで元の並びへ戻す。

        **狭い幅でも、見えている幅の中で折り返す。**表は横に動く枠の中にあるので、
        そのままだと文が表の幅（見えている幅より広い）で折り返し、右が切れた。
        枠の見えている幅を測って文の幅の上限にし、横に動かしても左端に留める。
      -->
      <template #details="{ item }">
        <div
          v-if="canIssue && voidBlockedByCancelledContract(item)"
          class="row-below-note"
          :style="rowBelowNoteStyle"
          data-void-blocked="cancelled"
        >{{ voidBlockedNote }}</div>
      </template>
    </CDataTable>

    <!--
      **空になるのは、契約企業が1社も無いときだけ。**
      確定前でも契約企業は「未確定（見込み）」で並ぶので、
      「登録したのに出てこない」が起きない。
    -->
    <p v-if="!loading && !rows.length" class="empty">
      この月に請求する契約企業がありません。
      <strong>契約企業一覧で契約を登録すると、ここに見込みが並びます。</strong>
    </p>

    <!--
      **制限の見直しに失敗したことは、いつでも見えるところに出す。**

      前はこの帯が入金の欄（行の「入金」を押して開く）の中に
      あった。請求を取り消したときは開いていないので、
      **見直しが落ちても誰にも見えなかった**
      （2026-09-07、監査2・9）。

      やり直すのは見直しだけ。入金の登録も取り消しも、請求の
      取り消しも、もう一度は押させない。

      **会社ごとに持つ。**前は1組しか持っていなかったので、
      A社の見直しが落ちたまま B社の操作が通ると、**A社の警告が
      消えていた。**やり直し先も応答時の選択を読んでいたので、
      別の会社へ送られることがあった（2026-09-07、監査R7）。
    -->
    <CAlert v-if="reviewFailures.length" color="warning">
      <div v-for="failure in reviewFailures" :key="failure.accountId">
        {{ failure.accountName || ("会社ID " + failure.accountId) }}
        ── {{ failure.message }}
        <CButton
          size="sm"
          color="warning"
          variant="outline"
          class="ml-2"
          :disabled="isRetrying(failure.accountId)"
          @click="retryReview(failure)"
        >
          制限の見直しを再度実行する
        </CButton>
      </div>
    </CAlert>
    <!-- ［制限の見直しを再度実行する］の結果。通るとその会社の警告は消えるので、警告の外に出す。 -->
    <ActionResultMessage v-bind="reviewRetryResult"/>

    <!--
      入金の消し込み。**行の「入金」を押すと開く。**

      表は 0073 で作ったが、書き込む経路が無かった。入金が1件も
      入らないので、支払期限を過ぎた会社は払っていても読み取り専用へ
      落ちる。
    -->
    <section v-if="paymentTarget" ref="paymentSection" class="payments">
      <h2>
        入金の記録 ── {{ paymentTarget.accountName }}
        （{{ readableMonth(paymentTarget.yearMonth) }}）
      </h2>

      <p class="pricing-note">
        ご請求 <strong>{{ yen(paymentTarget.totalAmount) }}</strong>
        ／ ご入金 <strong>{{ yen(paidTotal) }}</strong>
        <span v-if="paymentShortage > 0" class="short">
          （不足 {{ yen(paymentShortage) }}）
        </span>
        <span v-else class="settled">（充当済み）</span>
      </p>

      <!--
        **入金を記録・取り消した直後に、その会社だけ見直している**（2026-10-08）。
        前はここに「ここで状態は変わりません。…毎日 3:20 の自動処理が…決めます。」と
        出していた。実際は記録と取り消しの直後に見直していて、同じ画面の下の
        「契約の状態が変わった記録」の説明とも食い違っていた。文は決めてもらった文（OW-15）。
      -->
      <p class="pricing-note" data-testid="payment-review-note">{{ paymentReviewNote }}</p>

      <div class="table-scroll">
      <table class="pricing-table">
        <thead>
          <tr>
            <th>入金日</th>
            <th class="numeric">金額</th>
            <th>方法</th>
            <th>照合キー</th>
            <th>記録した人</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in paymentRows" :key="row.id">
            <td>{{ day(row.paidAt) }}</td>
            <td class="numeric">{{ yen(row.paidAmount) }}</td>
            <td>{{ methodLabel(row.paymentMethod) }}</td>
            <td>{{ row.externalRef || "—" }}</td>
            <!--
              **記録した人は、名前だけ**（2026-10-08）。back は、記録に付けている内部の目印
              （どの処理が書いたか）を外して返す。名前の無い記録は、照合キーの空欄と
              同じ「—」で出す。
            -->
            <td class="muted">{{ row.recordedBy || "—" }}</td>
            <td>
              <!--
                **読み込んでいる間は押させない。**押せると、
                前の会社の明細が残っているあいだに、その入金を
                取り消せる（2026-09-07、監査R6）。
              -->
              <CButton
                color="danger"
                variant="outline"
                size="sm"
                :disabled="savingPayment || loadingPayments"
                @click="cancelPayment(row)"
              >
                取消
              </CButton>
            </td>
          </tr>
          <!--
            **読み込み中に「ありません」と言わない。**切り替えた
            瞬間に明細を空にするので、そのままだと嘘になる。
          -->
          <tr v-if="loadingPayments">
            <td colspan="6" class="muted">読み込んでいます…</td>
          </tr>
          <tr v-if="!paymentRows.length && !loadingPayments">
            <td colspan="6" class="muted">まだ入金の記録がありません。</td>
          </tr>
        </tbody>
      </table>
      </div>

      <div class="payment-form">
        <CInput
          v-model="paymentForm.paidAt"
          label="入金日"
          type="date"
          :disabled="paymentLocked"
        />
        <CInput
          v-model="paymentForm.paidAmount"
          label="金額"
          type="number"
          min="1"
          :disabled="paymentLocked"
        />
        <!--
          **記録できる方法が1つだけのときは、選ぶ欄にしない**（2026-10-08）。
          いま記録できるのは請求書払い（銀行振込）だけ。1つしか無い選ぶ欄は、
          開いても何も選べない。値として見せる（欄と同じ位置・同じ見出し）。
          つないだ方法を RECORDABLE_PAYMENT_METHODS へ足すと、選ぶ欄に戻る。
          見せるのは、送る値そのもの（押し直しで前の内容を送るときも、その値）。
        -->
        <CSelect
          v-if="methodOptions.length > 1"
          label="方法"
          :value.sync="paymentForm.paymentMethod"
          :options="methodOptions"
          :disabled="paymentLocked"
        />
        <div
          v-else
          class="form-group payment-method-fixed"
          data-testid="payment-method-fixed"
        >
          <label>方法</label>
          <div class="payment-method-fixed__value">{{ methodLabel(paymentForm.paymentMethod) }}</div>
        </div>
        <CInput
          v-model="paymentForm.externalRef"
          label="照合キー"
          description="振込の識別子や取引番号を入力します。同じものは二度記録できません。"
          maxlength="128"
          :disabled="paymentLocked"
        />
        <CInput
          v-model="paymentForm.remarks"
          label="備考"
          :disabled="paymentLocked"
        />
        <!--
          **その操作が取り消されていたことを、画面で伝える。**
          同じ目印はもう通らないので、押しても断られ続ける
          （2026-09-23、監査 F01-2）。新しく始めるのは、
          押した人の意思で。
        -->
        <CAlert
          v-if="paymentOperationState === 'cancelled'"
          color="warning"
          class="payment-form__notice"
        >
          この入金は取り消されています。同じ内容では記録できません。
          入れ直す場合は［新しい入金を始める］を押してください。
          <CButton
            class="ml-2"
            color="warning"
            size="sm"
            :disabled="savingPayment"
            @click="beginNewPayment"
          >
            新しい入金を始める
          </CButton>
        </CAlert>
        <!--
          **結果が分からない入金があるあいだは、入力を変えさせない**
          （2026-09-23、監査 F01-2 の残件）。押し直しは最初に送った
          内容で送るので、ここで直されると画面と送る中身がずれる。
        -->
        <CAlert
          v-if="paymentOperationState === 'pending' && !savingPayment"
          color="info"
          class="payment-form__notice"
        >
          前回の入金処理の結果を確認できませんでした。
          ［入金を記録する］を押すと、前回と同じ内容で再送信します
          （重複して記録されることはありません）。
          <!--
            **ここに［新しい入金を始める］を出さない**（2026-09-23、監査）。
            前の送信が入ったかどうかは、この画面からは分からない。明細は
            応答が落ちる前に読んだものかもしれず、まだ届いていない要求が
            あとから入ることもある。ここで始め直すと、二重に記録される。
            決着は同じ目印の再送でつける（入っていれば成功、入っていなければ記録）。
            毎回失敗する入力（長すぎる照合キーなど）は、画面とサーバで先に断る。
          -->
          <div class="mt-2">
            再送信が成功しない場合は、時間をおいて再度お試しください。
            解消しない場合は、開発へご連絡ください。
            ※別の入金として新たに入力すると、二重に記録されるおそれがあります。
          </div>
        </CAlert>
        <CAlert
          v-if="paymentOperationState === 'conflict'"
          color="warning"
          class="payment-form__notice"
        >
          この操作に対する別の入金データがすでに記録されています。
          上記の入金明細をご確認ください。別の入金として新たに記録する場合は
          ［新しい入金を始める］を押してください。
          <CButton
            class="ml-2"
            color="warning"
            size="sm"
            :disabled="savingPayment"
            @click="beginNewPayment"
          >
            新しい入金を始める
          </CButton>
        </CAlert>
        <CButton
          color="primary"
          :disabled="savingPayment || paymentOperationFinished"
          @click="recordPayment"
        >
          入金を記録する
        </CButton>
        <CButton
          color="secondary"
          variant="outline"
          :disabled="savingPayment"
          @click="closePayments"
        >
          閉じる
        </CButton>
      </div>
      <!--
        **保存できたのか、見直しまで終わったのかを分けて出す。**
        見直しに失敗したときは、入金を二重に登録せずに
        見直しだけ再試行できる。

        置き場所は［入金を記録する］のすぐ下（2026-09-26）。前は明細の表の上で、
        入力欄の下のボタンから見えず、断られた理由は画面上端に出ていた。
      -->
      <ActionResultMessage :text="paymentMessage" :type="paymentMessageType"/>
    </section>

    <!--
      **同じ画面に置く。**「なぜ請求書が出せないのか」を、別の画面へ
      探しに行かせない。設定する場所がどこにも無く、初回導入では
      誰も請求書を出せなかった（2026-09-06）。
    -->
    <!--
      **督促を保留している請求を、黙って放置しない。**

      確定した時点の支払方法が分からない請求（0088 より前に確定
      したもの）。いまの設定で代用すると、確定後に支払方法を変えて
      いた会社の扱いが変わるので、督促を止めて運営に見せる。
    -->
    <!--
      **理由は行ごとに出す。**見出しに理由を書くと、別の理由の行を
      足したときに見出しが嘘になる（2026-09-07）。
    -->
    <CAlert v-if="reviewRows.length" color="warning">
      <div>
        <strong>{{ reviewRows.length }} 件の請求について、内容をご確認ください。</strong>
      </div>
      <ul class="mb-0 mt-2">
        <li v-for="row in reviewRows" :key="row.billingPeriodId">
          {{ row.accountName || ("会社ID " + row.accountId) }}
          … {{ row.yearMonth }}分 {{ yen(row.totalAmount) }}
          <span v-if="row.issued">（請求書は発行済み）</span>
          <span v-else>（請求書は未発行）</span>
          <div v-if="row.reason" class="small text-muted">
            {{ row.reason }}
          </div>
        </li>
      </ul>
    </CAlert>

    <section class="issuer">
      <h2>発行元の設定</h2>
      <!--
        **見るだけの人には、押せる形で出さない（2026-09-22）。**サーバは
        請求書を発行できる人（PROVIDER_INVOICE_ISSUE）以外を断る。前は
        ボタンが押せて、押すと初めて「更新権限がありません」と出た。
      -->
      <p v-if="!canIssue" class="pricing-note">
        閲覧のみです。発行元の変更は、請求書を発行できる運営の担当者が行います。
      </p>
      <p class="pricing-note">
        <strong>請求書に載る、自社（PREIKA運営）の情報です。</strong>
        住所とインボイス登録番号が空だと請求書を発行できません。
        <strong>採った番号は取り消せない</strong>ので、空欄のままでは出しません。
      </p>

      <!--
        **足りないものを、そのまま名前で出す。**「設定してください」
        だけでは、どれを入れればよいか分からない。
      -->
      <CAlert v-if="issuer.missing" color="warning">
        <strong>請求書を発行できません。</strong>
        未設定の項目: {{ issuer.missing }}
      </CAlert>

      <div v-if="issuer.registered === false" class="pricing-note">
        運営の会社がまだ登録されていません。
        先に運営メニューの利用者管理から、運営権限の利用者を作ってください。
      </div>

      <div v-else class="table-scroll">
      <table class="issuer-form">
        <tbody>
          <tr>
            <th>会社名</th>
            <td>
              {{ issuer.name || "（未設定）" }}
              <div class="small text-muted">
                この画面からは変更できません（運営設定から変更してください）。
              </div>
            </td>
          </tr>
          <tr>
            <th>住所<span class="required">必須</span></th>
            <td>
              <CInput
                v-model="issuerForm.address"
                :disabled="issuerSaving || !canIssue"
                placeholder="福岡県福岡市中央区…"
              />
            </td>
          </tr>
          <tr>
            <th>建物名</th>
            <td>
              <CInput
                v-model="issuerForm.building"
                :disabled="issuerSaving || !canIssue"
              />
            </td>
          </tr>
          <tr>
            <th>電話番号</th>
            <td>
              <CInput
                v-model="issuerForm.telephone"
                :disabled="issuerSaving || !canIssue"
              />
            </td>
          </tr>
          <tr>
            <th>インボイス登録番号<span class="required">必須</span></th>
            <td>
              <CInput
                v-model="issuerForm.registrationNo"
                :disabled="issuerSaving || !canIssue"
                placeholder="T1234567890123"
              />
              <div class="small text-muted">
                空のまま出すと「未登録」と印字され、
                受け取った側が仕入税額控除を受けられません。
              </div>
            </td>
          </tr>
          <tr>
            <th>振込先</th>
            <td>
              <CInput
                v-model="issuerForm.bankName"
                :disabled="issuerSaving || !canIssue"
                placeholder="○○銀行 ○○支店 普通 1234567 カ）…"
              />
              <div class="small text-muted">
                請求書払いの会社の紙にだけ出ます。
              </div>
            </td>
          </tr>
          <tr>
            <th>角印</th>
            <td>
              <!--
                **消すつもりの表示を、いちばん先に出す。**チェックを
                入れたのに「登録済み」のままだと、保存で何が起きるか
                読めない。
              -->
              <span v-if="removeSeal">保存すると消えます</span>
              <span v-else-if="issuerForm.sealImage">選択しました</span>
              <span v-else-if="issuer.hasSeal">登録済み</span>
              <span v-else>未登録</span>
              <input
                ref="sealInput"
                type="file"
                accept="image/png,image/jpeg"
                :disabled="issuerSaving || removeSeal || !canIssue"
                class="ml-2"
                @change="pickSeal"
              >
              <!--
                **消す手段を画面に出す。**サーバは空で送れば消せるのに、
                画面からは上書きしかできなかった。間違って登録すると
                差し替えるしかない（テスターの指摘）。
              -->
              <label v-if="issuer.hasSeal" class="seal-remove">
                <input
                  type="checkbox"
                  v-model="removeSeal"
                  :disabled="issuerSaving || !canIssue"
                  @change="onRemoveSealChanged"
                >
                角印を消す
              </label>
              <div class="small text-muted">
                PNG か JPEG、1MBまで。
                <!--
                  **選ばなければ今の印をそのまま残す。**毎回
                  選び直させると、うっかり消える。

                  **下の2文は、角印が登録済みのときだけ出す**（上の「角印を消す」の
                  チェックと同じ条件）。未登録のときは「いまの角印」が無く、チェックも
                  画面に無いので、どちらの文も当てはまらない（2026-10-09、自社設定の
                  同じ欄の画面の確かめで見つかった。こちらも同じ形だったので合わせた）。
                  未登録のときに残る注意は、上の画像の形式だけ。
                -->
                <template v-if="issuer.hasSeal">
                  <span class="seal-keep-note">選ばなければ、いまの角印をそのまま残します。</span>
                  <span class="seal-remove-note">消すときは「角印を消す」にチェックを入れて保存してください。</span>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <!--
        **角印の画像を読み込んでいる間は押せない。**読み込みが終わる前に保存すると、
        選んだ画像を送らずに保存が通り、選んだのに元の角印のまま残る。
      -->
      <CButton
        v-if="issuer.registered !== false && canIssue"
        color="primary"
        :disabled="issuerSaving || sealReading || !canIssue"
        @click="saveIssuer"
      >
        発行元を保存する
      </CButton>
      <!--
        保存の結果は、ボタンのすぐ下に出す（2026-09-26）。前は成功だけが節の頭、
        失敗は画面上端に出ていて、7行の表の下のボタンから見えなかった。
      -->
      <ActionResultMessage :text="issuerMessage" :type="issuerMessageType"/>
    </section>

    <!--
      **同じ画面に置く。**「なぜ請求額が0円なのか」を、別の画面へ
      探しに行かせない。実際に単価が未設定のまま0円と出た。
    -->
    <section class="pricing">
      <!--
        **プランを増やす入口を、料金の表にも置く**（2026-10-08。利用者「いくらでも追加できるように
        できるならプランは随時追加できるようにしたい」）。追加の幕は前からあるが、置き場所が
        契約企業一覧のプランマスター（会社の一覧の下）だけで、料金を見ているこの表からは辿れなかった。
        押すと契約企業一覧へ移り、［プランを追加］の幕が開いた状態で始まる（幕は1か所のまま。
        無料プランもその幕で作る）。合図の渡し方は src/utils/planAddRequest.js。
      -->
      <div class="pricing-head">
        <h2>プランの料金</h2>
        <CButton
          v-if="canEditPricing"
          color="primary"
          size="sm"
          class="pricing-add-plan"
          data-testid="pricing-add-plan"
          @click="goAddPlan"
        >プランを追加</CButton>
      </div>
      <p class="pricing-note">
        <!--
          最低月額（0107）が入ったので、式の説明を置き換える。
          **最低月額に届かない月は最低月額を請求する**ことが分かる文（Gemini と決めた、2026-09-26）。
        -->
        請求額は基本料 ＋ その月に在籍した人数 × 1人あたりの単価で計算し、最低月額に届かない場合は最低月額となります。
        <!--
          **最低月額が何のための欄かを、例で見せる**（PP-6、Gemini。2026-10-08）。利用者から
          「最低月額は10人×1000円の場合1万円と入れろということでしょうか？」と聞かれた。
          式の説明だけでは、何を入れる欄なのかが分からなかった。
        -->
        たとえば1人あたり1,000円で最低月額を10,000円に設定すると、在籍人数が10人に満たない月でも10,000円が請求されます。
        <strong>日割りはありません。</strong>月の途中で入った人も辞めた人も、その月は1人として数えます。
        <!--
          **後半は事実と違っていた**（Gemini S13、2026-09-30）。前は「単価は確定時点の値を
          請求ごとに保持しているため、変更は次回の確定から適用されます。」だった。
          実際に使うのは、確定した時点の値ではなく「その月の1日時点の料金」。
        -->
        設定を変更しても、<strong>すでに確定済みの請求には反映されません。</strong>
        請求の確定には、その月の1日時点の料金が適用されます。
      </p>

      <div ref="pricingScroll" class="table-scroll">
      <table class="pricing-table plan-pricing-table">
        <thead ref="planPriceHead">
          <tr>
            <th>プラン</th>
            <th class="numeric">基本料</th>
            <th class="numeric">1人あたり</th>
            <th class="numeric">
              最低月額
              <div class="pricing-column-note" lang="ja">空欄の場合は最低月額を適用しません。</div>
            </th>
            <th>
              適用請求月
              <!--
                **どの月の請求からこの料金を使うかを、月で選ぶ**（M-A、2026-09-30）。
                前は「いつから」を日付で選ばせ、「当月1日以前の日付を指定」と説明していた。
                請求は月ごとで各月はその月の1日時点の料金を使うので、日付は意味が分かりにくかった
                （利用者「いつからっていりますか？」）。
                説明は月を入れない AM-1（全部の行で共有する見出しに来月を入れると、行の月を
                9月にしても10月を選べと読めた。2026-09-30 の見直し）。
              -->
              <div class="pricing-column-note" lang="ja">{{ appliedMonthNote }}</div>
            </th>
            <th class="numeric">契約中</th>
            <th class="volume-discount-head">人数割</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="plan in plans">
          <!--
            帯の行・0円の注意から移ってきた行には、色の目印を付ける（2026-10-10、実機で発見：
            どの行へ来たのかが分からなかった）。色は、この画面で「気づいてほしいもの」に
            使っている色（送信予約の無い請求書の知らせと同じ）。帯へ戻るか、表示する月を
            変えるまで付けておく。
          -->
          <tr
            :key="plan.id"
            :ref="'planPriceRow-' + plan.id"
            :class="{ 'pricing-row--jumped': planPriceJumpedPlanId === plan.id }"
            :data-testid="'pricing-row-' + plan.id"
          >
            <td>
              <div class="account-name">{{ plan.planName }}</div>
              <div class="account-id">{{ plan.planCode }}</div>
              <!--
                **先の月の料金があれば、今月分と先の月分を1行ずつ全部並べる**（M-D の形。今月分も同じ形で AM-3。
                最低月額は AM-2a／AM-2b。2026-09-30 の見直し）。行の欄は選んでいる適用請求月の値なので、既定の来月に別の料金が
                あると欄は来月の値になる。今月分も文で出さないと今の料金が画面から消える。
                前の月と同じ値の月は並べない。前は先の月の最初の1か月の基本料と1人あたりだけで、
                2つ目以降の月と先の月の最低月額は画面のどこにも出なかった。
              -->
              <template v-if="priceLines(plan).length">
                <div
                  v-for="(line, index) in priceLines(plan)"
                  :key="'price-line-' + index"
                  class="account-id price-line"
                >{{ line }}</div>
              </template>
              <!--
                **先の月の料金が無ければ、今の料金を何年何月分の請求から使っているか**（M-C）。
                欄の値はそのまま今の料金。月が見えないと、過去の月で保存して新しい記録に
                隠れたとき、保存が消えたように見える（2026-09-30、テスターの S5 No.8）。
              -->
              <div v-else-if="priceSince(plan)" class="account-id applied-since">{{ priceSince(plan) }}</div>
            </td>
            <td class="numeric">
              <input
                v-model="plan.basePrice"
                type="number"
                min="0"
                step="100"
                class="form-control price-input"
                :disabled="!canEditPricing || savingPricing"
                @input="markPriceTyped(plan, 'basePrice')"
              >
            </td>
            <td class="numeric">
              <input
                v-model="plan.pricePerUser"
                type="number"
                min="0"
                step="100"
                class="form-control price-input"
                :disabled="!canEditPricing || savingPricing"
                @input="markPriceTyped(plan, 'pricePerUser')"
              >
            </td>
            <td class="numeric">
              <!--
                **最低月額（0107）。空欄は「最低月額なし」。**
                料金と同じ記録（適用開始日つき）に入るので、保存すると
                この欄の値も「適用請求月」から効く。**空にして保存すると、なしになる。**

                無料プランには入れさせない（請求書を作らない月を、最低月額で
                有料にしない。back も断る）。

                **step は付けない。**number の欄は min を刻みの起点にするので、
                min=1・step=100 だと 1, 101, 201… の刻みになり、10,000円が
                刻みに合わない（矢印で 10,001円 などへ動く）。［プランを追加］の欄と同じ1刻み。
              -->
              <input
                v-model="plan.minimumMonthlyAmount"
                type="number"
                min="1"
                :max="minimumMonthlyMax"
                class="form-control price-input minimum-input"
                :class="{ 'is-invalid': minimumIsWrong(plan) }"
                :disabled="!canEditPricing || savingPricing || plan.freePlan === true"
                @input="markPriceTyped(plan, 'minimumMonthlyAmount')"
              >
            </td>
            <td>
              <!--
                **適用請求月（どの月の請求からこの料金を使うか）。既定は来月。**
                送るのはその月の1日。先の月も、料金が無くて確定できなかった過去の月も選べる
                （2026-09-07、監査R3 の直し方）。

                **同じ行の基本料・1人あたり・最低月額は、この月に使われる値にする**（2026-09-30 の見直し）。
                月を変えたら入れ替える。前は欄がいつも今月の値で、来月分を保存してある行で最低月額だけ
                直して保存すると、来月分の基本料と1人あたりが今月の値で置き換わった（確定は同じ月の記録の
                うち後から入れたものを使う）。

                **打った欄は、月を変えても消さない**（2026-10-08。利用者「適用請求月を変えるとなぜか他の
                テキストボックスがクリアされます」）。前は、欄を直したあとで月を変えると確認が出て、
                「はい」で打った値が消え、「いいえ」で月が戻った。金額を入れてから月を選ぶ、という左から
                右への順では入力できなかった。入れ替えるのは打っていない欄だけ。その月に料金の記録が
                無いときは、出ている値を残す（全部の欄が空になっていた）。changePriceMonth。
              -->
              <input
                :value="plan.appliedMonth"
                type="month"
                class="form-control applied-month-input"
                :disabled="!canEditPricing || savingPricing"
                @input="changePriceMonth(plan, $event)"
              >
            </td>
            <td class="numeric">
              <!--
                **何社に効くのかを、押す前に見せる。**
                全社共通のマスタなので、1つ変えると全部に効く。
              -->
              {{ plan.accountCount }} 社
              <div class="muted">{{ plan.userLimitTotal }} 人分</div>
            </td>
            <td class="volume-discount-cell">
              <!--
                **ボリュームディスカウント（0108）。今日効いている帯の要約を小さく出す。**
                帯の切れ目でだけ折り返す（「11〜30名：10%OFF」の途中で切らない）。
                帯が無い・率が全部0なら「割引なし」。
                設定は［人数割設定］の小窓で行う（適用開始日つき。料金の［保存］とは別に送る）。
                **無料プランには出さない**（back も断る。請求しない月に割引は無い）。

                要約の後ろに、今月の組を何年何月分から使っているか（M-C「（{年}年{月}月分〜適用）」）を添える。
                **割引なしのときは添えない。**請求はその月の1日時点の組で計算するので、
                月が見えないと「要約は変わったのに今月の見込みが下がらない」の理由が分からない（点検 2）。
                先の月の組があれば、その下に「（何年何月分から：要約）」を月ごとに全部出す（AM-4。
                先の月で保存した組は、その月が来るまで要約に出ないため。前の月と同じ組の月は出さない。
                2026-09-30 の見直しで、2つ目以降の月も出すようにした）。

                ［人数割設定］は、保存のあとの帯の読み直しのあいだも押せない
                （読み直しの前の帯から小窓が始まらないように。点検 9）。
              -->
              <div class="volume-discount-summary">
                <span
                  v-for="(piece, index) in volumeDiscountPieces(plan)"
                  :key="index"
                  :class="{ 'volume-discount-part': piece.part }"
                >{{ piece.text }}</span><span
                  v-if="volumeDiscountSince(plan)"
                  class="volume-discount-since"
                >{{ volumeDiscountSince(plan) }}</span>
              </div>
              <div
                v-for="(line, index) in volumeDiscountNext(plan)"
                :key="'tiers-line-' + index"
                class="volume-discount-next"
              >{{ line }}</div>
              <CButton
                v-if="canEditPricing && plan.freePlan !== true"
                color="primary"
                variant="outline"
                size="sm"
                class="volume-discount-open"
                :disabled="savingPricing || volumeDiscountReloading"
                @click="openVolumeDiscount(plan)"
              >
                人数割設定
              </CButton>
            </td>
            <td>
              <CButton
                v-if="canEditPricing"
                color="primary"
                variant="outline"
                size="sm"
                :disabled="savingPricing"
                @click="savePricing(plan)"
              >
                保存
              </CButton>
            </td>
          </tr>
          <!--
            **［保存］の結果は、押した行のすぐ下に出す**（2026-10-08。利用者「保存を押しても保存しません」）。
            前は表の下に1つだけ出していた。行が増えると押した［保存］から離れ、画面の下に隠れて、
            保存できたのか断られたのかが分からなかった。［人数割設定］の小窓で保存できたときも、
            そのプランの行の下に出す（断りは小窓の中）。
          -->
          <tr
            v-if="pricingMessage && pricingResultPlanId === plan.id"
            :key="'pricing-result-' + plan.id"
            class="pricing-result-row"
            :data-testid="'pricing-result-' + plan.id"
          >
            <td colspan="8">
              <!--
                **結果の文は、見えている幅の中で折り返す**（2026-10-10、実機で発見）。
                表は横に動く枠の中にあり、このセルは表の幅いっぱい（見えている幅より広い）。
                そのままだと、長い文（0円のときの注意）が右で切れた。枠の見えている幅を
                文の幅の上限にし、横に動かしても左端に留める（一覧の行の下の理由と同じ考え方）。
              -->
              <div class="pricing-result-box" :style="pricingResultStyle">
              <ActionResultMessage :text="pricingMessage" :type="pricingMessageType"/>
              <!--
                **帯の行から移ってきて保存したあとは、帯へ戻る道をここに置く**（2026-10-10）。
                次にすることは、帯のその会社の行の［この会社だけ確定する］。保存のあと自動で
                帯へ戻すと、ここに出る保存の結果（いくらで保存したか・0円のときの注意）を
                読む前に画面が動くので、ボタンにした。出すのは、保存が通ったときだけ。
                字（backToHeldBandLabel）がまだ決まっていないあいだは出さない。
              -->
              <div v-if="canGoBackToHeldBand(plan)" class="back-to-held">
                <CButton
                  size="sm"
                  color="warning"
                  variant="outline"
                  data-testid="back-to-held-band"
                  @click="goBackToHeldBand"
                >
                  {{ backToHeldBandLabel }}
                </CButton>
              </div>
              </div>
            </td>
          </tr>
          </template>
        </tbody>
      </table>
      </div>
      <!-- どの行の結果でもないとき（行が見つからない読み直しなど）だけ、表のすぐ下に出す。 -->
      <ActionResultMessage
        v-if="!pricingResultOnARow"
        :text="pricingMessage"
        :type="pricingMessageType"
      />

      <!--
        ボリュームディスカウントの小窓。**開くときだけ作る**（開くたびに、そのときの行から始める）。
        閉じる（×・キャンセル）は小窓が送っているあいだ止める。保存できたら、ここで守りなしで閉じる。
      -->
      <PlanVolumeDiscountModal
        v-if="volumeDiscountPlan"
        :plan="volumeDiscountPlan"
        :default-month="defaultAppliedMonth()"
        @close="volumeDiscountPlan = null"
        @saved="volumeDiscountSaved"
      />

      <p v-if="!canEditPricing" class="pricing-note">
        料金の変更には<strong>契約プラン変更の権限</strong>が要ります。
      </p>
    </section>

    <!--
      請求書メールの送付。

      **請求書を発行すると、送信予約が1本積まれる**（発行と同じ取引）。
      決めた日時が来ると送られる。運営が止めなければ届く。

      発行した瞬間に送ると、宛先の間違いに気づく前に相手の経理へ届く。
      かといって毎月手で送るなら忘れる。**忘れても届き、気づけば
      止められる**形にしてある。
    -->
    <section class="mail">
      <h2>請求書メールの送付</h2>

      <p class="pricing-note">
        請求書を発行すると、<strong>送信予約が1本積まれます</strong>。
        下で決めた日時が来ると送られます。
        <strong>止めたいものは、送信予定の一覧で止めてください。</strong>
      </p>

      <div v-if="mailSettingLoadError" class="mail-warning">
        {{ mailSettingLoadError }}
      </div>

      <div v-else class="mail-setting">
        <label class="mail-switch">
          <input
            v-model="mailForm.enabled"
            type="checkbox"
            :disabled="!canIssue || mailSaving"
          >
          <span>毎月、自動で送る</span>
        </label>

        <div class="mail-row">
          <span>毎月</span>
          <input
            v-model.number="mailForm.sendDay"
            type="number"
            min="1"
            max="28"
            class="mail-number"
            :disabled="!canIssue || mailSaving"
          >
          <span>日の</span>
          <input
            v-model.number="mailForm.sendHour"
            type="number"
            min="0"
            max="23"
            class="mail-number"
            :disabled="!canIssue || mailSaving"
          >
          <span>時に送る</span>
        </div>
        <p class="mail-hint">
          <!--
            **29〜31日を選べないことを、先に書く。**
            保存してから断られるより、選ぶ前に分かるほうがよい。
          -->
          1〜28日で指定してください。29日以降にすると、
          <strong>その日が無い月に1通も送られません</strong>。
        </p>

        <div class="mail-row">
          <span>発行から</span>
          <input
            v-model.number="mailForm.holdHours"
            type="number"
            min="0"
            max="720"
            class="mail-number"
            :disabled="!canIssue || mailSaving"
          >
          <span>時間は送らない</span>
        </div>
        <p class="mail-hint">
          <strong>確認のための猶予です。</strong>
          送付日を過ぎてから発行したときも、この時間だけおいてから
          送ります（翌月まで待ちません）。
        </p>

        <div class="mail-row">
          <span class="mail-label">件名のあたま</span>
          <input
            v-model="mailForm.subjectPrefix"
            type="text"
            maxlength="100"
            class="mail-text"
            placeholder="空なら既定のあたま"
            :disabled="!canIssue || mailSaving"
          >
        </div>

        <div class="mail-row">
          <span class="mail-label">控えの宛先（Bcc）</span>
          <input
            v-model="mailForm.bccEmail"
            type="text"
            maxlength="255"
            class="mail-text"
            placeholder="空なら送りません"
            :disabled="!canIssue || mailSaving"
          >
        </div>

        <div class="mail-row">
          <CButton
            color="primary"
            size="sm"
            :disabled="!canIssue || mailSaving"
            @click="saveMailSetting"
          >
            {{ mailSaving ? '保存しています…' : '設定を保存' }}
          </CButton>
          <span v-if="mailMessage" class="mail-message">
            {{ mailMessage }}
          </span>
        </div>
      </div>

      <!--
        **予約が積まれていない発行済み請求書。**

        この仕組みを入れる前に発行したものが残る。出さないと、
        送られないまま誰も気づかない。
      -->
      <div v-if="mailMissingRows.length > 0" class="mail-warning">
        <strong>
          送信予約が無い請求書が {{ mailMissingRows.length }} 件あります。
        </strong>
        この仕組みを入れる前に発行したものです。
        <strong>このままでは送られません。</strong>
        <ul class="mail-missing">
          <li v-for="row in mailMissingRows" :key="row.billingPeriodId">
            {{ row.accountName }}
            （{{ readableMonth(row.yearMonth) }}／{{ row.invoiceNo }}）
          </li>
        </ul>
      </div>

      <h3 class="mail-subtitle">送信予定と、送った履歴</h3>

      <div class="mail-row">
        <CButton
          color="secondary"
          size="sm"
          :disabled="!canIssue || mailRunning"
          @click="runMailNow"
        >
          {{ mailRunning ? '送っています…' : '時刻が来ているぶんを、いま送る' }}
        </CButton>
        <span class="mail-hint">
          ふだんは毎時05分に自動で走ります。
        </span>
      </div>
      <ActionResultMessage v-bind="mailRunResult" class="mail-run-result"/>

      <p v-if="mailRows.length === 0" class="muted">
        まだ送信予定がありません。請求書を発行すると一覧に追加されます。
      </p>

      <div v-else class="table-scroll">
      <table class="pricing-table">
        <thead>
          <tr>
            <th>いつ送る</th>
            <th>契約企業</th>
            <th>対象月</th>
            <th>請求書番号</th>
            <th>宛先</th>
            <th>状態</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in mailRows" :key="row.id">
            <td class="nowrap mail-scheduled-at">{{ dateTime(row.scheduledAt) }}</td>
            <td>
              <div class="account-name">{{ row.accountName }}</div>
              <div class="account-id">ID {{ row.accountId }}</div>
            </td>
            <td class="nowrap">{{ readableMonth(row.yearMonth) }}</td>
            <td class="nowrap">{{ row.invoiceNo || '—' }}</td>
            <td>
              <div v-if="row.toEmail">{{ row.toEmail }}</div>
              <div v-else class="mail-danger">宛先が設定されていません</div>
              <div class="account-id">
                {{ mailSourceLabel(row.toSource) }}
              </div>
            </td>
            <td class="nowrap">
              <CBadge :color="mailStatusColor(row.status)">
                {{ mailStatusLabel(row) }}
              </CBadge>
              <!--
                **設定で止まっているのか、1本ずつ止めたのかを分ける。**
                同じ「止めています」でも、直し方が違う。
              -->
              <!--
                **判定は back がしている。**
                止めた人の欄の中身をここで見比べると、同じ文字列が
                2つのリポジトリにできて、片方を直したときに
                見分けが静かに壊れる。
              -->
              <div
                v-if="row.status === 'HELD' && row.heldBySetting"
                class="account-id"
              >
                自動送付が「切」のため
              </div>
              <div v-if="row.lastError" class="account-id mail-danger">
                {{ row.lastError }}
              </div>
            </td>
            <td class="nowrap">
              <CButton
                v-if="row.status === 'PENDING'"
                color="warning"
                size="sm"
                :disabled="!canIssue || mailBusyIds.includes(row.id)"
                @click="holdMail(row)"
              >
                止める
              </CButton>
              <CButton
                v-else-if="row.status === 'HELD'"
                color="primary"
                size="sm"
                :disabled="!canIssue || mailBusyIds.includes(row.id)"
                @click="releaseMail(row)"
              >
                送信待ちへ戻す
              </CButton>
              <!--
                **宛先を直したあとの積み直し。**

                「戻す」は使えない。あれは状態を送信待ちへ戻すだけで
                宛先も予定日時も更新しないので、空のまま拾われて
                また落ちる。ここは宛先を引き直してから積み直す。

                **宛先が無い予約にだけ出す。**ほかの状態で押せると、
                送信済みのものまで積み直せるように見える（back は
                409 で断るが、押せる時点で誤解させる）。
              -->
              <CButton
                v-else-if="row.status === 'NO_RECIPIENT'"
                color="primary"
                size="sm"
                :disabled="!canIssue || mailBusyIds.includes(row.id)"
                @click="requeueMail(row)"
              >
                宛先を反映して再予約
              </CButton>
              <span v-else class="muted">—</span>
              <ActionResultMessage v-bind="mailRowResultOf(row.id)"/>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </section>

    <!--
      契約の状態が変わった記録。

      **「なぜこの会社が止まっているのか」は必ず聞かれる。**
      記録は前から取っていたのに読む経路が1本も無く、
      DB を直に見るしかなかった。
    -->
    <section class="history">
      <h2>契約の状態が変わった記録</h2>
      <p class="pricing-note">
        止めたのも戻したのも<strong>仕組みが判断します</strong>
        （毎日 03:20 のほか、<strong>入金を記録・取り消した直後にも
        その会社だけ見直します</strong>）。新しい順に100件まで。
      </p>

      <!--
        **読めなかったときと、0件を分ける**（2026-10-09）。前は読めなかったときも
        空の並びにしていたので、権限や通信で落ちても「まだ記録がありません。」と出た。
        最初の応答が返るまでは、どちらも出さない（読む前に「ありません」と言わない）。
      -->
      <p v-if="historyFailed" class="muted" data-history="failed">{{ historyFailedNote }}</p>
      <p
        v-else-if="historyLoaded && history.length === 0"
        class="muted"
        data-history="empty"
      >{{ historyEmptyNote }}</p>

      <div v-else-if="history.length > 0" class="table-scroll">
      <table class="pricing-table">
        <thead>
          <tr>
            <th>いつ</th>
            <th>契約企業</th>
            <th>状態</th>
            <th>理由</th>
            <th>きっかけ</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in history" :key="row.id">
            <td class="nowrap">{{ row.changedAt }}</td>
            <td>
              <div class="account-name">{{ row.accountName }}</div>
              <div class="account-id">ID {{ row.accountId }}</div>
            </td>
            <td class="nowrap">
              <span class="muted">
                {{ contractLabel(row.statusBefore) }}
              </span>
              →
              <CBadge :color="contractColor(row.statusAfter)">
                {{ contractLabel(row.statusAfter) }}
              </CBadge>
            </td>
            <!--
              **理由は、説明の文だけを出す**（2026-10-08）。前は説明が無いと理由の
              英字のコードをそのまま出す作りだった。back が書く理由（未払いで落とした・
              入金で戻した）にはどちらも説明の文がある。無い記録は「—」。
            -->
            <td data-testid="history-reason">{{ row.reasonDetail || "—" }}</td>
            <!--
              **誰が変えたかは出さない**（2026-10-08）。前は「きっかけ」の下に、記録の
              内部の名前がそのまま出ていた。この記録を書くのは仕組みだけで、いつも同じ
              名前（上の説明に「仕組みが判断します」と書いてある）。back も返さない。
            -->
            <td class="nowrap" data-testid="history-trigger">
              <span v-if="row.yearMonth">
                {{ readableMonth(row.yearMonth) }}の請求
              </span>
              <span v-else class="muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </section>
  </div>
</template>

<script>
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import PlanVolumeDiscountModal from './PlanVolumeDiscountModal.vue'
import { askToAddPlan } from '@/utils/planAddRequest'
import { getAccessContext } from '@/services/accessContext'
import providerBilling from '@/services/providerBilling'
import nationalHolidayMaster from '@/services/nationalHolidayMaster'
import { SUMMARY_SEPARATOR, appliedSinceText, nextTiersLines, summaryParts, summaryText } from '@/utils/volumeDiscount'
import {
  APPLIED_MONTH_NOTE,
  EMPTY_MONTH_MESSAGE,
  appliedSinceText as priceSinceText,
  entryOfMonth,
  monthStartOf,
  monthValueOf,
  nextMonthValue,
  priceSavedText,
  priceScheduleLines,
  tiersSavedText
} from '@/utils/billingMonth'
import { revealSection } from '@/utils/revealSection'
import {
  canIssueProviderInvoice,
  canChangeProviderPlan,
  canManageProviderNoticesAndHolidays
} from '@/utils/accessPolicy'
import moment from 'moment'

/** 日本時間の、協定世界時からのずれ（分）。日本に夏時間は無い。 */
const JAPAN_UTC_OFFSET_MINUTES = 9 * 60

/**
 * 最低月額（0107）の範囲。**1円〜1,000万円。**0円は「なし」と区別が付かない
 * ので受け付けない（なしは空欄）。back（PlanPricingService）と同じ範囲で断る。
 */
const MINIMUM_MONTHLY_MIN = 1
const MINIMUM_MONTHLY_MAX = 10000000

/** 基本料金・1人あたり単価の上限。back（PlanPricingService.MAX_AMOUNT）と同じ1,000万円。 */
const PLAN_AMOUNT_MAX = 10000000

/* 「プランの料金」の行で、適用請求月に合わせて入れ替える欄。 */
const PRICE_FIELDS = ['basePrice', 'pricePerUser', 'minimumMonthlyAmount']

/** 見込み（DRAFT）へ請求書は出さない。取消（VOID）にも出さない。 */
const ISSUABLE = ['FIXED', 'PAID']

/**
 * 金額を持たない行。
 *
 * **確定できない行と、この月は請求しない行。**
 * ここに 0 を書くと、0円で請求されると読める。
 * 見込み（PLANNED）は入れない。金額を出せるから。
 */
const NO_AMOUNT = ['HELD', 'OUT_OF_SCOPE']

/*
 * 契約の状態。**請求の状態とは別。**
 *
 * 同じ関数を使い回すと、見つからなかった値をそのまま返す作りなので、
 * ACTIVE や READ_ONLY が生の英字で画面に出る。
 */
const CONTRACT_LABEL = {
  PREPARING: '準備中',
  TRIAL: '試用',
  ACTIVE: '利用中',
  READ_ONLY: '未払い（見るだけ）',
  SUSPENDED: '未払い（停止）',
  CANCELLED: '解約',
  EXPIRED: '期限切れ'
}

const CONTRACT_COLOR = {
  PREPARING: 'secondary',
  TRIAL: 'info',
  ACTIVE: 'success',
  READ_ONLY: 'warning',
  SUSPENDED: 'danger',
  CANCELLED: 'dark',
  EXPIRED: 'dark'
}

const STATUS_LABEL = {
  DRAFT: '見込み',
  FIXED: '確定',
  INVOICED: '請求書発行済み',
  PAID: '入金済み',
  VOID: '取消',
  /*
   * **確定前の2つ。**表には行があるが、請求はまだ立っていない。
   *
   * 前は確定して初めて行ができたので、契約企業を登録しても
   * 確定するまで一覧は空のままだった。**登録したのに出てこない**
   * と読める（2026-09-18、利用者の指摘）。
   */
  PLANNED: '未確定（見込み）',
  HELD: '確定できません',
  /*
   * **請求しない会社も出す。**無料試用中・解約済み・利用開始前。
   * 出さないと、運営からは「登録したのに出てこない」としか見えない。
   */
  OUT_OF_SCOPE: '請求しません'
}

const STATUS_COLOR = {
  DRAFT: 'secondary',
  FIXED: 'info',
  INVOICED: 'primary',
  PAID: 'success',
  VOID: 'dark',
  PLANNED: 'info',
  /* 確定できないのは手当てが要る。**この月は請求しない、とは別。** */
  HELD: 'warning',
  OUT_OF_SCOPE: 'secondary'
}

const ISSUE_LABEL = {
  ISSUED: '発行しました',
  ALREADY_ISSUED: 'すでに発行済みでした（二重には出していません）',
  NOT_FOUND: '対象の請求が見つかりませんでした',
  /*
   * **英語のまま出さない。**
   *
   * 断った理由が ISSUER_NOT_READY と出ても、運営には何をすれば
   * よいか分からない。理由の本文（message）も行に出す。
   */
  NOT_ISSUABLE: '確定していないため発行しませんでした',
  ISSUER_NOT_READY: '発行元の情報が不足しているため、発行しませんでした',
  /*
   * 運営の会社（請求書の発行元）宛ての請求だった（2026-10-08、9/30 の決め1）。
   * 前に立った運営の会社宛ての請求が、一括発行に含まれたときに出る。
   * 文は Gemini と決めた OW-3（画面での呼び名は「運営会社」）。
   */
  OWN_ACCOUNT: '運営会社宛てのため発行しませんでした',
  /*
   * 決済の準備の済んでいない支払方法（口座振替・カード）で確定した請求だった
   * （2026-10-08、監査 B764-2）。一覧では選べないが、一括発行に含まれると出る。
   * 理由と次の手（取り消して確定し直す）は、back が返す本文に出る。
   * 文は決めてもらった文（OW-19）。
   */
  PAYMENT_METHOD_NOT_READY: 'お支払い方法が口座振替・クレジットカードのため発行しませんでした',
  FAILED: '発行できませんでした'
}

/*
 * 運営の会社（請求書の発行元）の行に出す印。一覧の「契約企業」の欄、会社名の下。
 * 文は Gemini と決めた OW-2（2026-10-08、9/30 の決め1）。ヘルプも同じ札の名前で書いてある。
 */
const ISSUER_ACCOUNT_MARK = '運営会社（請求対象外）'

/*
 * 入金の欄（行の［入金］で開く）の説明。合計のすぐ下に出す。
 * 文は Gemini と決めた OW-15（2026-10-08）。もとの事実：入金を記録・取り消すと、その直後に
 * その会社だけ支払状況を見直す（合計が請求額に届けば札が「入金済み」になり、止まっていた契約が戻る）。
 */
/*
 * 請求管理の文（2026-10-09、Gemini と決めた B1〜B4。一字も変えない）。
 * B1・B4 は、画面の実際の呼び名に合わせて決め直した文（「操作履歴画面」という
 * 画面も、「解約を取り消す」というボタンも無い）。
 *   B1 … 「契約の状態が変わった記録」が0件のとき
 *   B2 … 同じ欄で、記録を読めなかったとき
 *   B3 … ［確定分を一括発行］が、発行できる確定済みの請求が0件で押せないとき
 *   B4 … 解約済みの会社の行に［取り消す］を出さない理由
 */
const HISTORY_EMPTY_NOTE = '利用制限およびその解除の記録はありません。支払遅延による利用制限や、入金完了による制限解除の履歴がここに表示されます。'
const HISTORY_FAILED_NOTE = '記録を表示できませんでした。画面を開き直してください。'
const NOTHING_TO_ISSUE_NOTE = '発行できる確定済みの請求がありません。'
const VOID_BLOCKED_BY_CANCELLED_CONTRACT_NOTE = '解約済みの契約のため請求を取り消せません。変更が必要な場合は「契約企業一覧」の［管理］を開き、「契約」タブで「契約状態」を切り替えて［契約を更新］したあとにお試しください。'

/**
 * 請求の状態だけを見て、取り消せる状態か（確定済み・発行済み）。
 * ［取り消す］を出すか（canVoid）と、出さない理由を出すか
 * （voidBlockedByCancelledContract）が、同じ決まりを見る。
 */
function isVoidableStatus (row) {
  return row.billingStatus === 'FIXED' || row.billingStatus === 'INVOICED'
}

/*
 * 0円の行から料金の行へ移るボタン・保存のあと帯へ戻るボタンの字（2026-10-10）。
 * 字は Gemini と決めた L1・L2（一字も変えない）。空にすると、ボタンは出ない。
 */
const PLAN_PRICE_JUMP_LABEL = '料金を設定'
const BACK_TO_HELD_BAND_LABEL = '帯へ戻る'

/**
 * 料金の行へ移るとき、見出しから行の下端までが「画面の高さ − この余白」に収まれば、
 * 見出しを上端に合わせる（上の固定の帯と、下の少しの余白のぶん）。
 */
const PLAN_PRICE_REVEAL_MARGIN_PX = 120

/** 見送りの理由の符号：請求額が0円（back の見送りの行の missing）。 */
const MISSING_ZERO_AMOUNT = 'zeroAmount'

const PAYMENT_REVIEW_NOTE = '入金を記録または取り消すと、その会社の支払状況がすぐに更新され、利用制限や状態に反映されます。'

/*
 * 入金の方法の呼び名（2026-10-08、9/30 の決め3）。
 *
 * **記録できる方法と、呼び名の表を分ける。**口座振替とクレジットカードは、
 * 引き落とす仕組み・決済代行につなぐまで、入金の方法として選ばせない
 * （その方法で入金されることが無いので、選べると「使えている」ように見える）。
 * それでも呼び名は残す——**前に口座振替やカードとして記録した入金の明細**が、
 * 英字（BANK_TRANSFER・CARD）で出ないように。
 */
const PAYMENT_METHOD_LABEL = {
  BANK_TRANSFER: '口座振替',
  INVOICE: '請求書払い（銀行振込）',
  CARD: 'クレジットカード'
}

/*
 * いま入金の方法として記録できるもの。画面の選択肢の並び順。
 *
 * **つないだ日に、ここへ足す**（'BANK_TRANSFER'・'CARD'）。呼び名は上の表に
 * あるので、足すだけで選ぶ欄に出る。1つだけのあいだは、選ぶ欄ではなく値として
 * 見せる（1つしか無い選ぶ欄は、開いても何も選べない）。
 */
const RECORDABLE_PAYMENT_METHODS = ['INVOICE']

/*
 * 送信箱の状態。**英字のまま出さない。**
 *
 * back（InvoiceMailService.label）と同じ言葉にしてある。
 * 画面とログで呼び名が違うと、問い合わせのときに話が合わない。
 *
 * **back で状態を足したら、ここにも足す。**足し忘れると英字のまま
 * 出る。取り消し・結果不明・積み直しの3つが、back にだけ入っていた
 * （2026-09-12 に気づいた）。
 */
const MAIL_STATUS_LABEL = {
  PENDING: '送信待ち',
  HELD: '止めています',
  SENDING: '送信中',
  SENT: '送信済み',
  FAILED: '送れませんでした',
  NO_RECIPIENT: '宛先が設定されていません',
  CANCELED: '請求が取り消されました',
  SEND_UNKNOWN: '送信結果が不明です',
  SUPERSEDED: '再予約済み'
}

/** 宛先は入っているが、メールアドレスの形でない行の札（Gemini 2026-10-09、KZ-2）。 */
const MAIL_STATUS_LABEL_BAD_RECIPIENT = '宛先不正'

/*
 * **結果不明は、人が確かめるまで終わらない。**失敗と同じ色にして、
 * 送信済みの並びに埋もれさせない。取り消し・積み直しは、もう何も
 * しなくてよい行なので目立たせない。
 */
const MAIL_STATUS_COLOR = {
  PENDING: 'info',
  HELD: 'warning',
  SENDING: 'primary',
  SENT: 'success',
  FAILED: 'danger',
  NO_RECIPIENT: 'danger',
  CANCELED: 'secondary',
  SEND_UNKNOWN: 'danger',
  SUPERSEDED: 'secondary'
}

/**
 * 宛先をどこから採ったか。
 *
 * **根拠を画面に出す。**「なぜこのアドレスなのか」は必ず聞かれる。
 * 出しておかないと、直す場所を探して運営がDBを見ることになる。
 */
const MAIL_SOURCE_LABEL = {
  BILLING_SETTING: '請求先の設定',
  CONTRACT_PARTY: '契約の請求先',
  ACCOUNT_CONTACT: '会社の請求担当',
  TENANT_OWNER: '契約企業の管理者'
}

/*
 * 「いま送る」の結果の文。**文言は、ここ1か所で組む。**
 *
 * **結果不明を「送れなかった」と言わない。**送信を始めたあとに
 * 落ちたものは、相手に届いている可能性がある。「送れなかった」と
 * 読んだ運営が送り直すと、2通届く（2026-09-12、監査）。
 * そのため back は、種類ごとに数を返す。
 *
 * 呼び名は表の状態名（MAIL_STATUS_LABEL）とそろえる。
 * 言い回しは 2026-09-12 に Gemini で確かめた（事務の担当者が読む前提）。
 */
const MAIL_RUN_TEXT = {
  sent: n => n + '通の送信を完了しました。',
  sendUnknown: n => '送信結果が不明なもの ' + n + '通'
    + '（相手に届いている可能性があるため、自動では送り直しません。'
    + '一覧で状態をご確認ください）。',
  failedBeforeSmtp: n => '送れなかったもの ' + n + '通'
    + '（相手には届いていません。一覧で理由をご確認ください）。',
  canceled: n => '請求の取り消しにより送信しなかったもの ' + n + '通。',
  /*
   * **「次回の自動送信で処理する」と約束しない（2026-09-12、監査）。**
   * commit のあとに close だけが落ちた回は、結果不明や「送れませんでした」に
   * 進んでいることがある。どうなったかは一覧で見てもらう。
   */
  deferred: n => '今回の処理で結果が確定しなかったもの '
    + n + '通（一覧で状態をご確認ください）。',
  sweptToSendUnknown: n => '前回の送信処理で結果が確定しなかった '
    + n + '通の状態を「送信結果が不明です」に変更しました。',
  /*
   * **古い back（sent と failed だけ）のときの言い方。**
   * その failed には結果不明も取り消しも混ざっているので、
   * 「送れなかった」とは言い切らない。
   */
  notSentOnOldBack: n => '（ほか ' + n + '通は送信が完了していません。'
    + '一覧で状態をご確認ください）'
}

/*
 * 送信済みのあとに、この順でつなぐ。**0 のものは出さない。**
 * 送信済みだけは0でも出す（押せていないのと見分けがつかない）。
 */
const MAIL_RUN_ORDER = [
  'sendUnknown',
  'failedBeforeSmtp',
  'canceled',
  'deferred',
  'sweptToSendUnknown'
]

function mailRunMessage (outcome) {
  const count = key => Number((outcome && outcome[key]) || 0)
  const sentText = MAIL_RUN_TEXT.sent(count('sent'))
  /*
   * **sendUnknown のキーが無ければ、古い back。**front を先に出したとき
   * にここを通る。新しい back の failed は古い画面のための合計なので、
   * 下の新しい分岐では読まない。back と front の両方が出たら、back の
   * failed と一緒にこの分岐を消す。
   */
  if (!outcome
    || !Object.prototype.hasOwnProperty.call(outcome, 'sendUnknown')) {
    const failed = count('failed')
    return sentText
      + (failed > 0 ? MAIL_RUN_TEXT.notSentOnOldBack(failed) : '')
  }
  return MAIL_RUN_ORDER
    .filter(key => count(key) > 0)
    .reduce((text, key) => text + MAIL_RUN_TEXT[key](count(key)), sentText)
}

export default {
  name: 'PlatformBilling',

  components: { ActionResultMessage, PlanVolumeDiscountModal },

  data () {
    return {
      yearMonthInput: this.defaultMonth(),
      rows: [],
      selectedIds: [],
      issueResults: [],
      loading: false,
      issuing: false,
      /*
       * **画面上端に出すのは、読み込みの失敗だけ**（2026-09-26）。
       * 押した操作の結果は、押したボタン・行・節のそばへ出す（下の fixError・
       * accountFixResult・rowResults・reviewRetryResult・paymentMessage・
       * issuerMessage・pricingMessage・mailRunResult・mailRowResults）。
       * 上端だと各節から遠くて見えず、しかも確定・発行の失敗は直後の
       * 読み直し（load）が消していた。
       */
      errorMessage: '',
      /* 上の並びのボタン（確定・見直し・人数・発行）の失敗。並びのすぐ下に出す。 */
      fixError: '',
      /* 見送り・失敗の一覧の［この会社だけ確定する］の結果。一覧のすぐ下に出す。 */
      accountFixResult: { type: 'info', text: '' },
      /* 表の行の［取り消す］の結果。キーは契約企業。その行に出す。 */
      rowResults: {},
      /* ［制限の見直しを再度実行する］の結果。見直しの警告のすぐ下に出す。 */
      reviewRetryResult: { type: 'success', text: '' },
      accessContext: null,
      /*
       * 祝日マスタの残り（/platform-holidays の答え）。取り込める人にだけ読む。
       * 読めなかったときは何も出さない（請求の画面を止めない）。
       */
      holidayStock: null,
      plans: [],
      /* 督促を保留している請求。**確定時の支払方法が不明。** */
      reviewRows: [],
      /* 見直しで落ちた会社。**件数に混ぜない。** */
      dunningFailedRows: [],
      /* 入金は保存できたが、制限の見直しに失敗した。 */
      /*
       * **見直しに失敗した会社。**{accountId, accountName, message}
       *
       * 1組のスカラーで持っていたときは、A社の失敗が未解決のまま
       * B社の操作が通ると**A社の警告が消えていた**
       * （2026-09-07、監査R7）。配列にして、その会社の見直しが
       * 通ったときだけ外す。
       */
      reviewFailures: [],
      /*
       * **警告の世代。**足すたびに1つ増える。
       *
       * 会社ごとに持っても、応答が届く順番までは決められない。
       * 再試行の成功が遅れて届くと、そのあいだに起きた**新しい
       * 失敗まで消えていた**（2026-09-07、監査S4-B）。
       * 要求を出した時点の世代を控え、それより新しい失敗は
       * 消さない。
       */
      reviewVersion: 0,
      /*
       * いま見直しをやり直している会社。**二重に押させない。**
       *
       * 1社しか持てない形にしていたので、A社が処理中のまま B社を
       * 押すと**A社のボタンが復活して二重に送れた**
       * （2026-09-07、監査S4）。会社ごとに持つ。
       */
      retryingAccountIds: [],
      /*
       * **入金の明細を読み込んでいるか。**
       *
       * 切り替えた直後は前の会社の明細が残っていて、その行の
       * 取消を押すと**前の会社の入金が消える**（監査R6）。
       */
      loadingPayments: false,
      /*
       * **何回目の読み込みか。**遅れて届いた古い応答を捨てる。
       * 請求IDの比較だけだと、同じ請求を2回開いたときに区別が
       * つかない。
       */
      paymentSeq: 0,
      /* 発行元（運営会社）。**請求書に載る内容。** */
      issuer: {},
      issuerForm: {
        address: '',
        building: '',
        telephone: '',
        registrationNo: '',
        bankName: '',
        /* **選ばなければ null のまま。**今の角印を残す。 */
        sealImage: null
      },
      /*
       * 角印を消すか。
       *
       * **issuerForm には入れない。**あちらは送る内容そのもので、
       * sealImage は「null=そのまま／空=消す／中身=差し替え」の
       * 3状態を持つ。消すかどうかは画面の都合なので、送るときに
       * 空文字へ翻訳する。
       */
      removeSeal: false,
      /*
       * 角印の画像を読み込んでいる最中か。**この間は保存しない。**
       * 読み込みの前に保存すると sealImage がまだ null で、角印を送らずに保存が通る。
       */
      sealReading: false,
      /*
       * 角印を選んだ（選び直した・取り消した・消すことにした）回数。
       * 読み込みは選んだ順に終わるとは限らない。終わったときにこの番号が自分の番と
       * 違えば、あとから別の操作があったので、その結果は捨てる。
       */
      sealPick: 0,
      issuerSaving: false,
      /* 発行元の保存の結果。［発行元を保存する］のすぐ下に出す。 */
      issuerMessage: '',
      issuerMessageType: 'success',
      history: [],
      /* 記録を読めなかったか（0件と分けて出す）。 */
      historyFailed: false,
      /* 記録の最初の応答が返ったか。返るまでは「ありません」と出さない。 */
      historyLoaded: false,
      /*
       * 記録を読み直した回数。**いちばん新しく始めた読み直しの応答だけを反映する。**
       * 入金・取り消しのたびに読み直すので、前の応答があとから返ることがある。
       */
      historySeq: 0,
      /* 請求の一覧を読めたか。読めなかったときは「発行できる請求がありません」と言わない。 */
      rowsLoaded: false,
      /* 一覧を読み直した回数。いちばん新しく始めた回の応答だけを反映する。 */
      loadSeq: 0,
      /*
       * 請求の一覧の、見えている幅（横に動く枠の内側の幅）。行の下に出す理由の文の
       * 幅の上限にする。0 は「まだ測れていない」。
       */
      tableVisibleWidth: 0,
      /* 送信予定を読み直した回数。いちばん新しく始めた回の応答だけを反映する。 */
      mailOutboxSeq: 0,
      /*
       * 0円の行から「プランの料金」のそのプランの行へ移るボタンと、保存のあと帯へ戻る
       * ボタンの字（Gemini と決めた字。定数の側に書いてある）。空のあいだはボタンを出さない。
       */
      planPriceJumpLabel: PLAN_PRICE_JUMP_LABEL,
      backToHeldBandLabel: BACK_TO_HELD_BAND_LABEL,
      /*
       * 確定の要求の通し番号（全社の確定・1社の確定で共通）。返事が届いたとき、
       * 「いちばん新しく始めた要求で、対象の月がいま表示している月」のときだけ画面へ書く。
       * 月の字だけを比べると、8月 → 9月 → 8月 と戻したときに古い8月の返事を採ってしまう
       * ので、月を変えるたびに番号も進める。
       */
      fixSeq: 0,
      /*
       * 表示する月の世代。月を変えるたびに進める。行に出す結果（取り消しの結果）は、
       * 操作を始めたときの世代と同じときだけ行へ書く（2026-10-10、監査 BF-4）。
       * 確定の通し番号（fixSeq）とは別にする：あちらは確定を押すたびにも進むので、
       * 取り消しの返事を待つあいだに確定を押すと、同じ月の取り消しの結果まで捨ててしまう。
       */
      monthSeq: 0,
      /* 帯の行・0円の注意から移ってきたプランの番号。保存のあと、帯へ戻るボタンを出す行。 */
      planPriceJumpedPlanId: null,
      /*
       * 移ってきたあとに、その行の保存が通ったか。［帯へ戻る］はこれが真のときだけ出す
       * （移ってくる前の保存の結果が残っていても、保存せずに戻れるようには見せない）。
       */
      planPriceSavedAfterJump: false,
      /* 「プランの料金」の表の、見えている幅（横に動く枠の内側）。保存の結果の文の幅の上限。 */
      pricingVisibleWidth: 0,
      savingPricing: false,
      /* 料金の保存の結果。押した行のすぐ下に出す（pricingResultPlanId がその行）。 */
      pricingMessage: '',
      pricingMessageType: 'success',
      /* 結果を出す行（プランの番号）。null なら表のすぐ下。 */
      pricingResultPlanId: null,
      /* 最低月額の上限（円）。back の上限（料金と同じ1,000万円）とそろえる。 */
      minimumMonthlyMax: MINIMUM_MONTHLY_MAX,
      /*
       * ［人数割設定］で開いたプランの行（0108）。null なら小窓は閉じている。
       * 小窓は開いたときの行を写して覚えるので、一覧を読み直しても送り先は変わらない。
       */
      volumeDiscountPlan: null,
      /*
       * 帯を保存したあと、帯だけを読み直しているあいだ（点検 9）。
       * このあいだは［人数割設定］を押せない。
       */
      volumeDiscountReloading: false,
      fixing: false,
      fixMessage: '',
      /* 人数を数えているあいだ。二重に押させない。 */
      counting: false,
      /* 確定を見送った会社（対象月の人数・プラン・料金が不明）。 */
      heldRows: [],
      /* 確定が落ちた会社。**見送りと分ける。** */
      failedRows: [],
      paymentTarget: null,
      paymentRows: [],
      savingPayment: false,
      /* 入金の記録・取消の結果。［入金を記録する］の並びのすぐ下に出す。 */
      paymentMessage: '',
      paymentMessageType: 'success',
      paymentForm: {
        paidAt: '',
        paidAmount: '',
        // 開く前の仮の値。開くと emptyPaymentForm が入れ直す（口座振替は記録できる方法に無い）。
        paymentMethod: 'INVOICE',
        externalRef: '',
        remarks: ''
      },
      /*
       * **1回の入金登録の目印**（2026-09-22 の設計レビュー F01）。
       *
       * 押した直後に通信が切れると、入ったのかどうか画面からは
       * 分からない。押し直しで同じ目印を送れば、サーバは2件目を
       * 作らずに先の結果を返す。**照合キー（振込の取引番号）は
       * 空でも登録できる**ので、それだけでは押し直しを見分け
       * られない。記録できたら次の入金のために作り直す。
       */
      paymentOperationId: '',
      /*
       * **結果が分からないままの操作を、請求ごとに覚える**
       *（2026-09-23、監査 F01-2）。
       *
       * 画面を閉じて同じ請求を開き直すと目印が変わり、**同じ入金が
       * 2件**入っていた（サーバから見れば別の操作なので止められない）。
       * 覚えておけば、開き直しても同じ操作の再送として送れる。
       * 新しい目印を作るのは、結果が分かったとき（記録できたとき）だけ。
       */
      paymentOperationIds: {},
      /*
       * **いま開いている入金の、前回の送信の終わり方。**
       *   ''          … まだ送っていない／結果が分かった
       *   'pending'   … 送ったが結果が分からない
       *   'cancelled' … その操作は取り消されていた
       */
      paymentOperationState: '',
      /* ---- 請求書メールの送付 ---- */
      /*
       * **保存する内容そのもの。**読んだ値をそのまま持つと、
       * 編集の途中で読み直しが走ったときに入力が消える。
       */
      mailForm: {
        enabled: false,
        sendDay: 5,
        sendHour: 10,
        holdHours: 24,
        subjectPrefix: '',
        bccEmail: ''
      },
      mailSaving: false,
      /* ［設定を保存］の結果。ボタンの横に出す。 */
      mailMessage: '',
      /* ［時刻が来ているぶんを、いま送る］の結果。ボタンのすぐ下に出す。 */
      mailRunResult: { type: 'info', text: '' },
      /* 送信予定の行の［止める］［送信待ちへ戻す］［宛先を反映して再予約］の結果。キーは予約。その行に出す。 */
      mailRowResults: {},
      /*
       * **設定が読めなかった理由。**
       *
       * 0093 を当てていない環境では読めない。そのとき入力欄だけ
       * 出すと、保存を押すたびに断られる。**理由を出して、
       * 入力欄は出さない。**
       */
      mailSettingLoadError: '',
      mailRows: [],
      /* 予約が積まれていない発行済み請求書。**0件でなければ出す。** */
      mailMissingRows: [],
      /*
       * いま操作中の予約。**二重に押させない。**
       *
       * 1つしか持てない形にすると、A を止めている間に B を押すと
       * A のボタンが戻って二重に送れる（同じ形の事故を
       * 入金の見直しで起こしている）。
       */
      mailBusyIds: [],
      mailRunning: false,
      fields: [
        { key: 'select', label: '', _style: 'width:38px' },
        { key: 'accountName', label: '契約企業' },
        { key: 'billedUserCount', label: '請求人数' },
        // 請求額の下に注（見込み額・人数割・最低月額調整）が入るので、最小の幅を持たせる（94px では注が5行に割れた）。
        { key: 'totalAmount', label: '請求額（税込）', _style: 'min-width: 12em' },
        { key: 'paidAmount', label: '入金' },
        { key: 'billingStatus', label: '状態' },
        { key: 'invoiceNo', label: '請求書番号' },
        { key: 'paymentDueAt', label: '支払期限' },
        { key: 'payment', label: '', _style: 'width:150px' }
      ]
    }
  },

  computed: {
    /** 「適用請求月」の見出しの下の説明（AM-1。月は入れない。小窓の欄の下と同じ文）。 */
    appliedMonthNote () {
      return APPLIED_MONTH_NOTE
    },

    /**
     * 入金の入力欄を触れなくするか。送っている間と、
     * <b>前回の結果が分からないまま</b>の間（押し直しは前回の内容で送る）。
     */
    paymentLocked () {
      return this.savingPayment || this.paymentOperationState === 'pending'
    },

    /** その操作はもう終わっていて、同じ目印では記録できない。 */
    paymentOperationFinished () {
      return this.paymentOperationState === 'cancelled'
        || this.paymentOperationState === 'conflict'
    },

    /** yyyyMM。API へ渡す形。 */
    compactMonth () {
      return String(this.yearMonthInput || '').replace('-', '')
    },

    canIssue () {
      return canIssueProviderInvoice(this.accessContext)
    },

    /**
     * 祝日マスタの残りが少ない（または1件も無い）ことを、請求管理の上にも出すか。
     *
     * **出すのは取り込める人だけ**（緊急最高管理者と、権限を持つ運営管理者）。
     * サポート閲覧者に出しても打つ手が無い。判定は祝日マスタの画面と同じ warn。
     */
    holidayStockWarning () {
      return canManageProviderNoticesAndHolidays(this.accessContext)
        && Boolean(this.holidayStock && this.holidayStock.warn)
    },

    /**
     * 祝日マスタの登録が、もう切れているか（最後の日が今日より前）。
     *
     * サーバは残りを「今日から最後の日までの日数」で返すので、過ぎると負になる。
     * 祝日マスタの画面（stockExpired）と同じ判定。
     */
    holidayStockExpired () {
      return Boolean(this.holidayStock && this.holidayStock.lastDate)
        && Number(this.holidayStock.daysRemaining) < 0
    },

    /**
     * **月初の自動確定が動いているか**（2026-09-30）。
     *
     * back が「発行元の設定」の応答（読むときも保存のときも）に入れて返す。
     * **true と明示されたときだけ動いている側。**読み込み前・読めなかった・
     * 項目が無いときは止まっている側（既定も止まっている）。
     */
    autoFixRunning () {
      return Boolean(this.issuer) && this.issuer.autoFixEnabled === true
    },

    /**
     * **料金は全社に効く。**1社ぶんの契約を直すのとは重さが違う。
     * back も PLAN_CHANGE を要求する。
     */
    canEditPricing () {
      return canChangeProviderPlan(this.accessContext)
    },

    /** 料金の結果を出す行が、いまの表にあるか（無ければ表のすぐ下に出す）。 */
    pricingResultOnARow () {
      return this.pricingResultPlanId !== null
        && this.plans.some(plan => plan.id === this.pricingResultPlanId)
    },

    /** 入金の合計。**一部だけ振り込まれることがある。** */
    paidTotal () {
      return this.paymentRows.reduce(
        (sum, row) => sum + Number(row.paidAmount || 0), 0)
    },

    paymentShortage () {
      if (!this.paymentTarget) return 0
      return Number(this.paymentTarget.totalAmount || 0) - this.paidTotal
    },

    /**
     * 記録できる支払方法（運営が選ぶ）。
     *
     * **口座振替とクレジットカードは並べない**（2026-10-08、9/30 の決め3）。つなぐまで、
     * その方法で入金されることは無い。1つだけのあいだは、画面は選ぶ欄を出さない。
     */
    methodOptions () {
      return RECORDABLE_PAYMENT_METHODS.map(value => ({
        value,
        label: PAYMENT_METHOD_LABEL[value]
      }))
    },

    /** 運営の会社（請求書の発行元）の行に出す印の文。 */
    issuerAccountMark () {
      return ISSUER_ACCOUNT_MARK
    },

    /** 入金の欄の説明（記録・取り消しの直後に、その会社だけ見直す）。 */
    paymentReviewNote () {
      return PAYMENT_REVIEW_NOTE
    },

    issuableRows () {
      return this.rows.filter(row => this.isIssuable(row))
    },

    /**
     * ［確定分を一括発行］が、発行できる請求が無いために押せないか（2026-10-09）。
     * 読み込み中と、一覧を読めなかったときは false（無いとは言えない）。
     */
    nothingToIssue () {
      return this.canIssue && this.rowsLoaded && !this.loading &&
        this.issuableRows.length === 0
    },

    nothingToIssueNote () {
      return NOTHING_TO_ISSUE_NOTE
    },

    /** 保存の結果の文の幅の上限（料金の表の見えている幅）。測れていなければ付けない。 */
    pricingResultStyle () {
      return this.pricingVisibleWidth > 0
        ? { maxWidth: this.pricingVisibleWidth + 'px' }
        : null
    },

    /** 行の下に出す理由の文の幅の上限（一覧の見えている幅）。測れていなければ付けない。 */
    rowBelowNoteStyle () {
      return this.tableVisibleWidth > 0
        ? { maxWidth: this.tableVisibleWidth + 'px' }
        : null
    },

    voidBlockedNote () {
      return VOID_BLOCKED_BY_CANCELLED_CONTRACT_NOTE
    },

    historyEmptyNote () {
      return HISTORY_EMPTY_NOTE
    },

    historyFailedNote () {
      return HISTORY_FAILED_NOTE
    },

    allSelected () {
      return this.issuableRows.length > 0 &&
        this.selectedIds.length === this.issuableRows.length
    }
  },

  watch: {
    yearMonthInput () {
      /*
       * **月を変えたら、前の月の結果は消す。**
       * 発行のあとの再読込では消さない（結果を見せるのが目的）。
       */
      this.issueResults = []
      this.fixMessage = ''
      this.fixError = ''
      /*
       * **前の月へ出した確定の要求は、もう古い**（2026-10-10、監査 BF-1）。番号を進めて、
       * 遅れて届く返事（成功も失敗も）をこの月の画面へ書かせない。押せない状態も解く
       * （古い要求の後始末は、もう画面を触らない）。
       */
      this.fixSeq += 1
      this.fixing = false
      /* 行に出す結果（取り消し）も同じ：前の月で始めた操作の返事を、この月の行へ書かせない。 */
      this.monthSeq += 1
      /* **保留と失敗の理由も消す。**前の月の会社が残って見える。 */
      this.heldRows = []
      this.failedRows = []
      /* 前の月の行・一覧に出した結果も消す（2026-09-26）。 */
      this.planPriceJumpedPlanId = null
      this.planPriceSavedAfterJump = false
      this.accountFixResult = { type: 'info', text: '' }
      this.rowResults = {}
      /*
       * **入金の欄も閉じる。**
       *
       * 開いたままだと、見出しは前の月の請求のままで、その行の
       * 取消はその請求へ飛ぶ。会社を切り替えたときと同じ形の
       * ずれ（2026-09-07、監査R6）。
       *
       * 見直しの警告は消さない。**会社の話であって月の話では
       * ないので、月を変えても未解決のままにする。**
       */
      this.closePayments()
      this.load()
    }
  },

  created () {
    /*
     * **役割は毎回サーバへ確かめる。**画面の中に持っていた頃は、
     * 権限を外したのにボタンが出たままだった。
     * 取れなくても一覧は開ける（発行のボタンだけ出ない）。
     */
    getAccessContext()
      .then(context => {
        this.accessContext = context
        this.loadHolidayStock()
      })
      .catch(() => { this.accessContext = null })
    this.load()
    this.loadPlans()
    this.loadIssuer()
    this.loadReviewNeeded()
    this.loadHistory()
    this.loadMailSetting()
    this.loadMailOutbox()
  },

  mounted () {
    /*
     * 請求の一覧の見えている幅を測る（行の下の理由の文を、その幅の中で折り返すため）。
     * 窓の幅が変わったときと、枠の幅が変わったとき（左のメニューの開け閉め）に測り直す。
     */
    this.measureTableWidth()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.measureTableWidth)
      const frame = this.billingTableFrame()
      const pricingFrame = this.$refs ? this.$refs.pricingScroll : null
      if (typeof window.ResizeObserver === 'function' && (frame || pricingFrame)) {
        this.tableWidthObserver = new window.ResizeObserver(() => this.measureTableWidth())
        if (frame) this.tableWidthObserver.observe(frame)
        if (pricingFrame) this.tableWidthObserver.observe(pricingFrame)
      }
    }
  },

  beforeDestroy () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.measureTableWidth)
    }
    if (this.tableWidthObserver) {
      this.tableWidthObserver.disconnect()
      this.tableWidthObserver = null
    }
  },

  methods: {
    /** 請求の一覧の、横に動く枠。無ければ null。 */
    billingTableFrame () {
      const root = this.$el
      return root && root.querySelector
        ? root.querySelector('.billing-table .table-responsive')
        : null
    },

    /** 請求の一覧の見えている幅を測って覚える。 */
    measureTableWidth () {
      const frame = this.billingTableFrame()
      this.tableVisibleWidth = frame ? (frame.clientWidth || 0) : 0
      const pricingFrame = this.$refs ? this.$refs.pricingScroll : null
      this.pricingVisibleWidth = pricingFrame ? (pricingFrame.clientWidth || 0) : 0
    },

    /**
     * 祝日マスタの残りを読む（取り込める人だけ）。
     *
     * 開けない人が呼ぶとサーバが断るので、呼ぶ前に同じ判定で絞る。読めなくても
     * 請求の画面は止めない（警告を出さないだけ）。
     */
    loadHolidayStock () {
      if (!canManageProviderNoticesAndHolidays(this.accessContext)) return
      nationalHolidayMaster.status()
        .then(status => { this.holidayStock = status })
        .catch(() => { this.holidayStock = null })
    },

    /**
     * ［再読込］。一覧と一緒に、上の祝日の警告も読み直す。
     *
     * 警告を読むのは画面を開いたときだけだったので、別の画面で祝日を取り込んで
     * 戻ってきて［再読込］を押しても、「登録が切れている」が出たままだった。
     */
    reload () {
      this.load()
      this.loadHolidayStock()
      /*
       * 画面下の「送信予定と、送った履歴」も読み直す（2026-10-10、実機で発見）。
       * 前は画面を開いたときにしか読まず、［再読込］を押しても古いままだった。
       */
      this.loadMailOutbox()
    },

    /* ================================================================
       請求書メールの送付
       ================================================================ */

    /**
     * 送付設定を読む。
     *
     * <b>読めなくても画面全体は開く。</b>ここで止めると、
     * 請求管理の一覧まで見られなくなる。
     */
    loadMailSetting () {
      /*
       * **Promise.resolve で包む。**
       *
       * ここで例外が出たり、返り値が Promise でなかったりすると、
       * created がそこで止まって**画面全体が白くなる。**
       * 請求書メールは、この画面の一部でしかない。
       */
      return Promise.resolve()
        .then(() => providerBilling.mailSetting())
        .then(setting => {
          if (!setting) {
            throw new Error('請求書メールの設定を読めませんでした。')
          }
          return setting
        })
        .then(setting => {
          this.mailSettingLoadError = ''
          this.mailForm = {
            /* **数値の1/0で来る。**真偽値へ直してから入力欄へ渡す。 */
            enabled: Number(setting.enabled) === 1,
            sendDay: Number(setting.sendDay),
            sendHour: Number(setting.sendHour),
            holdHours: Number(setting.holdHours),
            subjectPrefix: setting.subjectPrefix || '',
            bccEmail: setting.bccEmail || ''
          }
        })
        .catch(error => {
          this.mailSettingLoadError = this.messageOf(error,
            '請求書メールの設定を読めませんでした。')
        })
    },

    /** 送信予定と履歴、それに「積まれていない発行済み」。 */
    loadMailOutbox () {
      /*
       * **どちらも、落ちても画面を止めない。**
       * 送信予定が読めないだけで請求管理の一覧まで消えるのは重い。
       */
      /*
       * **いちばん新しく始めた読み直しの応答だけを反映する**（2026-10-10）。
       * 発行のあとと［再読込］でも読み直すようにしたので、前の回の応答があとから
       * 返ると、発行で増えた予定を古い並びで消す。
       */
      const seq = (this.mailOutboxSeq || 0) + 1
      this.mailOutboxSeq = seq
      Promise.resolve()
        .then(() => providerBilling.mailOutbox(null))
        .then(rows => { if (seq === this.mailOutboxSeq) this.mailRows = rows || [] })
        .catch(() => { if (seq === this.mailOutboxSeq) this.mailRows = [] })
      Promise.resolve()
        .then(() => providerBilling.mailMissing(null))
        .then(rows => { if (seq === this.mailOutboxSeq) this.mailMissingRows = rows || [] })
        .catch(() => { if (seq === this.mailOutboxSeq) this.mailMissingRows = [] })
    },

    saveMailSetting () {
      this.mailSaving = true
      this.mailMessage = ''
      providerBilling.saveMailSetting({
        enabled: this.mailForm.enabled,
        sendDay: this.mailForm.sendDay,
        sendHour: this.mailForm.sendHour,
        holdHours: this.mailForm.holdHours,
        subjectPrefix: this.mailForm.subjectPrefix,
        bccEmail: this.mailForm.bccEmail
      })
        .then(() => {
          this.mailMessage = '保存しました。'
          /*
           * **積まれている予約は、この保存では動かない。**
           * 自動送付を「入」にしても、止まっているぶんは
           * 1本ずつ戻してもらう（溜まっていたものが一斉に飛ばない）。
           */
          this.loadMailOutbox()
        })
        .catch(error => {
          this.mailMessage = this.messageOf(error, '保存できませんでした。時間をおいて再度お試しください。')
        })
        .then(() => { this.mailSaving = false })
    },

    /**
     * 送信予定の行に結果を出す（2026-09-26）。前は［設定を保存］の横に出していて、
     * 一覧の下のほうの行で押すと見えなかった。設定が読めないときは入れ物ごと
     * 描かれず、どこにも出なかった。
     */
    setMailRowResult (rowId, type, text) {
      this.mailRowResults = Object.assign({}, this.mailRowResults, { [rowId]: { type, text } })
    },

    /** その行に出す結果。無ければ空（部品は何も出さない）。 */
    mailRowResultOf (rowId) {
      return this.mailRowResults[rowId] || { type: 'success', text: '' }
    },

    holdMail (row) {
      this.mailBusyIds.push(row.id)
      this.setMailRowResult(row.id, 'success', '')
      providerBilling.holdMail(row.id)
        .then(() => { this.setMailRowResult(row.id, 'success', '送信を止めました。') })
        .catch(error => {
          this.setMailRowResult(row.id, 'danger', this.messageOf(error, '止められませんでした。'))
        })
        .then(() => {
          this.mailBusyIds =
            this.mailBusyIds.filter(id => id !== row.id)
          this.loadMailOutbox()
        })
    },

    /**
     * 宛先を直したので、積み直す。
     *
     * **断られた理由をそのまま出す。**「積み直せませんでした」だけ
     * だと、運営は何を直せばよいか分からない。back は「宛先がまだ
     * 使えません。…請求先の設定を直してから」まで返す。
     */
    requeueMail (row) {
      this.mailBusyIds.push(row.id)
      this.setMailRowResult(row.id, 'success', '')
      /*
       * **Promise を返す。**呼ぶ側（検査）が終わりを待てる。
       * 返さないと、await が中身の完了より先に進む。
       */
      return providerBilling.requeueMail(row.id)
        .then(result => {
          this.setMailRowResult(row.id, 'success', result && result.status === 'HELD'
            ? '宛先を反映して再予約しました。自動送付が「切」のため、送信は止めた状態です。'
            : '宛先を反映して再予約しました。')
        })
        .catch(error => {
          this.setMailRowResult(row.id, 'danger',
            this.messageOf(error, '再予約できませんでした。時間をおいて再度お試しください。'))
        })
        .then(() => {
          this.mailBusyIds =
            this.mailBusyIds.filter(id => id !== row.id)
          this.loadMailOutbox()
        })
    },

    releaseMail (row) {
      this.mailBusyIds.push(row.id)
      this.setMailRowResult(row.id, 'success', '')
      providerBilling.releaseMail(row.id)
        .then(() => { this.setMailRowResult(row.id, 'success', '送信待ちへ戻しました。') })
        .catch(error => {
          this.setMailRowResult(row.id, 'danger', this.messageOf(error, '戻せませんでした。'))
        })
        .then(() => {
          this.mailBusyIds =
            this.mailBusyIds.filter(id => id !== row.id)
          this.loadMailOutbox()
        })
    },

    /**
     * 時刻が来ているぶんを、いま送る。
     *
     * **結果不明を「送れなかった」と出さない。**文は mailRunMessage
     * の1か所で組む（言い回しを直すときも、そこだけ触る）。
     */
    runMailNow () {
      this.mailRunning = true
      /*
       * 結果はこのボタンのすぐ下に出す（2026-09-26）。前は［設定を保存］の横に
       * 出していて、設定が読めないときは入れ物ごと描かれず、どこにも出なかった。
       * 送れなかった回（読めなかった・通信の失敗）は赤、それ以外は文のとおり。
       */
      this.mailRunResult = { type: 'info', text: '' }
      /*
       * **Promise を返す。**呼ぶ側（検査）が終わりを待てる。
       * 返さないと、await が中身の完了より先に進む。
       */
      return providerBilling.runMail()
        .then(outcome => {
          let type = 'info'
          let text
          if (outcome && outcome.skipped === 'DISABLED') {
            text = '自動送付が「切」のため、1通も送っていません。'
          } else if (outcome && outcome.skipped === 'NO_SETTING') {
            text = '請求書メールの設定がまだ用意されていません。'
          } else if (outcome && outcome.skipped === 'SETTING_UNREADABLE') {
            /*
             * **読めなかった回は、送りにいって失敗した回と分ける（2026-09-13）。**
             * どちらも1通も送っていないが、次に打つ手が違う。
             */
            type = 'danger'
            text =
              '請求書メールの設定を読み込めなかったため、送信できませんでした。' +
              '時間をおいて再度お試しください。'
          } else if (outcome && outcome.skipped === 'DUE_UNREADABLE') {
            const swept = Number(outcome.sweptToSendUnknown || 0)
            type = 'danger'
            text =
              '送信予定を読み込めなかったため、送信できませんでした。' +
              '時間をおいて再度お試しください。' +
              (swept > 0
                ? 'なお、送信を始めたまま止まっていた ' + swept +
                  ' 通を「結果不明」に変更しました。送信予定の一覧で確認してください。'
                : '')
          } else {
            text = mailRunMessage(outcome)
          }
          this.mailRunResult = { type, text }
        })
        .catch(error => {
          this.mailRunResult = { type: 'danger', text: this.messageOf(error, '送れませんでした。') }
        })
        .then(() => {
          this.mailRunning = false
          this.loadMailOutbox()
        })
    },

    /*
     * 宛先が原因で送られない行（NO_RECIPIENT）は2通りある（2026-10-09）。
     * 宛先が1つも無い行と、宛先は入っているがメールアドレスの形でない行。
     * 後の行は、すぐ隣の列「宛先」に入れた値が出ているので、札まで
     * 「宛先が設定されていません」だと同じ行の中で食い違う。札を分ける。
     */
    mailStatusLabel (row) {
      const status = row && row.status
      if (status === 'NO_RECIPIENT' && row.toEmail) return MAIL_STATUS_LABEL_BAD_RECIPIENT
      return MAIL_STATUS_LABEL[status] || status
    },

    mailStatusColor (status) {
      return MAIL_STATUS_COLOR[status] || 'secondary'
    },

    mailSourceLabel (source) {
      return MAIL_SOURCE_LABEL[source] || '—'
    },

    defaultMonth () {
      const now = new Date()
      const month = String(now.getMonth() + 1)
      return now.getFullYear() + '-' + (month.length < 2 ? '0' + month : month)
    },

    load () {
      if (!this.compactMonth || this.compactMonth.length !== 6) return
      /*
       * **いちばん新しく始めた読み直しの応答だけを反映する**（2026-10-10）。
       * 料金の保存のあとにも読み直すようにしたので、月の切り替えや確定の読み直しと
       * 重なることがある。前の回の応答があとから返ると、新しい月・新しい額を古い並びで
       * 上書きする。
       */
      const seq = (this.loadSeq || 0) + 1
      this.loadSeq = seq
      this.loading = true
      this.errorMessage = ''
      this.selectedIds = []
      providerBilling.periods(this.compactMonth)
        .then(rows => {
          if (seq !== this.loadSeq) return
          this.rows = Array.isArray(rows) ? rows : []
          this.rowsLoaded = true
        })
        .catch(error => {
          if (seq !== this.loadSeq) return
          this.rows = []
          this.rowsLoaded = false
          this.errorMessage = this.messageOf(error)
        })
        .then(() => { if (seq === this.loadSeq) this.loading = false })
    },

    pdfUrl (row) {
      return providerBilling.invoicePdfUrl(row.id)
    },

    /**
     * 見直しだけ、もう一度。
     *
     * **入金は二重にならない。**保存はもう済んでいる。
     */
    retryReview (failure) {
      if (!failure || failure.accountId == null) return
      /*
       * **いま見ている警告の世代を控える。**応答が遅れているうちに
       * 同じ会社で新しい失敗が起きたら、それは消さない（監査S4-B）。
       */
      const since = this.reviewVersion
      const accountId = failure.accountId
      if (this.isRetrying(accountId)) return
      this.retryingAccountIds.push(accountId)
      /*
       * 結果は見直しの警告のすぐ下に出す（2026-09-26）。前は入金の欄の中
       * （開いていなければ出ない）と画面上端にしか出ず、押した所から見えなかった。
       * 警告の行は通ると消えるので、警告の外に出す。
       */
      this.reviewRetryResult = { type: 'success', text: '' }
      providerBilling.runDunning(accountId)
        .then(result => {
          /*
           * **応答を読む。**
           *
           * 前は「呼べたこと」だけで成功にしていた。落ちた会社が
           * 返ってきていても警告が消え、
           * 「契約の制限を見直しました。」へ変わって**再試行の口ごと
           * 消えていた。**この会社の状態は変わっていないのに、
           * 窓口には見直せたように見える（2026-09-06、⑧）。
           */
          const failed = (result && result.failed) || []
          if (failed.length > 0) {
            this.noteReviewFailure({
              accountId: accountId,
              accountName: failure.accountName
            },
              failed[0].reason
              || '契約の制限を見直せませんでした。この会社の状態は'
                + '変わっていません。')
            const 理由 = this.reviewFailures
              .filter(one => one.accountId === accountId)
              .map(one => one.message)[0] || ''
            this.reviewRetryResult = { type: 'danger', text: 理由 }
            this.echoToPaymentPanel(accountId, 理由, 'danger')
          } else {
            /* **通ったこの会社だけ外す。**新しい失敗は残す。 */
            this.clearReviewFailure(accountId, since)
            this.reviewRetryResult = { type: 'success', text: '契約の制限を見直しました。' }
            this.echoToPaymentPanel(accountId, '契約の制限を見直しました。', 'success')
            /* 見直しで制限が動くと記録が増えるので、画面下の記録も読み直す（2026-10-09）。 */
            this.loadHistory()
          }
          this.load()
        })
        .catch(error => {
          /* **通信ごと落ちても、再試行の口は残す。** */
          this.reviewRetryResult = { type: 'danger', text: this.messageOf(error) }
        })
        .then(() => {
          const at = this.retryingAccountIds.indexOf(accountId)
          if (at >= 0) this.retryingAccountIds.splice(at, 1)
        })
    },

    /**
     * 今月の請求人数を、いま数える。
     *
     * **請求の人数は「その月に一度でも在籍した人」で数える。**
     * 毎日 2:50 に自動で数えるが、落ちた日があるとその日だけ
     * 在籍した人が漏れる。押して数え直せるようにしておく。
     *
     * **何度押しても増えない。**同じ月の同じ人は1行だけ。
     */
    countHeadcount () {
      this.counting = true
      this.fixError = ''
      this.fixMessage = ''
      return providerBilling.countHeadcount()
        .then(result => {
          const rows = (result && result.countedRows) || 0
          this.fixMessage = rows > 0
            ? '今月の在籍を数えました（' + rows + ' 人分の記録を更新）。'
            : '数える対象がいませんでした。'
        })
        .catch(error => { this.fixError = this.messageOf(error) })
        .then(() => {
          this.counting = false
          /* 数えた結果が請求人数と見込み額に出る。 */
          this.load()
        })
    },

    runDunning () {
      this.fixing = true
      this.fixError = ''
      this.fixMessage = ''
      providerBilling.runDunning()
        .then(result => {
          const changed = result && result.changed ? result.changed : 0
          /*
           * **落ちた会社を出す。**
           *
           * 前は「変えた件数」だけを出していたので、落ちた会社は
           * 0件として混ざり、運営には見えなかった。
           */
          this.dunningFailedRows = (result && result.failed) || []
          /* **数えるのは会社。**1社に請求が2本あっても1回で決める。 */
          this.fixMessage = changed > 0
            ? changed + ' 社の状態を見直しました。'
            : (this.dunningFailedRows.length
              ? ''
              : '見直す対象はありませんでした（すべて最新です）。')
        })
        .catch(error => { this.fixError = this.messageOf(error) })
        .then(() => {
          this.fixing = false
          this.load()
          this.loadPayments()
          /* 止めた／戻した記録が増えるので、画面下も読み直す。 */
          this.loadHistory()
        })
    },

    /**
     * 見送った1社だけ確定し直す。
     *
     * **その会社の行だけ消す。**ほかの会社の理由は残す。
     */
    /**
     * 確定の要求を1つ始める。対象の月（いま表示している月）と通し番号を控えた札を返す。
     */
    beginFix () {
      this.fixSeq += 1
      this.fixing = true
      return { seq: this.fixSeq, month: this.compactMonth }
    },

    /**
     * その札の要求の返事を、いまの画面へ書いてよいか（2026-10-10、監査 BF-1）。
     * いちばん新しく始めた要求で、対象の月がいま表示している月のときだけ。
     */
    fixIsCurrent (ticket) {
      return !!ticket && ticket.seq === this.fixSeq && ticket.month === this.compactMonth
    },

    /** 見送り・失敗の行に、その結果が対象にした月を結び付ける（［料金を設定］がその月を使う）。 */
    withBilledMonth (rows, month) {
      return (Array.isArray(rows) ? rows : []).map(one => Object.assign({}, one, { billedMonth: month }))
    },

    fixOneAccount (row) {
      const ticket = this.beginFix()
      /*
       * 結果は、見送り・失敗の一覧のすぐ下に出す（2026-09-26）。押した行は
       * 確定できると一覧から消えるので、一覧の外に出す。
       */
      this.accountFixResult = { type: 'info', text: '' }
      providerBilling.fixPeriods(ticket.month, row.accountId)
        .then(result => {
          /* 月を変えたあとに届いた、前の月の返事は捨てる（監査 BF-1）。 */
          if (!this.fixIsCurrent(ticket)) return
          const created = result && result.created ? result.created : 0
          const held = this.withBilledMonth(result && result.held, ticket.month)
          const failed = this.withBilledMonth(result && result.failed, ticket.month)
          /*
           * **その会社の行を、両方の一覧から入れ替える。**
           *
           * 前は保留の一覧から消すだけだった。落ちた会社は held にも
           * failed にも入らないまま消えるので、**請求ができていないのに
           * 一覧から居なくなる**（監査 2026-09-06、16:05）。
           */
          const others = kept => kept.accountId !== row.accountId
          this.heldRows = this.heldRows.filter(others).concat(held)
          this.failedRows = this.failedRows.filter(others).concat(failed)
          // **無料で締めた会社も、そうと分かるように出す**（0101）。
          // 出さないと「押したのに何も起きない」に見える。
          const freeClosed = result && result.freeClosed ? result.freeClosed : 0
          const name = row.accountName || ('会社ID ' + row.accountId)
          // **無料なのは月額だけ。**旧方式の日割り調整が残っていれば、その分の
          // 請求は立つ（利用者の決め b）。「請求書は出ない」とは言わない。
          let text
          if (freeClosed > 0) {
            text = created > 0
              ? name + 'の月額料金を無料とし、過去の調整分の請求を確定しました。'
              : name + 'の月額料金を無料として確定しました。'
          } else if (created > 0) {
            text = name + ' の請求を確定しました。'
          } else {
            /*
             * **確定できなかったときも、押した結果を言う**（2026-09-26）。前は何も
             * 出さず、同じ行が一覧に残るだけで、押せたのかが分からなかった。
             * 理由は一覧の行に出ている。
             */
            /*
             * 2026-10-10：前は「…一覧の確認事項をご確認ください。」。「確認事項」という
             * 字は画面に無い。理由が出ているのは、すぐ上の黄色の帯のその会社の行
             * （Gemini K1）。
             */
            text = name + 'の請求を確定できませんでした。理由は上の帯にあるこの会社の行をご確認ください。'
            /*
             * **確定できなかった知らせは、断りの色で出す**（2026-10-10、実機で発見）。
             * 前は成功（「…の請求を確定しました。」）と同じ水色で、できたのかできなかった
             * のかが色で分からなかった。
             */
            this.accountFixResult = { type: 'danger', text }
            return
          }
          this.accountFixResult = { type: 'info', text }
        })
        .catch(error => {
          /* 古い要求の失敗の知らせも、新しい月の画面には出さない。 */
          if (!this.fixIsCurrent(ticket)) return
          this.accountFixResult = { type: 'danger', text: this.messageOf(error) }
        })
        .then(() => {
          /* 古い要求の後始末は、画面を触らない（押せる・押せないも、一覧の読み直しも）。 */
          if (!this.fixIsCurrent(ticket)) return
          this.fixing = false
          this.load()
        })
    },

    fixPeriods () {
      const ticket = this.beginFix()
      this.fixError = ''
      this.fixMessage = ''
      this.heldRows = []
      this.failedRows = []
      providerBilling.fixPeriods(ticket.month)
        .then(result => {
          /* 月を変えたあとに届いた、前の月の返事は捨てる（監査 BF-1）。 */
          if (!this.fixIsCurrent(ticket)) return
          const created = result && result.created ? result.created : 0
          /*
           * **見送った会社を出す。**
           * 0件のときは何も出さない（ふだんは空になる）。
           * 行には、この結果が対象にした月を結び付ける。
           */
          this.heldRows = this.withBilledMonth(result && result.held, ticket.month)
          this.failedRows = this.withBilledMonth(result && result.failed, ticket.month)
          // **無料で締めた会社の数も出す**（0101）。請求の件数とは別に数える。
          const freeClosed = result && result.freeClosed ? result.freeClosed : 0
          const freeNote = freeClosed > 0
            ? '月額料金を無料として確定した会社：' + freeClosed
              + '社（過去の調整分がある場合は、その分の請求が発生します）。'
            : ''
          if (created > 0) {
            this.fixMessage = created + ' 件の請求を確定しました。' + freeNote
          } else if (freeClosed > 0) {
            this.fixMessage = freeNote
          } else if (this.heldRows.length || this.failedRows.length) {
            /*
             * **「すでに確定済み」と言わない。**
             * 下に「確定していません」と並ぶので、読んだ人が
             * どちらを信じればいいか分からなくなる。
             */
            this.fixMessage = ''
          } else {
            this.fixMessage =
              '新しく確定した請求はありません（すでに確定済みです）。'
          }
        })
        .catch(error => {
          /* 古い要求の失敗の知らせも、新しい月の画面には出さない。 */
          if (!this.fixIsCurrent(ticket)) return
          this.fixError = this.messageOf(error)
        })
        .then(() => {
          /* 古い要求の後始末は、画面を触らない。 */
          if (!this.fixIsCurrent(ticket)) return
          this.fixing = false
          this.load()
        })
    },

    /** 取り消せる状態か。**払い終わったものは先に入金を取り消す。** */
    /**
     * 取り消せる請求か。
     *
     * **解約した会社は取り消せない。**月額も精算も解約を外して
     * いるので、取り消すとその月はもう作れず、請求が静かに消える。
     * サーバでも断るが、**押せてしまう時点で事故のもと**なので
     * ボタンを出さない（2026-09-07）。
     */
    canVoid (row) {
      if (row.contractStatus === 'CANCELLED') return false
      return isVoidableStatus(row)
    },

    /**
     * ［取り消す］が出ない理由が「契約が解約済み」だけの行か（2026-10-09）。
     *
     * 状態は取り消せるのに、解約済みなのでボタンを出していない行。
     * 取り消し済み・入金済みの行は、解約済みでなくてもボタンが出ないので入れない。
     */
    voidBlockedByCancelledContract (row) {
      return !!row && row.contractStatus === 'CANCELLED' &&
        isVoidableStatus(row)
    },

    /**
     * 間違えて出した請求を取り消す。
     *
     * **理由を必ず聞く。**空のまま取り消せると、あとから
     * 「誰が何を間違えたのか」を追えない。
     */
    voidBilling (row) {
      const reason = window.prompt(
        '取り消す理由を入力してください（請求書の控えは残ります）。'
      )
      if (reason === null) return
      /*
       * 結果は、押した行に出す（2026-09-26）。前は画面上端に出していて、
       * 表の下のほうの行で押すと見えなかった。
       * $set を使わず作り直すのは、素の持ち物で動かす試験でも同じに動かすため。
       *
       * **どの月の・どの請求の結果かを一緒に覚え、行へ出す直前に照合する**
       * （2026-10-10、監査 BF-4）。前は会社の番号だけで覚えて出していたので、
       *   ・8月で［取り消す］→ 返事を待つあいだに9月へ → 8月の結果が9月の同じ会社の行に出た
       *   ・取り消して確定し直すと、元の請求の結果が、新しい請求の行にも出た（発行後も残った）
       * 覚えるのは会社ごとに1つのまま（その会社の、いちばん新しい取り消しの結果）。
       */
      const ticket = {
        seq: this.monthSeq,
        month: this.compactMonth,
        accountId: row.accountId,
        billingId: row.id
      }
      /* 取り消しを始めたときの表示（月の世代と月）のままか。変わっていたら、行へは書かない。 */
      const 同じ表示 = () => ticket.seq === this.monthSeq && ticket.month === this.compactMonth
      const 行に出す = (type, text) => {
        if (!同じ表示()) return
        this.rowResults = Object.assign({}, this.rowResults, {
          [row.accountId]: { type, text, month: ticket.month, billingId: ticket.billingId }
        })
      }
      if (!String(reason).trim()) {
        行に出す('danger', '取り消す理由を入力してください。')
        return
      }
      行に出す('success', '')
      const since = this.reviewVersion
      providerBilling.voidBilling(row.id, reason)
        .then(result => {
          /*
           * **取り消しと見直しは別の結果。**
           *
           * 最後の未払いを取り消すと、その会社は督促の対象から
           * 消える。サーバはその場で制限を見直すが、**落ちたときに
           * 帯へ出さないと、払う先が無いのに止まったままの会社が
           * 誰にも見えない**（2026-09-07、監査2）。
           *
           * 取り消せなかった（NOT_VOIDABLE）ときは、断りとして赤で出す。
           */
          行に出す(
            result && result.status === 'NOT_VOIDABLE' ? 'danger' : 'success',
            (result && result.message) || '請求を取り消しました。')
          /*
           * **会社ごとの警告は、月を変えたあとに届いた返事でも出す**（監査 BF-4）。
           * 取り消しのあとの「その会社の利用制限の見直しの失敗」は、月ではなく会社の
           * 未解決の事。行への結果（上）とは分けて、今までどおり扱う。
           */
          if (result && result.reviewError) {
            this.noteReviewFailure(row, result.reviewError)
          } else if (this.reviewFinished(result)) {
            /*
             * **取り消せなかったときは消さない。**
             * 入金が残っていると NOT_VOIDABLE が返り、見直しは
             * 走っていない（監査S4-A）。
             */
            this.clearReviewFailure(row.accountId, since)
          }
          /* 一覧の読み直しは、取り消しを始めた月をまだ表示しているときだけ（別の月の一覧を読み直さない）。 */
          if (同じ表示()) this.load()
          /*
           * 取り消せたときは、制限の見直しで記録が増えうるので、画面下の記録も
           * 読み直す（2026-10-09）。取り消せなかったときは何も動いていない。
           */
          if (!(result && result.status === 'NOT_VOIDABLE')) {
            this.loadHistory()
          }
        })
        .catch(error => { 行に出す('danger', this.messageOf(error)) })
    },

    /**
     * 表の行に出す結果。無ければ空（部品は何も出さない）。
     *
     * **その行の請求の結果だけを出す**（2026-10-10、監査 BF-4）。覚えてある結果が、
     * いま表示している月の・その行の請求のものでなければ出さない。同じ会社でも、
     * 取り消して確定し直したあとの新しい請求の行には、元の請求の結果を付けない。
     */
    rowResultOf (item) {
      const empty = { type: 'success', text: '' }
      const found = item ? this.rowResults[item.accountId] : null
      if (!found || !found.text) return empty
      if (found.month !== undefined && found.month !== this.compactMonth) return empty
      if (found.billingId !== undefined && found.billingId !== null &&
        String(found.billingId) !== String(item.id)) return empty
      return { type: found.type, text: found.text }
    },

    /**
     * 1回の入金登録の目印を作る。
     *
     * <b>推測できなくてよいが、ぶつかってはいけない。</b>
     * 古いブラウザや安全でない接続では crypto が無いことがあるので、
     * そのときは時刻と乱数で作る（ぶつかっても会社が違えば別物、
     * 同じ会社で同時に押すのは同じ人なので実害の範囲が狭い）。
     */
    /**
     * その請求の、結果が分からないままの操作。
     *
     * <b>目印だけでなく、送った内容と終わり方も一緒に覚える。</b>
     * 目印だけ戻しても、フォームが初期値に戻っていれば
     * 「同じ操作なのに中身が違う」と断られる（監査 F01-2）。
     * 画面を読み直しても戻れるよう、この端末の一時記憶にも置く
     * （読めない環境では、その場限りの記憶だけで動く）。
     */
    pendingOperationFor (billingPeriodId) {
      const 鍵 = String(billingPeriodId)
      if (this.paymentOperationIds[鍵]) {
        return this.paymentOperationIds[鍵]
      }
      const 覚え = this.readStoredOperation(鍵)
      if (覚え && 覚え.id) {
        this.$set(this.paymentOperationIds, 鍵, 覚え)
        return 覚え
      }
      return null
    },

    /** 新しい操作を始める（目印だけ先に決める）。 */
    startOperation (billingPeriodId) {
      const 鍵 = String(billingPeriodId)
      const 操作 = { id: this.newOperationId(), state: '', form: null }
      this.$set(this.paymentOperationIds, 鍵, 操作)
      this.writeStoredOperation(鍵, 操作)
      return 操作
    },

    /**
     * 送った内容を覚える。
     *
     * <b>送る前に覚える。</b>応答が返らない場合こそ必要なので、
     * 返ってから覚えたのでは間に合わない。
     */
    rememberSentPayment (billingPeriodId, operationId, form) {
      const 鍵 = String(billingPeriodId)
      const 操作 = {
        id: operationId,
        state: 'pending',
        form: Object.assign({}, form)
      }
      this.$set(this.paymentOperationIds, 鍵, 操作)
      this.writeStoredOperation(鍵, 操作)
      this.paymentOperationState = 'pending'
    },

    /**
     * その操作は取り消されていた、と覚える。
     *
     * <b>ここで勝手に新しい目印を作らない。</b>作って送り直すと、
     * 人が取り消したものを画面が打ち消すことになる。
     */
    markOperationCancelled (billingPeriodId) {
      this.markOperationFinished(billingPeriodId, 'cancelled')
    },

    /**
     * その操作は終わっている（取り消された／別の内容で入っている）と覚える。
     * <b>新しい目印は作らない。</b>始め直すのは押した人。
     */
    markOperationFinished (billingPeriodId, state) {
      const 鍵 = String(billingPeriodId)
      const 操作 = this.paymentOperationIds[鍵]
      if (!操作) return
      const 次 = Object.assign({}, 操作, { state })
      this.$set(this.paymentOperationIds, 鍵, 次)
      this.writeStoredOperation(鍵, 次)
      if (this.paymentTarget && String(this.paymentTarget.id) === 鍵) {
        this.paymentOperationState = state
      }
    },

    /**
     * <b>［新しい入金を始める］。</b>終わった操作を捨てて、
     * まっさらな入力に戻す。押した人の意思で始まる。
     */
    beginNewPayment () {
      if (!this.paymentTarget) return
      /*
       * **結果が分からないあいだは始め直さない**（2026-09-23、監査 P1）。
       * 前の送信が保存されていれば、新しい目印で送った分が2件目になる。
       * 画面にボタンを出していないだけでなく、ここでも断る。
       */
      if (this.paymentOperationState === 'pending') return
      const target = this.paymentTarget
      this.forgetOperation(target.id)
      this.paymentForm = this.emptyPaymentForm(target)
      this.paymentOperationId = this.startOperation(target.id).id
      this.paymentOperationState = ''
      /* 前の入金の断り（入金の欄の中に出している）を残さない。 */
      this.paymentMessage = ''
    },

    /**
     * 入金の入力の初期値。
     *
     * **金額は不足額**（2026-09-30 の洗い出し、請求 #3）。前は請求額の全額で、一部入金の
     * あとに開き直してそのまま記録すると過入金になった（サーバは請求額を超える入金を断らない）。
     * 不足が無ければ空欄。**方法は確定時に固めた支払方法**（請求の抜け2）。
     *
     * **確定時の方法が無い・知らない値・いま記録できない方法（口座振替・カード）なら、請求書払い**
     * （2026-10-08、9/30 の決め3）。前はここが口座振替だった。口座から引き落とす仕組みは
     * 無く、実際の入金は銀行振込なので、そのまま記録すると振込の入金が口座振替として残った。
     */
    emptyPaymentForm (row) {
      const shortage = Number(row.totalAmount || 0) - Number(row.paidAmount || 0)
      // 選択肢（methodOptions）と同じ並び。computed に頼らない（関数だけを呼ぶ試験もある）。
      const fixedMethod = RECORDABLE_PAYMENT_METHODS.indexOf(row.fixedPaymentMethod) >= 0
        ? row.fixedPaymentMethod : 'INVOICE'
      return {
        paidAt: this.today(),
        paidAmount: shortage > 0 ? String(shortage) : '',
        paymentMethod: fixedMethod,
        externalRef: '',
        remarks: ''
      }
    },

    /** 結果が分かったので、その請求の覚えを捨てる。 */
    forgetOperation (billingPeriodId) {
      const 鍵 = String(billingPeriodId)
      this.$delete(this.paymentOperationIds, 鍵)
      try {
        window.sessionStorage.removeItem(this.operationStoreKey(鍵))
      } catch (ignored) {
        /* 読み書きできない環境では、その場限りの記憶だけで動く */
      }
    },

    operationStoreKey (鍵) {
      return 'preika.payment.operation.' + 鍵
    },

    readStoredOperation (鍵) {
      try {
        const 文字 = window.sessionStorage.getItem(this.operationStoreKey(鍵))
        return 文字 ? JSON.parse(文字) : null
      } catch (ignored) {
        /* 読めない・古い形で入っている。その場限りの記憶で動く。 */
        return null
      }
    },

    writeStoredOperation (鍵, 操作) {
      try {
        window.sessionStorage.setItem(
          this.operationStoreKey(鍵), JSON.stringify(操作))
      } catch (ignored) {
        /* 読み書きできない環境では、その場限りの記憶だけで動く */
      }
    },

    newOperationId () {
      const api = window.crypto || window.msCrypto
      if (api && typeof api.randomUUID === 'function') {
        return api.randomUUID()
      }
      if (api && typeof api.getRandomValues === 'function') {
        const bytes = new Uint8Array(16)
        api.getRandomValues(bytes)
        return Array.from(bytes)
          .map(one => one.toString(16).padStart(2, '0'))
          .join('')
      }
      return String(Date.now()) + '-' +
        String(Math.floor(Math.random() * 1e12))
    },

    openPayments (row) {
      this.paymentTarget = row
      /* 入金の節は表の下に生える。押した行から見えないので、そこへ動かす。 */
      revealSection(this, 'paymentSection')
      /* **前の会社の明細を残さない。**closePayments と同じ形。 */
      this.paymentRows = []
      this.paymentMessage = ''
      /*
       * **開き直したら、結果が分からない操作へそのまま戻る。**
       * 目印だけでなく<b>送った内容も戻す。</b>戻さないと、同じ目印で
       * 違う金額を送ることになり、再送も新規もできなくなる。
       */
      const 未確認 = this.pendingOperationFor(row.id)
      this.paymentForm = 未確認 && 未確認.form
        ? Object.assign({}, 未確認.form)
        : this.emptyPaymentForm(row)
      this.paymentOperationId =
        未確認 ? 未確認.id : this.startOperation(row.id).id
      this.paymentOperationState = 未確認 ? (未確認.state || '') : ''
      this.loadPayments()
    },

    closePayments () {
      this.paymentTarget = null
      this.paymentRows = []
      this.paymentMessage = ''
      this.loadingPayments = false
      /* **開いていた読み込みの応答を捨てる。** */
      this.paymentSeq += 1
    },

    /**
     * 入金の明細を読み直す。
     *
     * **応答が返ったときに、いまの選択を読み直さない。**
     * 要求した回の番号と食い違えば捨てる。前は到着順で
     * 上書きしていたので、B社の見出しの下へ A社の明細が
     * 出ることがあった（2026-09-07、監査R6）。
     */
    loadPayments () {
      if (!this.paymentTarget) return
      const seq = ++this.paymentSeq
      this.loadingPayments = true
      providerBilling.payments(this.paymentTarget.id)
        .then(rows => {
          if (seq !== this.paymentSeq) return
          this.paymentRows = Array.isArray(rows) ? rows : []
        })
        .catch(error => {
          if (seq !== this.paymentSeq) return
          this.errorMessage = this.messageOf(error)
        })
        .then(() => {
          if (seq === this.paymentSeq) this.loadingPayments = false
        })
    },

    recordPayment () {
      if (!this.paymentTarget) return
      /*
       * **相手を先に控える。**応答が返るまでに別の会社へ
       * 切り替えられると、`.then` の中の paymentTarget は
       * もう別の会社を指している（2026-09-07、監査R7）。
       */
      const target = this.paymentTarget
      const since = this.reviewVersion
      this.savingPayment = true
      this.paymentMessage = ''
      /*
       * **押し直しでも同じ目印を送る。**作り直すのは記録できたあとだけ。
       * ここで毎回作ると、通信が切れて押し直したときに2件入る。
       */
      if (!this.paymentOperationId) {
        this.paymentOperationId = this.startOperation(target.id).id
      }
      const operationId = this.paymentOperationId
      /*
       * **結果が分からない操作の押し直しは、最初に送った内容で送る**
       *（2026-09-23、監査 F01-2 の残件）。
       *
       * 前は押すたびにいまの入力で覚え直していた。通信が切れたあと
       * 金額を直して押すと、サーバは「同じ操作なのに中身が違う」と
       * 断る。そのうえ覚えは直した金額に置き換わっているので、
       * **最初に送った内容で送り直す手段が消えていた。**
       * 入力欄も最初の内容へ戻して、何を送ったのかを画面で見せる。
       */
      const 未確認 = this.pendingOperationFor(target.id)
      const 送り直し = !!(未確認 && 未確認.id === operationId
        && 未確認.state === 'pending' && 未確認.form)
      if (送り直し) {
        this.paymentForm = Object.assign({}, 未確認.form)
      } else {
        /* **送る前に、送る内容を覚える。**応答が返らない場合のため。 */
        this.rememberSentPayment(target.id, operationId, this.paymentForm)
      }
      const 送る = Object.assign({}, this.paymentForm)
      this.paymentOperationState = 'pending'
      providerBilling.recordPayment({
        billingPeriodId: target.id,
        accountId: target.accountId,
        paymentMethod: 送る.paymentMethod,
        paidAmount: Number(送る.paidAmount || 0),
        paidAt: 送る.paidAt,
        externalRef: 送る.externalRef,
        remarks: 送る.remarks,
        operationId
      })
        .then(result => {
          this.afterPaymentMoved(
            result,
            '入金を記録しました。契約の制限も見直しました。',
            target, since)
          this.paymentForm.externalRef = ''
          /*
           * **金額と備考も空にする。**残しておくと、記録できたあとに
           * もう一度押しただけで、同じ金額が別の入金として入る
           * （照合キーが空なので一意の守りも効かない）。空なら
           * サーバが「入金額を入力してください」で断る。
           */
          this.paymentForm.paidAmount = ''
          this.paymentForm.remarks = ''
          /* **次の入金は別の操作。**同じ目印を使い回さない。 */
          this.forgetOperation(target.id)
          this.paymentOperationId = this.startOperation(target.id).id
          this.paymentOperationState = ''
        })
        .catch(error => {
          /*
           * 断られた理由は、入金の欄の中（［入金を記録する］のすぐ下）に出す
           * （2026-09-26）。前は画面上端で、入金の欄から見えなかった。
           * 別の請求へ切り替えていたら、その欄には書かない。
           */
          if (this.stillOnPaymentTarget(target)) {
            this.paymentMessage = this.messageOf(error)
            this.paymentMessageType = 'danger'
          }
          /*
           * **取り消されていたと分かったときだけ、その操作を終わらせる。**
           * 通信が切れただけのときに捨てると、二重登録へ戻る。
           * ここで新しい目印を作って送り直すのも駄目で、
           * 人が取り消したものを画面が打ち消すことになる。
           */
          if (this.saysTheOperationWasCancelled(error)) {
            this.markOperationCancelled(target.id)
          } else if (this.saysADifferentPaymentIsRecorded(error)) {
            this.markOperationFinished(target.id, 'conflict')
          } else if (this.wasRefusedBeforeSaving(error)) {
            /*
             * **何も入っていないと分かったときだけ、送った内容の覚えを捨てる。**
             * 金額の誤りなどで断られたなら、直して送るのが正しい。
             * 目印はそのまま使う（サーバには、この目印の行が無い）。
             * 通信が切れた・サーバが落ちたときは、入ったかどうか
             * 分からないので捨てない。
             */
            this.releaseSentPayment(target.id, operationId)
          }
        })
        .then(() => { this.savingPayment = false })
    },

    /**
     * <b>サーバが、何も記録せずに断ったか。</b>
     *
     * <p>応答が返っていて、入力の誤り（400）か、請求そのものの状態
     * （409）で断られたとき。<b>ただし次の2つは「前の送信が入っていた」
     * 印なので含めない。</b></p>
     * <ul>
     *   <li>同じ操作で、違う内容の入金がすでに記録されています</li>
     *   <li>この入金は取り消されています</li>
     * </ul>
     * <p>通信が切れた・500 番台のときは、入ったかどうか分からない。</p>
     */
    wasRefusedBeforeSaving (error) {
      const status = error && error.response ? error.response.status : 0
      if (status !== 400 && status !== 409) return false
      if (this.saysADifferentPaymentIsRecorded(error)) return false
      if (this.saysTheOperationWasCancelled(error)) return false
      return true
    },

    /**
     * 送った内容の覚えだけ捨てて、同じ目印で送り直せるようにする。
     * 覚えている目印が別のものに変わっていたら触らない。
     */
    releaseSentPayment (billingPeriodId, operationId) {
      const 鍵 = String(billingPeriodId)
      const 操作 = this.paymentOperationIds[鍵]
      if (!操作 || 操作.id !== operationId) return
      const 次 = { id: operationId, state: '', form: null }
      this.$set(this.paymentOperationIds, 鍵, 次)
      this.writeStoredOperation(鍵, 次)
      if (this.paymentTarget && String(this.paymentTarget.id) === 鍵) {
        this.paymentOperationState = ''
      }
    },

    /**
     * <b>「その入金は取り消されています」と返ってきたか。</b>
     *
     * サーバ（BillingPaymentService）が、取り消し済みの操作IDの
     * 再送へ返す文言。**片方だけ変えると黙って効かなくなる**ので、
     * back 側にも同じ文言を見る検査がある。
     */
    saysTheOperationWasCancelled (error) {
      /*
       * **「この請求は取り消されています」と取り違えない。**
       * 前は「取り消されています」だけを見ていたので、請求そのものが
       * 取り消されていて何も入っていないときにも、入金が取り消された
       * 案内と［新しい入金を始める］を出していた。
       */
      return String(this.messageOf(error, ''))
        .indexOf('この入金は取り消されています') >= 0
    },

    /**
     * <b>同じ操作で、別の内容がすでに記録されていたか。</b>
     *
     * 結果は分かっている（記録されている）。送り直しても同じ答えしか
     * 返らないので、明細を確かめてもらい、別の入金として記録するなら
     * 押した人の意思で始め直す。<b>画面が勝手に新しい目印で送らない。</b>
     */
    saysADifferentPaymentIsRecorded (error) {
      return String(this.messageOf(error, ''))
        .indexOf('違う内容の入金がすでに記録されています') >= 0
    },

    cancelPayment (row) {
      /* **相手を先に控える。**recordPayment と同じ理由。 */
      const target = this.paymentTarget
      const since = this.reviewVersion
      this.savingPayment = true
      this.paymentMessage = ''
      providerBilling.cancelPayment(row.id)
        .then(result => {
          this.afterPaymentMoved(
            result,
            '入金の記録を取り消しました。契約の制限も見直しました。',
            target, since)
        })
        .catch(error => {
          if (this.stillOnPaymentTarget(target)) {
            this.paymentMessage = this.messageOf(error)
            this.paymentMessageType = 'danger'
          }
        })
        .then(() => { this.savingPayment = false })
    },

    /**
     * 入金を動かしたあとの後始末。**登録と取り消しで同じ形。**
     *
     * サーバは「入金を動かせたか」と「制限を見直せたか」を
     * 分けて返す。**取り消し側はこれを読まずに捨てていたので、
     * 取り消したのに制限が残ったままでも運営には見えなかった**
     * （2026-09-07、監査9）。
     *
     * 見直しだけ落ちたときは、その会社を控えて
     * 「見直しだけやり直す」を出す。**同じ操作は繰り返さない。**
     */
    /**
     * 入金を動かしたあとの後始末。**登録と取り消しで同じ形。**
     *
     * @param since 要求を出した時点の警告の世代。
     *   **これより新しい失敗は消さない。**
     */
    afterPaymentMoved (result, done, target, since) {
      const failed = !!(result && result.reviewError)
      if (failed) {
        this.noteReviewFailure(target, result.reviewError)
      } else if (target && this.reviewFinished(result)) {
        this.clearReviewFailure(target.accountId, since)
      }
      /*
       * **いまも同じ請求を見ているときだけ、文を書いて読み直す。**
       * 切り替えたあとに書くと、別の会社の入金の欄に「記録しました」が出る
       * （2026-09-26 監査。見直しの失敗は上の noteReviewFailure で一覧側に残る）。
       * 切り替えたあとに読み直すと、別の会社の明細を出す。
       */
      if (this.stillOnPaymentTarget(target)) {
        this.paymentMessage = failed ? result.reviewError : done
        /* 見直しに失敗したときは、入金は動いていても赤で出す（手を離させない）。 */
        this.paymentMessageType = failed ? 'danger' : 'success'
        this.loadPayments()
      }
      this.load()
      /*
       * **画面下の「契約の状態が変わった記録」も読み直す**（2026-10-09）。
       * 入金を記録・取り消すと、サーバはその会社の制限をその場で見直し、
       * 動いたときは記録を書く。前は読み直さなかったので、画面の説明
       * （記録・取り消した直後にも見直す）どおりに動いても、記録は古いままだった。
       */
      this.loadHistory()
    },

    /**
     * 見直しのやり直しの結果を、入金の欄にも写す。**その会社の欄を開いているときだけ。**
     * 前は開いている欄がどの会社かを見ずに書いていたので、B社の欄を開いたまま
     * A社の警告から［やり直す］を押すと、B社の欄に「見直しました」が出た（2026-09-26 監査）。
     */
    echoToPaymentPanel (accountId, text, type) {
      if (!this.paymentTarget || this.paymentTarget.accountId !== accountId) return
      this.paymentMessage = text
      this.paymentMessageType = type
    },

    /** 入金の欄が、操作を始めたときの請求をまだ開いているか。 */
    stillOnPaymentTarget (target) {
      return !!(this.paymentTarget && target && this.paymentTarget.id === target.id)
    },

    /**
     * <b>見直しが本当に通ったか。</b>
     *
     * <p>「失敗が返ってこなかった」を成功の証拠にしない。
     * 請求の取り消しは、入金が残っていると例外ではなく
     * <code>{status:"NOT_VOIDABLE"}</code> を返し、
     * <b>見直しを呼ばずに終わる。</b>そのとき reviewError は
     * 無いので、前は警告を消していた（2026-09-07、監査S4-A）。</p>
     *
     * <p>見直しを飛ばした（reviewSkipped）ときも消さない。
     * 消してよいのは <b>reviewed が true のときだけ</b>。</p>
     */
    reviewFinished (result) {
      return !!(result && result.reviewed === true)
    },

    /** その会社の見直しを、いまやり直しているか。 */
    isRetrying (accountId) {
      return this.retryingAccountIds.indexOf(accountId) >= 0
    },

    /** 見直しに失敗した会社を、1社ぶんだけ足す（同じ会社は差し替え）。 */
    noteReviewFailure (target, message) {
      if (!target || target.accountId == null) return
      const entry = {
        accountId: target.accountId,
        accountName: target.accountName,
        message: message,
        version: ++this.reviewVersion
      }
      const at = this.reviewFailures
        .map(one => one.accountId).indexOf(target.accountId)
      if (at >= 0) this.reviewFailures.splice(at, 1, entry)
      else this.reviewFailures.push(entry)
    },

    /**
     * その会社の警告だけ外す。
     *
     * **全部は消さない。**ほかの会社の未解決を巻き添えにすると、
     * 止まったままの会社が誰にも見えなくなる（監査R7）。
     *
     * @param since 要求を出した時点の世代。**そのあとに足された
     *   失敗は残す。**古い成功応答が、新しい未解決を消さないため
     *   （監査S4-B）。null なら世代を見ない。
     */
    clearReviewFailure (accountId, since) {
      this.reviewFailures = this.reviewFailures.filter(one => {
        if (one.accountId !== accountId) return true
        return since != null && one.version > since
      })
    },

    /**
     * 入金の明細の「方法」。**いま選べない方法（口座振替）で記録された入金にも、
     * 呼び名を出す**（選択肢から引くと、英字のまま出る）。
     */
    methodLabel (value) {
      return PAYMENT_METHOD_LABEL[value] || value
    },

    today () {
      const now = new Date()
      const two = n => (String(n).length < 2 ? "0" + n : String(n))
      return now.getFullYear() + "-" + two(now.getMonth() + 1)
        + "-" + two(now.getDate())
    },

    readableMonth (value) {
      const text = String(value || "")
      if (text.length !== 6) return text
      return text.slice(0, 4) + "年" + Number(text.slice(4)) + "月"
    },

    /**
     * 契約の状態が変わった記録。
     *
     * **落ちても画面は出す。**請求の一覧が本題なので、
     * 記録が読めないことで請求まで見えなくなると困る。
     */
    /**
     * **読めなかったことは、0件と分けて覚える**（2026-10-09）。
     *
     * **いちばん新しく始めた読み直しの応答だけを反映する。**入金の記録・入金の
     * 取り消し・請求の取り消し・見直しのあとにも読み直すので、前の応答があとから
     * 返ると、新しい結果を古い並びで上書きする。
     */
    loadHistory () {
      const seq = ++this.historySeq
      providerBilling.contractHistory()
        .then(rows => {
          if (seq !== this.historySeq) return
          if (!Array.isArray(rows)) {
            /* 並びでない応答は、0件ではなく「読めなかった」。 */
            this.history = []
            this.historyFailed = true
            this.historyLoaded = true
            return
          }
          this.history = rows
          this.historyFailed = false
          this.historyLoaded = true
        })
        .catch(() => {
          if (seq !== this.historySeq) return
          this.history = []
          this.historyFailed = true
          this.historyLoaded = true
        })
    },

    contractLabel (value) {
      if (!value) return '（新規）'
      return CONTRACT_LABEL[value] || value
    },

    contractColor (value) {
      return CONTRACT_COLOR[value] || 'secondary'
    },

    /**
     * 発行元の設定を読む。
     *
     * **入力欄にも写す。**読んだだけで欄が空だと、保存したときに
     * 全部消える。
     */
    /** 督促を保留している請求を読む。 */
    loadReviewNeeded () {
      return providerBilling.reviewNeeded()
        .then(rows => {
          this.reviewRows = Array.isArray(rows) ? rows : []
        })
        .catch(error => { this.errorMessage = this.messageOf(error) })
    },

    loadIssuer () {
      return providerBilling.issuer()
        .then(data => {
          this.issuer = data || {}
          this.issuerForm = {
            address: this.issuer.address || '',
            building: this.issuer.building || '',
            telephone: this.issuer.telephone || '',
            registrationNo: this.issuer.registrationNo || '',
            bankName: this.issuer.bankName || '',
            sealImage: null
          }
        })
        .catch(error => { this.errorMessage = this.messageOf(error) })
    },

    /**
     * 角印を選んだ。
     *
     * **base64 にして持つ。**送るのは保存のときだけ。
     */
    pickSeal (event) {
      /*
       * **選ぶたびに番号を進める。**前に選んだ画像の読み込みがまだ終わっていなくても、
       * その結果は下の番号の照合で捨てる（最後に選んだ画像だけが残る）。
       */
      const pick = ++this.sealPick
      const file = event.target.files && event.target.files[0]
      if (!file) {
        this.sealReading = false
        this.issuerForm.sealImage = null
        return
      }
      const reader = new FileReader()
      this.sealReading = true
      reader.onload = () => {
        if (pick !== this.sealPick) return
        /* data:image/png;base64,XXXX の後ろだけ取る。 */
        const text = String(reader.result || '')
        const at = text.indexOf(',')
        this.issuerForm.sealImage = at >= 0 ? text.slice(at + 1) : ''
        this.sealReading = false
      }
      reader.onerror = () => {
        if (pick !== this.sealPick) return
        /* 発行元の節の中（［発行元を保存する］のすぐ下）に出す。角印の欄はその真上。 */
        this.issuerMessage = '角印の画像を読み取れませんでした。'
        this.issuerMessageType = 'danger'
        this.issuerForm.sealImage = null
        this.sealReading = false
      }
      reader.readAsDataURL(file)
    },

    /**
     * 「角印を消す」を切り替えた。
     *
     * **消すのと差し替えるのは両立しない。**両方できる形にすると、
     * 保存で何が起きるかが読めなくなる。消す側を選んだら、選んで
     * あった画像は捨てる。
     */
    onRemoveSealChanged () {
      if (!this.removeSeal) {
        return
      }
      /* 読み込みの途中の画像も捨てる（あとから読み込みが終わって、選んだことにならないように）。 */
      this.sealPick++
      this.sealReading = false
      this.issuerForm.sealImage = null
      if (this.$refs.sealInput) {
        /* 選んだファイル名が残ると、消すのに「選択しました」に見える。 */
        this.$refs.sealInput.value = ''
      }
    },

    saveIssuer () {
      /* 角印の読み込みが終わる前には送らない（選んだ画像が入らないまま保存が通る）。 */
      if (this.sealReading) {
        return
      }
      this.issuerSaving = true
      this.issuerMessage = ''
      /*
       * **空文字は「消す」。**null（そのまま）とは別の意味なので、
       * ここで翻訳する。issuerForm を直接書き換えると、保存に失敗
       * したあとも消す指示が残る。
       */
      const form = Object.assign({}, this.issuerForm)
      if (this.removeSeal) {
        form.sealImage = ''
      }
      providerBilling.saveIssuer(form)
        .then(data => {
          this.issuer = data || {}
          this.issuerForm.sealImage = null
          this.removeSeal = false
          if (this.$refs.sealInput) {
            this.$refs.sealInput.value = ''
          }
          this.issuerMessage = this.issuer.missing
            ? '保存しました。一部の必須項目が未入力です。'
            : '保存しました。請求書を発行できます。'
          this.issuerMessageType = 'success'
        })
        .catch(error => {
          this.issuerMessage = this.messageOf(error)
          this.issuerMessageType = 'danger'
        })
        .then(() => { this.issuerSaving = false })
    },

    loadPlans () {
      providerBilling.plans()
        .then(rows => {
          /*
           * **適用請求月の既定は来月。**入れ直さないと前の行の値が残る。
           * **料金の欄はその月に使われる値にする**（2026-09-30 の見直し。loadPriceOfMonth）。
           */
          this.plans = (Array.isArray(rows) ? rows : []).map(plan => {
            plan.appliedMonth = this.defaultAppliedMonth()
            // 今月の料金の記録・月ごとの料金と組が無いと鍵ごと返らないことがある。入れておかないと描き直しに乗らない。
            this.fillPricingKeys(plan, plan)
            this.loadPriceOfMonth(plan)
            return plan
          })
        })
        .catch(error => { this.errorMessage = this.messageOf(error) })
    },

    /**
     * **行の料金の欄に、選んでいる適用請求月に使われる値を入れる**（2026-09-30 の見直し）。
     *
     * 一覧の月ごとの料金（priceSchedule）から、その月の1日以前でいちばん新しい月の値
     * （確定と同じ選び方）。その月に料金が無ければ空欄（保存すると「基本料金を入力してください。」で止まる）。
     * 最低月額が無い月は空欄（なし）。入れた値と月を覚えて、あとで入れかけかどうかを見る。
     */
    loadPriceOfMonth (plan) {
      const entry = entryOfMonth(plan.priceSchedule, monthStartOf(plan.appliedMonth))
      const text = value => (this.isBlank(value) ? '' : value)
      PRICE_FIELDS.forEach(field => {
        this.$set(plan, field, entry ? text(entry[field]) : '')
      })
      // 読み直した（最初の読み込み・保存のあと）ので、打った印は全部外す。
      this.$set(plan, 'typedPrice', {})
    },

    /** 利用者がその欄に打った（月を変えても、保存するか読み直すまで、その欄は入れ替えない）。 */
    markPriceTyped (plan, field) {
      if (!plan.typedPrice) this.$set(plan, 'typedPrice', {})
      this.$set(plan.typedPrice, field, true)
    },

    /**
     * 行の「適用請求月」を変えた。**打っていない欄だけ、その月に使われる料金へ入れ替える。**
     *
     * - 打った欄は残す（2026-10-08）。前は確認を出して、「はい」で打った値を捨て、「いいえ」で月を
     *   戻していたので、金額を入れてから月を選ぶ順では入力できなかった。
     * - その月に料金の記録が無い（最初の記録より前の月）ときは、出ている値を残す。前は全部の欄が
     *   空になり、同じ料金を前の月から使いたいだけでも全部打ち直しになった。
     * - 月の欄が空・読めない値のあいだは入れ替えない（保存は EMPTY_MONTH_MESSAGE で止まる）。
     *
     * 打っていない欄をその月の値にするのは、2026-09-30 の見直しのとおり（来月分を保存してある行で
     * 1か所だけ直して保存しても、ほかの欄が今月の値で来月分を置き換えないように）。
     */
    changePriceMonth (plan, event) {
      const value = event && event.target ? event.target.value : event
      plan.appliedMonth = value
      const monthStart = monthStartOf(value)
      if (!monthStart) return
      const entry = entryOfMonth(plan.priceSchedule, monthStart)
      if (!entry) return
      const typed = plan.typedPrice || {}
      const text = raw => (this.isBlank(raw) ? '' : raw)
      PRICE_FIELDS.forEach(field => {
        if (typed[field]) return
        this.$set(plan, field, text(entry[field]))
      })
    },

    /** その行（帯の見送りの行・一覧の行）のプランが、いま「プランの料金」の表にあれば返す。 */
    planOfBillingRow (row) {
      if (!row || row.planId === null || row.planId === undefined) return null
      const plans = Array.isArray(this.plans) ? this.plans : []
      return plans.find(plan => Number(plan.id) === Number(row.planId)) || null
    },

    /**
     * その行に、料金の行へ移るボタンを出すか（2026-10-10）。
     *
     * - 字が決まっている
     * - 料金を直せる人（直せない人を、直せない欄へ連れて行かない）
     * - **料金が未設定**が理由の行（帯の行は理由の符号、一覧の行は back が返す状態）。
     *   料金は入っているが計算の結果が0円の行（人数割などで0円）には出さない：
     *   料金の入力漏れではないので、料金の欄へ連れて行っても直らない（監査 BF-2）。
     *   注意の文があるか・合計が0円かでは決めない
     * - その行の結果が対象にした月が、いま表示している月（監査 BF-1）
     * - そのプランが料金の表にある（無いと、移る先が無い）
     */
    canJumpToPlanPrice (row) {
      if (!this.planPriceJumpLabel || !this.canEditPricing) return false
      if (!row) return false
      const unset = row.missing === MISSING_ZERO_AMOUNT ||
        row.zeroAmountState === MISSING_ZERO_AMOUNT
      return unset && this.rowIsForShownMonth(row) && !!this.planOfBillingRow(row)
    },

    /** その行（帯の行・一覧の行）の結果が対象にした月（YYYYMM）。分からなければ空。 */
    billedMonthOfRow (row) {
      return String((row && (row.billedMonth || row.yearMonth)) || '')
    },

    /** その行が、いま表示している月の結果か。月が分からない行は、表示中の月のものとして扱う。 */
    rowIsForShownMonth (row) {
      const month = this.billedMonthOfRow(row)
      return !month || month === this.compactMonth
    },

    /**
     * 0円の行から、「プランの料金」のその会社のプランの行へ移る（2026-10-10）。
     *
     * 1. その行の「適用請求月」を、いま表示している月に合わせる。**手で月を変えたときと
     *    同じ処理（changePriceMonth）を通す**ので、打っていない金額の欄はその月の値になる
     *    （打った欄は残る）。
     * 2. その行が見える位置へ画面を動かす。
     * 3. 基本料の欄に入力の焦点を置く。
     */
    goToPlanPrice (row) {
      const plan = this.planOfBillingRow(row)
      if (!plan) return Promise.resolve(false)
      /*
       * **適用請求月は、その行の結果が対象にした月にする**（2026-10-10、監査 BF-1）。
       * 押した時点の画面の月ではない。対象の月が表示中の月と食い違う行からは進めない
       * （前の月の結果で、今の月の料金を直させない）。
       */
      if (!this.rowIsForShownMonth(row)) return Promise.resolve(false)
      const billed = this.billedMonthOfRow(row)
      const month = billed.length === 6
        ? billed.slice(0, 4) + '-' + billed.slice(4)
        : this.yearMonthInput
      if (month) this.changePriceMonth(plan, month)
      /*
       * **その行に残っている前の保存の結果は消す**（2026-10-10、実機で発見）。先に0円で保存して
       * あった行へ移ると、保存する前から完了の文と［帯へ戻る］が出ていて、保存せずに戻れる
       * ように見えた。［帯へ戻る］は、移ってきたあとに保存が通ったときだけ。
       */
      if (this.pricingResultPlanId === plan.id) this.showPricingResult(plan.id, '', 'success')
      this.planPriceSavedAfterJump = false
      this.planPriceJumpedPlanId = plan.id
      this.measureTableWidth()
      const name = 'planPriceRow-' + plan.id
      return this.$nextTick().then(() => {
        const target = this.$refs ? this.$refs[name] : null
        const element = Array.isArray(target) ? target[0] : target
        const moved = this.revealPlanPriceRow(element)
        const input = element && element.querySelector
          ? element.querySelector('input.price-input:not([disabled])')
          : null
        if (input && typeof input.focus === 'function') {
          /* 画面を動かす途中で焦点が画面を飛ばさないように（動かすのは上の1回だけ）。 */
          try { input.focus({ preventScroll: true }) } catch (error) { input.focus() }
          /*
           * **中身を全部選ぶ**（2026-10-10、実機で発見）。欄には 0 が入っているので、
           * そのまま打つと「0300000」になった。選んでおけば、打った値に置き換わる。
           */
          if (typeof input.select === 'function') input.select()
        }
        return moved
      })
    },

    /**
     * 料金の行が見える位置へ画面を動かす。**表の見出し（適用請求月・基本料・1人あたり）が
     * 見える位置で止める**（2026-10-10、実機で発見：行が画面のいちばん上の端に来て、
     * どの欄が何かを読む見出しが隠れた）。
     *
     * 見出しから行の下端までが画面に収まるなら、見出しを上端（上の固定の帯のぶん下げた位置。
     * 見た目の決まりの scroll-margin-top）に合わせる。収まらない（プランが多く、行が見出しから
     * 遠い）ときは、行を画面のまん中に出す。
     */
    revealPlanPriceRow (row) {
      if (!row || typeof row.scrollIntoView !== 'function') return false
      const head = this.$refs ? this.$refs.planPriceHead : null
      const canMeasure = head && typeof head.getBoundingClientRect === 'function' &&
        typeof row.getBoundingClientRect === 'function' && typeof window !== 'undefined'
      const span = canMeasure
        ? row.getBoundingClientRect().bottom - head.getBoundingClientRect().top
        : Infinity
      const room = canMeasure ? (window.innerHeight || 0) - PLAN_PRICE_REVEAL_MARGIN_PX : 0
      if (canMeasure && typeof head.scrollIntoView === 'function' && span <= room) {
        head.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return true
    },

    /**
     * 保存の結果の下に、帯へ戻るボタンを出すか。帯の行（または0円の注意）から移ってきた
     * プランの行で、保存が通り、帯がまだ出ているときだけ。
     */
    canGoBackToHeldBand (plan) {
      return !!this.backToHeldBandLabel && !!plan &&
        this.planPriceJumpedPlanId === plan.id &&
        this.planPriceSavedAfterJump === true &&
        this.pricingResultPlanId === plan.id &&
        this.pricingMessageType === 'success' && !!this.pricingMessage &&
        this.heldRows.length > 0
    },

    /** 帯（確定されなかった会社の並び）が見える位置へ戻る。 */
    goBackToHeldBand () {
      /* 戻ったら、移ってきた行の目印と［帯へ戻る］は役目を終える（保存の結果の文は残す）。 */
      this.planPriceJumpedPlanId = null
      this.planPriceSavedAfterJump = false
      return revealSection(this, 'heldBand')
    },

    /** 料金の結果を、その行の下に出す（planId が null なら表のすぐ下）。 */
    showPricingResult (planId, text, type) {
      this.pricingResultPlanId = planId === undefined ? null : planId
      this.pricingMessage = text
      this.pricingMessageType = type
    },

    /**
     * ［プランを追加］。契約企業一覧へ移り、追加の幕を開いた状態で始める（幕は1か所のまま）。
     * 合図は道に載せない（載せて外すと、移った先の画面が作り直されて幕が消えた）。
     */
    goAddPlan () {
      askToAddPlan()
      this.$router.push('/platform/access-management')
    },

    isBlank (value) {
      return value === null || value === undefined || String(value).trim() === ''
    },

    /**
     * 送る最低月額。**空欄は null（なし）。無料プランはいつも null。**
     * 範囲の外か、整数でないなら undefined（送らずに止める）。
     * **小数は送らない**（front #784 の監査）。10000.5 が保存できると、請求書の明細の
     * 合計と小計が1円ずれる。10000.00 は整数（10000）として送る。
     */
    minimumToSend (plan) {
      if (plan.freePlan === true) return null
      if (this.isBlank(plan.minimumMonthlyAmount)) return null
      const amount = Number(String(plan.minimumMonthlyAmount).trim())
      if (!Number.isInteger(amount)
        || amount < MINIMUM_MONTHLY_MIN || amount > MINIMUM_MONTHLY_MAX) {
        return undefined
      }
      return amount
    },

    /** 欄を赤くするか。**0円・マイナス・上限超え・小数は、押す前に分かるようにする。** */
    minimumIsWrong (plan) {
      return this.minimumToSend(plan) === undefined
    },

    savePricing (plan) {
      const minimumMonthlyAmount = this.minimumToSend(plan)
      if (minimumMonthlyAmount === undefined) {
        // **送らずに止める。**back と同じ文で断る（範囲は 1円〜1,000万円の整数）。
        // 押した［保存］のそば（料金の表のすぐ下）に出す。上端の帯は表から離れていて見えない。
        this.showPricingResult(plan.id,
          '最低月額は1円〜10,000,000円の範囲内で、整数（小数点なし）で入力してください。', 'danger')
        return
      }
      /*
       * **空欄を0円にしない**（2026-09-30、テスターの S5 No.8）。
       * 前は Number(x || 0) で送っていたので、欄を空にして［保存］すると断られずに
       * 0円で保存され、成功の文まで出た。0円の料金は確定の見送りにもかからず、
       * そのまま0円の請求書が立つ。空欄・範囲外は送らずに止め、back と同じ文で断る。
       */
      const basePrice = this.amountToSend(plan.basePrice, '基本料金')
      const pricePerUser = this.amountToSend(plan.pricePerUser, '1人あたり単価')
      const amountProblem = basePrice.problem || pricePerUser.problem
      if (amountProblem) {
        this.showPricingResult(plan.id, amountProblem, 'danger')
        return
      }
      /*
       * **送るのは選んだ月の1日**（適用請求月、2026-09-30）。月の欄を空にしたら送らずに止める
       * （黙って来月にしない。止める文は AM-14）。
       */
      const appliedOn = monthStartOf(plan.appliedMonth)
      if (!appliedOn) {
        this.showPricingResult(plan.id, EMPTY_MONTH_MESSAGE, 'danger')
        return
      }
      this.savingPricing = true
      this.showPricingResult(plan.id, '', 'success')
      const planId = plan.id
      const planName = plan.planName
      let saved = {}
      providerBilling.savePlanPricing(
        planId,
        basePrice.value,
        pricePerUser.value,
        appliedOn,
        minimumMonthlyAmount
      )
        .then(result => {
          saved = result || {}
          /*
           * **上の一覧（見込みの額・0円の注意）も読み直す**（2026-10-10）。
           * 0円で見送られた月は「料金を保存 → 確定し直す」で直す。前は［再読込］を
           * 押すまで見込みが古いままで、保存で直ったのかが分からなかった。
           * 見送った会社の並びは確定の結果なので、ここでは消さない
           * （その行の［この会社だけ確定する］を次に押すため）。
           */
          this.load()
          return this.reloadSavedPlan(planId)
        })
        .then(() => {
          /*
           * **選んだ月より後の月の料金がもうあるなら、そう言う**（M-F）。その月からは
           * そちらが使われる。後の月は back が保存と同じ取引の中で引いて返す（laterAppliedOn）。
           * 無ければ「この設定は何年何月分の請求から適用されます」（M-E）。
           * その月から後にこのプランの確定済みの請求があれば、それには適用されないことを足す
           * （fixedBillingExists。選んだ月を入れた AM-5。2026-09-30 の見直し）。
           */
          /*
           * **いくらで保存したかを出す**（2026-10-10、Gemini K2）。前は月しか出ず、
           * 月を選び直したときに金額の欄がその月の値（0円）へ入れ替わったまま保存しても
           * 気づけなかった。出すのは back が受け取って保存した値（返らないときは送った値）。
           * **基本料も1人あたりも0円で保存したときは、その月が確定されないことを続けて言う**
           * （K3。無料プランとして設定したプランでは言わない）。保存は今までどおり通る。
           */
          const savedBase = saved.basePrice === undefined || saved.basePrice === null
            ? basePrice.value : saved.basePrice
          const savedPerUser = saved.pricePerUser === undefined || saved.pricePerUser === null
            ? pricePerUser.value : saved.pricePerUser
          this.showPricingResult(planId, priceSavedText(planName, appliedOn, saved.laterAppliedOn,
            saved.fixedBillingExists === true, {
              basePrice: savedBase,
              pricePerUser: savedPerUser,
              freePlan: plan.freePlan === true
            }), 'success')
          /* 帯の行・0円の注意から移ってきた行の保存が通った。ここで初めて［帯へ戻る］を出す。 */
          if (this.planPriceJumpedPlanId === planId) this.planPriceSavedAfterJump = true
        })
        .catch(error => {
          this.showPricingResult(planId, this.messageOf(error), 'danger')
        })
        .then(() => { this.savingPricing = false })
    },

    /**
     * 送る料金。**空欄・マイナス・上限超え・小数は送らずに止める。**
     * 断りの文は back（PlanPricingService.requireAmount）と同じ。上限は1,000万円。
     * @return {{ value: number|null, problem: string }}
     */
    amountToSend (raw, label) {
      if (this.isBlank(raw)) return { value: null, problem: label + 'を入力してください。' }
      const amount = Number(String(raw).trim())
      if (!Number.isFinite(amount)) return { value: null, problem: label + 'を入力してください。' }
      if (amount < 0) return { value: null, problem: label + 'にマイナスは入力できません。' }
      if (amount > PLAN_AMOUNT_MAX) return { value: null, problem: label + 'が大きすぎます。桁を確認してください。' }
      if (!Number.isInteger(amount)) return { value: null, problem: label + 'は整数（小数点なし）で入力してください。' }
      return { value: amount, problem: '' }
    },

    /** 「適用請求月」の既定：来月（<input type="month"> の値 YYYY-MM）。 */
    defaultAppliedMonth () {
      return nextMonthValue(new Date())
    },

    /** M-C：この行の料金を何年何月分の請求から使っているか。記録が無ければ空文字。 */
    priceSince (plan) {
      return priceSinceText(plan && plan.currentAppliedOn)
    },

    /** 今月の1日（YYYY-MM-01）。先の月の料金と組を分けるため。 */
    thisMonthStart () {
      return monthValueOf(new Date()) + '-01'
    },

    /** プラン名の下に並べる、今月分と先の月分の料金（M-D の形）。先の月が無ければ空の並び。 */
    priceLines (plan) {
      return priceScheduleLines(plan && plan.priceSchedule, this.thisMonthStart())
    },

    /**
     * 一覧の行から、今月の料金の記録の日と月ごとの料金・組を写す。
     * **鍵ごと返らないことがある**（MyBatis の map は NULL の列を入れない）ので、$set で必ず入れる。
     */
    fillPricingKeys (target, source) {
      this.$set(target, 'currentAppliedOn', this.isBlank(source.currentAppliedOn) ? null : source.currentAppliedOn)
      this.$set(target, 'priceSchedule', Array.isArray(source.priceSchedule) ? source.priceSchedule : [])
      this.$set(target, 'volumeDiscountSchedule',
        Array.isArray(source.volumeDiscountSchedule) ? source.volumeDiscountSchedule : [])
    },

    /**
     * **保存した行だけを読み直す**（2026-09-30 の洗い出し、請求 #4）。
     *
     * 前は保存のたびに表を丸ごと読み直していたので、ほかの行に打ちかけた料金と
     * 「適用請求月」が黙って消えた（人数割の保存は点検8で同じ事故を直してある）。
     * **選んでいた適用請求月はそのまま**にし、欄はその月に使われる値（＝いま保存した値）に入れ直す
     * （2026-09-30 の見直し。前は月を既定の来月へ戻し、欄は今月の値に戻していた）。
     * 返り値は読み直した行（見つからなければ null）。
     */
    reloadSavedPlan (planId) {
      return providerBilling.plans().then(rows => {
        const fresh = (Array.isArray(rows) ? rows : []).find(row => row.id === planId)
        const current = this.plans.find(row => row.id === planId)
        if (!fresh || !current) return null
        this.fillPricingKeys(current, fresh)
        current.accountCount = fresh.accountCount
        current.userLimitTotal = fresh.userLimitTotal
        this.loadPriceOfMonth(current)
        return fresh
      })
    },

    isIssuable (row) {
      /*
       * **運営の会社（請求書の発行元）宛ての請求は、発行に選ばせない**
       * （2026-10-08、9/30 の決め1）。back も断る（番号を採らない）。前に立った
       * 請求が残っている月に、印を付けられて「発行しませんでした」と返るのを避ける。
       */
      if (row.issuerAccount) return false
      /*
       * **決済の準備の済んでいない支払方法で確定した請求も、選ばせない**
       * （2026-10-08、監査 B764-2）。back が行に理由を付けて返す。発行しても
       * back が番号を採る前に断るので、選べると「発行しませんでした」が返るだけになる。
       */
      if (row.issueBlockedReason) return false
      return !row.invoiceNo && ISSUABLE.indexOf(row.billingStatus) >= 0
    },

    /**
     * その行に、意味のある金額があるか。
     *
     * **請求が立っていないことと、0円であることは別のもの。**
     * 確定できない行や、この月は請求しない行に「0人 ¥0」と
     * 並べると、0円で請求されると読める。
     */
    hasAmount (row) {
      return NO_AMOUNT.indexOf(row.billingStatus) < 0
    },

    /** 最低月額との差額（0107）が入っているか。無い・0円は出さない。 */
    hasMinimumAdjustment (row) {
      return Number(row.minimumAdjustmentAmount || 0) > 0
    },

    /** ボリュームディスカウント（0108）の割引額が入っているか。無い・0円は出さない。 */
    hasVolumeDiscount (row) {
      return Number(row.volumeDiscountAmount || 0) > 0
    },

    /**
     * 「プランの料金」の行に出す要約を、帯と「 / 」に分けたもの。
     * 帯（part）は途中で折り返さず、つなぎの所でだけ折り返す。帯が無い・率が全部0なら「割引なし」の1つ。
     * 今月効いている組（volumeDiscount）だけを見る（先の月の組は、その下に月ごとに並べる）。
     */
    volumeDiscountPieces (plan) {
      const tiers = plan && plan.volumeDiscount && Array.isArray(plan.volumeDiscount.tiers)
        ? plan.volumeDiscount.tiers : null
      const parts = summaryParts(tiers)
      if (!parts.length) return [{ text: summaryText(tiers), part: false }]
      return parts.reduce((pieces, part, index) => {
        if (index > 0) pieces.push({ text: SUMMARY_SEPARATOR, part: false })
        pieces.push({ text: part, part: true })
        return pieces
      }, [])
    },

    /** 要約の後ろに添える、今月の組を何年何月分から使っているか（M-C）。割引なしなら空文字。 */
    volumeDiscountSince (plan) {
      return appliedSinceText(plan && plan.volumeDiscount)
    },

    /** 先の月の組（AM-4「（{年}年{月}月分から：{人数割要約}）」）を月ごとに。無ければ空の並び。 */
    volumeDiscountNext (plan) {
      return nextTiersLines(plan && plan.volumeDiscountSchedule, this.thisMonthStart())
    },

    /**
     * ［人数割設定］。**料金の［保存］を送っているあいだは開かない**（ボタンも押せない）。
     * 結果の出し場所（表のすぐ下）を料金の保存と取り合わないように。
     * **帯の読み直しのあいだも開かない**（点検 9。読み直しの前の帯から始めない）。
     */
    openVolumeDiscount (plan) {
      if (!this.canEditPricing || this.savingPricing || this.volumeDiscountReloading
        || !plan || plan.freePlan === true) return
      this.volumeDiscountPlan = plan
    },

    /**
     * 小窓で保存できた。**成功のときだけ、守りなしで閉じる**（小窓の閉じる関数を通さない）。
     *
     * 結果は料金の表のすぐ下に、**開いたときのプランの名前で**出す（小窓が写して覚えた名前）。
     *
     * **一覧を丸ごと読み直さない**（点検 8）。丸ごと読み直すと行が作り直され、
     * 料金の欄に入れかけた値（単価・基本料・最低月額・いつから）が黙って消える。
     *   1. 送った帯と日付で、その行の帯をその場で書き換える（点検 9。読み直しが落ちても古い帯が残らない）
     *   2. 帯だけを読み直して、行ごとに差し込む（入れかけの欄には触らない）
     * **読み直しで落ちても「保存できませんでした」とは言わない**（保存は通っている。
     * submitPlan の saved と同じ考え）。読み込みの失敗として上端に出す。
     */
    volumeDiscountSaved (result) {
      this.volumeDiscountPlan = null
      /*
       * 料金と同じく、どの月の請求から効くか・後の月の組がもうあるか・確定済みの請求には
       * 効かないかを出し分ける（AM-6／AM-7、確定済みは AM-5。2026-09-30 の見直し。前は「{プラン名}の人数割を保存しました。」
       * だけで、過去の月に保存して後の月の組に隠れても、行が何も変わらない理由が出なかった）。
       */
      this.showPricingResult(result.planId, tiersSavedText(result.planName, result.appliedOn,
        result.laterAppliedOn, result.fixedBillingExists === true), 'success')
      this.applySavedVolumeDiscount(result)
      /* 人数割も見込みの額を変えるので、料金の保存と同じく上の一覧を読み直す（2026-10-10）。 */
      this.load()
      return this.reloadVolumeDiscounts()
    },

    /**
     * 保存できた帯を、その行にその場で入れる。
     *
     * 行が持っているのは「今月の組」（今月の1日時点で最新）と「月ごとの組」。
     *   ・月ごとの組は、保存した月の組を送った組にする（その月の組が無ければ足す）。
     *     ほかの月の組は変わらない（後の月の組はその月の日付の組を使うので）
     *   ・**先の月で保存したとき**は今月の組は変わらない
     *   ・**今月の組より前の月で保存したとき**も、今月の組は変わらない（書き換えない）
     *   ・今月・今月の組と同じか後の月（今月まで）なら、送った組が今月の組になる
     */
    applySavedVolumeDiscount (result) {
      const row = this.plans.find(plan => plan.id === result.planId)
      if (!row || !Array.isArray(result.tiers)) return
      const saved = {
        appliedOn: result.appliedOn,
        tiers: result.tiers.map(tier => ({ fromSeat: tier.fromSeat, discountRate: tier.discountRate }))
      }
      const schedule = (Array.isArray(row.volumeDiscountSchedule) ? row.volumeDiscountSchedule : [])
        .filter(entry => String(entry.appliedOn).slice(0, 10) !== String(result.appliedOn))
        .concat([saved])
        .sort((a, b) => (String(a.appliedOn) < String(b.appliedOn) ? -1 : 1))
      this.$set(row, 'volumeDiscountSchedule', schedule)
      if (String(result.appliedOn) > this.thisMonthStart()) return
      const current = row.volumeDiscount
      if (current && current.appliedOn && String(result.appliedOn) < String(current.appliedOn).slice(0, 10)) return
      // 帯の無いプランは volumeDiscount の鍵ごと返らないことがある。後から足す鍵も見張られるように $set で入れる
      // （今は結果の文などと同時に変わるので、代入でも描き直される。試験ではこの差を見分けられない）。
      this.$set(row, 'volumeDiscount', saved)
    },

    /**
     * 帯だけを読み直して、行ごとに差し込む。**料金の欄（入れかけの値）には触らない。**
     * 読み直しのあいだは［人数割設定］を押せない。
     */
    reloadVolumeDiscounts () {
      this.volumeDiscountReloading = true
      return providerBilling.plans()
        .then(rows => {
          const fresh = {}
          ;(Array.isArray(rows) ? rows : []).forEach(row => {
            fresh[row.id] = {
              current: row.volumeDiscount || null,
              schedule: Array.isArray(row.volumeDiscountSchedule) ? row.volumeDiscountSchedule : []
            }
          })
          this.plans.forEach(plan => {
            if (Object.prototype.hasOwnProperty.call(fresh, plan.id)) {
              this.$set(plan, 'volumeDiscount', fresh[plan.id].current)
              this.$set(plan, 'volumeDiscountSchedule', fresh[plan.id].schedule)
            }
          })
        })
        .catch(error => { this.errorMessage = this.messageOf(error) })
        .then(() => { this.volumeDiscountReloading = false })
    },

    toggleOne (row) {
      const at = this.selectedIds.indexOf(row.id)
      if (at >= 0) this.selectedIds.splice(at, 1)
      else this.selectedIds.push(row.id)
    },

    toggleAll () {
      this.selectedIds = this.allSelected
        ? []
        : this.issuableRows.map(row => row.id)
    },

    issueSelected () {
      this.runIssue(providerBilling.issueSelected(this.selectedIds.slice()))
    },

    issueAll () {
      this.runIssue(providerBilling.issueAll(this.compactMonth))
    },

    runIssue (promise) {
      this.issuing = true
      this.fixError = ''
      this.fixMessage = ''
      this.issueResults = []
      promise
        .then(results => {
          this.issueResults = Array.isArray(results) ? results : []
          if (!this.issueResults.length) {
            /*
             * **0件でも、押した結果を伝える。**
             * 何も出ないと、壊れているのか終わっているのかが
             * 分からない。
             */
            this.fixMessage =
              '発行する請求はありませんでした（すでに全部発行済みです）。'
          }
        })
        .catch(error => { this.fixError = this.messageOf(error) })
        .then(() => {
          this.issuing = false
          /*
           * 発行すると、請求書メールの送信予定が積まれる。画面下の「送信予定と、
           * 送った履歴」も読み直す（2026-10-10、実機で発見）。前は開き直すまで
           * 「まだ送信予定がありません。」のままだった。断られて1枚も出なかった
           * ときも読み直す（何も増えていないことが分かる）。
           */
          this.loadMailOutbox()
          this.load()
        })
    },

    nameOf (accountId) {
      const hit = this.rows.filter(row => row.accountId === accountId)[0]
      return hit ? hit.accountName : '会社ID ' + accountId
    },

    /**
     * 入金が足りているか。
     *
     * **請求が立っている行だけで見る。**見込みの行は、まだ
     * 請求していないのだから足りないのは当たり前で、
     * 「不足 ¥77,000」と赤字で出すと**払われていない請求が
     * あるように読める**（2026-09-18、実機で発生）。
     */
    isShort (row) {
      if (!row.id) return false
      return this.shortage(row) > 0
    },

    shortage (row) {
      return Number(row.totalAmount || 0) - Number(row.paidAmount || 0)
    },

    statusLabel (value) {
      return STATUS_LABEL[value] || value
    },

    statusColor (value) {
      return STATUS_COLOR[value] || 'secondary'
    },

    /** 発行の結果のうち、本文が札の字と同じことを言うもの（すでに発行済み）。本文の1行だけを出す。 */
    issueSaysItInOneLine (row) {
      return !!row && row.status === 'ALREADY_ISSUED' && !!row.message
    },

    issueStatusLabel (value) {
      return ISSUE_LABEL[value] || value
    },

    yen (value) {
      return '¥' + Number(value || 0).toLocaleString('ja-JP')
    },

    day (value) {
      return String(value || '').slice(0, 10)
    },

    /**
     * 日時（「送信予定と、送った履歴」の「いつ送る」）。
     *
     * back は協定世界時の機械向けの形（2026-10-01T07:42:59.578+0000）で返す。
     * そのまま出していた（2026-09-30）。**同じ画面の「契約の状態が変わった
     * 記録」の「いつ」（2026-09-30 16:42 の形）とそろえ、日本時間で出す。**
     * 日本に夏時間は無いので、+9時間で固定してよい。PC の時刻の設定には
     * よらない（送る時刻の設定も定期処理も日本時間で決めている）。
     * 読めない値はそのまま出す（空欄にすると、何かあったことが見えない）。
     */
    dateTime (value) {
      if (!value) return '—'
      const at = moment(value, moment.ISO_8601, true)
      return at.isValid()
        ? at.utcOffset(JAPAN_UTC_OFFSET_MINUTES).format('YYYY-MM-DD HH:mm')
        : String(value)
    },

    /**
     * 応答の本文から、人が読める理由を取り出す。
     *
     * **back は断った理由を必ず本文に入れている。**
     * 「エラーが発生しました」で終わらせない。
     *
     * fallback を渡さなければ、これまでどおり読み込み失敗の言い方。
     * **保存や送信で使うときは、その場面の言い方を渡す**
     * （読み込みの文言が保存のボタンの隣に出ると話が通じない）。
     */
    messageOf (error, fallback) {
      const data = error && error.response ? error.response.data : null
      if (data && data.message) return data.message
      if (typeof data === 'string' && data) return data
      return fallback
        || '請求の情報を取得できませんでした。時間をおいて画面を再読み込みしてください。'
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

/* ［再読込］の字を折らない。幅が足りないと「再／読／込」と縦に1字ずつ並んでいた。 */
.header-actions .btn {
  flex-shrink: 0;
  white-space: nowrap;
}

/*
  **スマホでは見出しの説明を全幅にする。**横並びのままだと、右の
  月の入力とボタンに押されて説明文が幅100pxほどに縮み、1行に数文字の
  縦長の文になっていた（2026-09-18、利用者の指摘）。
*/
@media (max-width: 767.98px) {
  .page-header {
    flex-direction: column;
  }

  .header-actions,
  .toolbar-actions {
    flex-wrap: wrap;
  }
}

.month-input {
  margin-bottom: 0;
  min-width: 11rem;
}

.eyebrow {
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  color: #768192;
  margin-bottom: 0.15rem;
}

.billing-alert {
  margin-bottom: 1rem;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.toolbar-actions {
  display: flex;
  gap: 0.5rem;
}

/*
  ボタンの下に、押すと何が起きるかを置く。
  **横並びのボタンを崩さない。**ボタンごとに説明を入れると幅が伸びて
  1行に収まらなくなるので、下へ回す。
*/
/* ボタンの並びと、その下の「押せない理由」の箱。縦に積む。 */
.toolbar-actions-block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/*
  ボタンが押せない理由。ボタンの並びのすぐ下で、ボタンと同じ寄せにする。
  広い幅では［確定分を一括発行］は並びの右端なので右寄せ。
*/
.toolbar-reason {
  margin: 0;
  text-align: right;
  font-size: 0.8rem;
  color: #768192;
}

/* 狭い幅ではボタンが左から折り返して並ぶので、理由も左に寄せる。 */
@media (max-width: 767.98px) {
  .toolbar-reason {
    text-align: left;
  }
}

.toolbar-what-happens {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.5rem;
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  color: #768192;
}

.toolbar-what-happens > div {
  display: flex;
  gap: 0.4rem;
  align-items: baseline;
}

.toolbar-what-happens dt {
  font-weight: 600;
  color: #4f5d6b;
  white-space: nowrap;
}

.toolbar-what-happens dd {
  margin: 0;
}

.select-all {
  margin-bottom: 0;
  font-size: 0.9rem;
}

.numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.account-name {
  font-weight: 600;
}

.account-id {
  font-size: 0.75rem;
  color: #768192;
}

/*
  運営の会社（請求書の発行元）の印。会社名と ID の下に、控えめな札で出す。
  **札は折らない**（2026-10-08）。前は札の中で折り返す形にしていて、幅 1375px ほどの
  画面で「運営会社（請求対」「象外）」と括弧の途中で2行に折れた。札は短い1語なので
  1行で出し、そのぶん「契約企業」の欄が広がるのは構わない。
*/
.issuer-account-mark {
  margin-top: 0.2rem;
}

.issuer-account-mark .badge {
  white-space: nowrap;
  font-weight: 500;
}

.short {
  font-size: 0.75rem;
  color: #e55353;
}

/*
  **自前の表は、枠に入れないと画面ごと横へ広がる。**
  CoreUI の一覧は table-responsive が付くので収まるが、
  ここで書いた表には枠が無く、386px の画面で 500px まで伸びていた。
  文章が左へ寄って見えるのはこれが原因（2026-09-18）。
*/
.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.muted {
  color: #768192;
}

/* 確定前の行に添える一言。金額と同じ大きさにすると数字と紛れる。 */
.planned-note {
  font-size: .75rem;
  color: #768192;
  font-weight: normal;
  /* 注は文節で折る（「…ありま／す。」と最後の字が落ちないように。lang は下の注の div に付けてある）。 */
  word-break: auto-phrase;
}

/*
  行のすぐ下に幅いっぱいで出す理由（解約済みの会社の行に［取り消す］が無い理由）。
  字の大きさと色は、行の中の理由（held-note）と同じ。
*/
.row-below-note {
  /* 表を横に動かしても、見えている左端に留める。幅の上限は画面が測って渡す。 */
  position: sticky;
  left: 0;
  box-sizing: border-box;
  /* 測れないとき（古いブラウザ）の上限。画面の幅から左右の余白ぶんを引く。 */
  max-width: calc(100vw - 2rem);
  padding: 0.15rem 0.75rem 0.6rem;
  font-size: .75rem;
  color: #a05a1e;
  white-space: normal;
  overflow-wrap: anywhere;
}

/*
  差し込みの行のセル。全体の決まり（表のセルは高さ 45px）が当たると、文の無い会社の
  行の下にも空の帯が見える。高さは中身どおりにする（中身が無ければ0）。
  余白と枠線は、部品が差し込みのセルに付けている指定で0。
*/
.billing-table::v-deep tbody > tr.p-0 > td.p-0 {
  height: auto;
}

/* 欄の中の注は文節で折る（字を 12px にしたら「…確定できま／せん。」と最後の字が落ちた）。Chrome・Edge で効く。 */
.billing-table::v-deep td {
  word-break: auto-phrase;
}

/*
  請求の一覧の縞を、差し込みの行が無いときと同じ並びにする。
  部品の縞は「奇数本目の行」に色を付ける。差し込みの行が1本ずつ入ると、会社の行は
  1・3・5・7…本目になり、全部の会社の行に色が付く。元の並び（1社おき）に戻すため、
    ・2社目・4社目…（3・7・11…本目）の色を外す（カーソルを載せたときの色は残す）
    ・色の付いた会社の行のすぐ下の差し込み（2・6・10…本目）に、同じ色を付ける
*/
.billing-table::v-deep .table-striped tbody tr:nth-of-type(4n + 3):not(:hover) {
  background: transparent;
}

.billing-table::v-deep .table-striped tbody tr:nth-of-type(4n + 2) {
  background: var(--preika-gray-050, rgba(0, 0, 21, 0.05));
}

/*
  帯の行のボタンの箱（料金の行へ移る・この会社だけ確定する）。文のすぐ後ろに続け、
  幅が足りなければボタン1つずつ折り返す（狭い幅で2つが重なったり、はみ出したりしない）。
*/
.held-actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-left: 0.5rem;
  vertical-align: middle;
}

/* 全体の決まりがボタンの左に余白を付けるので、箱の中では間隔（gap）だけにする。 */
.held-actions .btn,
.zero-price-jump .btn,
.back-to-held .btn {
  margin-left: 0;
}

.zero-price-jump,
.back-to-held {
  margin-top: 0.3rem;
}

.held-note {
  font-size: .75rem;
  color: #a05a1e;
  margin-top: 0.15rem;
}

/*
  **「この月は請求しない」は困りごとではない。**
  確定できない行と同じ色にすると、全部が手当ての要る行に見える。
*/
.held-note.calm {
  color: #768192;
}

.invoice-no {
  font-variant-numeric: tabular-nums;
  margin-left: 0.4rem;
}

.issue-result {
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.issue-result h2 {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.issue-result ul {
  margin-bottom: 0;
  padding-left: 1.1rem;
}

/*
  **会社ごとの並びは、1件ずつ行を分けて縦に並べる**（2026-10-08、実機で発見）。

  全体の様式（assets/scss/style.scss）が li を横並び（inline-block）にしている。
  「発行の結果」では、説明つきの結果のすぐ右に次の会社の結果が流れ込み、
  どの説明がどの会社のものか読めなかった。

  この画面の li は6か所（確定を見送った会社・制限を見直せなかった会社・請求を
  作れなかった会社・発行の結果・確認が要る請求・送信予約の無い請求書）で、
  どれも「1件が1社（1通）」の並び。同じ指定の下にあるので、まとめて縦に戻す。
  **全体の様式は触らない**（ほかの画面の並びまで動く）。
*/
.platform-billing-page li {
  display: block;
}

/* 説明やボタンの付く行が続いても、どこまでが1件かが分かるだけの間を空ける。 */
.platform-billing-page li + li {
  margin-top: 0.3rem;
}

.row-actions {
  display: flex;
  gap: 0.35rem;
  align-items: center;
}

.payments {
  margin-top: 1.5rem;
  border: 1px solid #321fdb;
  border-radius: 0.25rem;
  padding: 1rem 1.25rem;
}

.payments h2 {
  font-size: 1.05rem;
  margin-bottom: 0.5rem;
}

.payment-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0 1rem;
  align-items: end;
  margin-top: 1rem;
}

/*
 * **案内は列に押し込めない。**入力欄は横に並ぶので、
 * そのまま置くと幅 11rem の細長い箱になって読めない
 * （2026-09-23、実機で確認）。
 */
.payment-form__notice {
  grid-column: 1 / -1;
  margin-bottom: 0.75rem;
}

/*
  入金の「方法」が1つだけのときの見せ方。**入力欄と同じ高さに置く**（並びの中で
  値だけが上へずれないように、入力欄の内側の余白と同じ分を取る）。枠は付けない
  （入力できる欄に見せない）。
*/
.payment-method-fixed__value {
  padding: 0.375rem 0;
  line-height: 1.5;
  font-weight: 600;
  color: #3c4b64;
}

.settled {
  color: #2eb85c;
}

.pricing {
  margin-top: 2rem;
  border-top: 1px solid #d8dbe0;
  padding-top: 1.25rem;
}

/*
  ［保存］の字と「契約中」の列を途中で折り返さない（2026-10-08）。列が狭く、
  ［保存］が「保／存」、「10 人分」が「10 人／分」と割れていた（本番の利用者の画面写真でも同じ）。
*/
.plan-pricing-table td:last-child .btn,
.plan-pricing-table th:nth-child(6),
.plan-pricing-table td:nth-child(6) {
  white-space: nowrap;
}

/* 見出しの右に［プランを追加］。狭い画面では見出しの下へ回る。 */
.pricing-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
}

/* 結果の行は、押した行とひと続きに見せる（上の線を消す）。 */
/*
  **［保存］の列は、表を横に動かしても右端に留める**（2026-10-10、実機で発見）。
  幅 1280（左のメニューを開いた状態）で表が横にはみ出し、右端の［保存］が隠れた。帯の行から
  移ってきた人は、金額を入れたあと押す物が見えなかった。列の並びは変えず、［保存］の列だけを
  見えている枠の右端に留める（狭い幅でも同じ。表のほかの列は今までどおり横に動く）。
  留めたセルの下を、横に動いたほかの列が通るので、下地の色を付ける（画面の地の色）。
*/
.plan-pricing-table thead th:last-child,
.plan-pricing-table tr[data-testid^="pricing-row-"] > td:last-child {
  position: sticky;
  right: 0;
  z-index: 1;
  background: var(--preika-bg, #f3f6f9);
  box-shadow: -6px 0 6px -6px rgba(0, 0, 21, 0.2);
}

/*
  帯の行・0円の注意から移ってきた行の目印。色は、この画面の「気づいてほしいもの」の色
  （.mail-warning と同じ地の色と、左の線の色）。右端に留めた［保存］のセルにも同じ地の色。
*/
.plan-pricing-table tr.pricing-row--jumped > td,
.plan-pricing-table tr.pricing-row--jumped > td:last-child {
  background: #fff9e6;
}

.plan-pricing-table tr.pricing-row--jumped > td:first-child {
  box-shadow: inset 4px 0 0 #f9b115;
}

/*
  料金の行へ移るとき、表の見出しを画面の上端に合わせる。上の固定の帯の下に隠れないように、
  帯の高さぶん下で止める。
*/
.plan-pricing-table thead {
  scroll-margin-top: calc(var(--preika-header-height, 56px) + 0.75rem);
}

/*
  保存の結果の箱。表を横に動かしても、見えている左端に留める。幅の上限は画面が測って渡す
  （測れないときは、画面の幅から左右の余白ぶんを引いた幅）。中の文は折り返す。
*/
.pricing-result-box {
  position: sticky;
  left: 0;
  box-sizing: border-box;
  max-width: calc(100vw - 2rem);
  white-space: normal;
}

.pricing-result-row > td {
  border-top: 0;
  padding-top: 0;
}

.pricing h2 {
  font-size: 1.1rem;
  margin-bottom: 0.35rem;
}

.pricing-note {
  font-size: 0.85rem;
  color: #768192;
  margin-bottom: 1rem;
}

.pricing-table {
  width: 100%;
  border-collapse: collapse;
}

.pricing-table th,
.pricing-table td {
  border-bottom: 1px solid #d8dbe0;
  padding: 0.5rem 0.6rem;
  vertical-align: middle;
}

.pricing-table th {
  font-size: 0.8rem;
  color: #768192;
  font-weight: 500;
}

/*
  **金額の列と、日付の列を離す。**「1人あたり」が右端、「いつから」が
  左端に付くので、見出しどうしが隣り合って1語に見えた（テスターの
  「ヘッダーの位置が異なる」）。揃え方は列の中身に合わせたまま、
  境目に余白を置いて切れ目を作る。
*/
.pricing-table th:nth-child(2),
.pricing-table td:nth-child(2),
.pricing-table th:nth-child(3),
.pricing-table td:nth-child(3) {
  padding-right: 1.6rem;
}

/*
  プランの料金の表だけ、最低月額（4列目）と「いつから」の境目にも余白を置く。
  **.pricing-table はこの画面の他の表も使っているので、4列目は絞って当てる。**
*/
.plan-pricing-table th:nth-child(4),
.plan-pricing-table td:nth-child(4) {
  padding-right: 1.6rem;
}

/* 最低月額の列の見出しの下の説明。見出しより弱く、1行に収まらなければ折り返す。 */
.pricing-column-note {
  font-size: 0.75rem;
  font-weight: 400;
  color: #768192;
  white-space: normal;
  /* 狭い欄の見出しの注は、文節で折る（「最低／月額を適用しませ／ん。」と割れたので）。Chrome・Edge で効く。 */
  word-break: auto-phrase;
}

/*
  ボリュームディスカウントの列（0108）。要約は長くなる（帯5本で約80字）ので、
  幅を決めて帯の切れ目で折り返す。帯の中（「11〜30名：10%OFF」）では切らない。
*/
.volume-discount-cell {
  min-width: 12rem;
  max-width: 18rem;
}

.volume-discount-summary {
  font-size: 0.75rem;
  color: #768192;
  line-height: 1.5;
  margin-bottom: 0.35rem;
}

.volume-discount-part {
  white-space: nowrap;
}

/* 適用開始日（W02）。「（2026-09-01〜適用）」の途中では切らない。要約の最後の帯のすぐ後ろに続ける。 */
.volume-discount-since {
  white-space: nowrap;
}

/*
  先の月の組の行（2026-09-30 の見直し）。すぐ上の要約と同じ大きさと色にする（前は指定が無く、
  要約より大きい濃い字で出ていた）。
*/
.volume-discount-next {
  font-size: 0.75rem;
  color: #768192;
  line-height: 1.5;
  margin-bottom: 0.35rem;
}

/*
  先の月の組の行と、プラン名の下の料金の行は、**空白の所でだけ折り返す**（2026-09-30 の見直し）。
  そのままだと「3,200」と「円）」、「1〜10名：」と「0%OFF」の間で折れていた。
  列より長い塊が来たときだけは、はみ出さないように折る（break-word。anywhere は表の列の最小幅まで
  縮めてしまう）。
*/
.volume-discount-next,
.price-line {
  word-break: keep-all;
  overflow-wrap: break-word;
}

/*
  金額の欄。**6桁（300000）まで、全部の桁が見える幅にする**（2026-10-10、実機で発見）。
  前は幅の指定が上限（9rem）だけで、列に押されて 82px まで縮み、左右の余白と増減の
  つまみを引くと字の入る幅が 32px しか無く、10000 が「100」に見えた。
  下限の幅を決め、左右の余白を詰める（字 12px・等幅の数字6桁で約 45px ＋ つまみ）。
  表は横に動く枠の中にあるので、狭い幅でも画面の外へはみ出さない。
  2026-10-10：下限を 7rem から 6rem へ詰めた（最低月額の8桁＝約 60px ＋ つまみも入る）。
  7rem にしたら、幅 1280 で表が横にはみ出す分が増え、右端の［保存］が隠れた。
*/
.price-input {
  width: 6rem;
  min-width: 6rem;
  max-width: 9rem;
  margin-left: auto;
  padding-left: 0.5rem;
  padding-right: 0.35rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/*
  角印を消すチェック。ファイル選択のすぐ隣に置く。
  **離すと、上書きと消すのが別の話に見える。**
*/
.seal-remove {
  margin-left: 0.75rem;
  margin-bottom: 0;
  font-size: 0.85rem;
  color: #768192;
  white-space: nowrap;
}

.seal-remove input {
  margin-right: 0.25rem;
  vertical-align: middle;
}

.empty {
  color: #768192;
  margin-top: 1rem;
}

/* ------------------------------------------------------------------
   請求書メールの送付
   ------------------------------------------------------------------ */

.mail {
  margin-top: 2.5rem;
}

.mail-setting {
  margin-bottom: 1.5rem;
}

.mail-switch {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.mail-switch input {
  margin-right: 0.4rem;
  vertical-align: middle;
}

.mail-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
  flex-wrap: wrap;
}

.mail-label {
  min-width: 9rem;
}

.mail-number {
  width: 4.5rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.mail-text {
  min-width: 18rem;
  max-width: 26rem;
}

.mail-hint {
  margin: 0 0 0.9rem 0;
  font-size: 0.85rem;
  color: #768192;
}

.mail-message {
  margin-left: 0.6rem;
  font-size: 0.9rem;
}

.mail-subtitle {
  margin-top: 1.75rem;
  font-size: 1.05rem;
}

/*
  **気づいてほしいものは、色で出す。**
  予約の無い発行済み請求書は、放っておくと永久に送られない。
*/
.mail-warning {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  border-left: 4px solid #f9b115;
  background: #fff9e6;
  font-size: 0.9rem;
}

.mail-missing {
  margin: 0.5rem 0 0 1.2rem;
}

.mail-danger {
  color: #e55353;
}
</style>
